// src/WorkshopDetails.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import QRCode from "qrcode";
import Cookies from "js-cookie";
import { jobberI } from "./employeeAccess";
import { onsketFerdigTekst, erForTidligOnsket, IKKE_SATT } from "./utils/ferdigDatoVisning";
import { OdoFelt, OdoAdvarsel, erElsykkelService, harOdo } from "./components/workshop/Odometer";
import ServiceInvoiceModal from "./features/invoice/ServiceInvoiceModal";
import ServiceButikkvarer from "./tilbud/ServiceButikkvarer";
import { tilDelelinjer, beregnTotaler, delelinjeBidrag, delerBidrag } from "./features/invoice/fakturaGrunnlag";
import ImportorSelect from "./components/ImportorSelect";
import { getTjenester, getChecklistsForServiceSlugs } from "./lib/tjenestekatalogApi";
import {
  fmtNOK,
  fmtDT,
  fmtDate,
  fmtMin,
  toShortRefFront,
  normalizePhone,
} from "./utils/format";

import {
  getServiceCategory,
  fillWorkshopSmsTemplate,
  inferWorkshopSmsTemplate,
} from "./utils/smsUtils";

import {
  sendSms,
  notifyCustomerNewDialogMessage,
} from "./utils/smsGateway";

import {
  sendWorkshopStatusEmail,
  sendWorkshopQuoteEmail,
} from "./utils/emailGateway";



import useChecklist from "./features/checklist/useChecklist";
import ChecklistModal from "./features/checklist/ChecklistModal";
import BarcodeScanner from "./components/workshop/BarcodeScanner";
import { isLineChecklistEligible } from "./features/checklist/utils";

import useQuote from "./features/quote/useQuote";
import QuoteModal from "./features/quote/QuoteModal";
import StatusModal from "./features/status/StatusModal";
import { getDisplayName } from "./utils/displayName";
import SmsTilKunden from "./components/SmsTilKunden";
import KundeBanner from "./components/KundeBanner";
import SykkelkortForslag from "./components/workshop/SykkelkortForslag";
import Ikon from "./ny-kundesamhandling/Ikon";
import { Sidetopp } from "./ny-kundesamhandling/Skjema";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function renameTjeneste(navn, slug = "") {
  if (
    String(slug || "").toLowerCase() === "garantihenvendelse" ||
    String(navn || "").toLowerCase() === "garantihenvendelse"
  ) {
    return "Reklamasjonsvurdering";
  }
  return navn;
}

// Dekningsbidrag på deler (kr og %), regnet som i nettbutikkens produktredigering.
const fmtProsent = (tall) =>
  `${tall.toLocaleString("no-NO", { maximumFractionDigits: 1 })} %`;

function DelBidrag({ linje }) {
  if (linje.isWarranty) return null;
  const b = delelinjeBidrag(linje);
  if (!b) {
    return (
      <div className="text-xs text-slate-600 mt-1">
        Dekningsbidrag: kostpris mangler (legg den inn under «Endre»)
      </div>
    );
  }
  return (
    <div className={`text-xs mt-1 ${b.bidrag < 0 ? "text-red-700" : "text-slate-600"}`}>
      Kostpris {fmtNOK(linje.kostpris)} eks. mva · Dekningsbidrag {fmtNOK(b.bidrag)}
      {b.dg != null ? ` · ${fmtProsent(b.dg)}` : ""}
    </div>
  );
}

function DelerBidragSum({ partLines }) {
  const sum = delerBidrag(partLines);
  if (!sum.medKostpris) return null;
  return (
    <div className="mt-1 flex items-center justify-between flex-wrap gap-x-4 text-sm text-slate-700">
      <span>
        Dekningsbidrag deler
        {sum.manglerKostpris
          ? ` (${sum.manglerKostpris} ${sum.manglerKostpris === 1 ? "del" : "deler"} uten kostpris er ikke med)`
          : ""}
      </span>
      <span className={sum.bidrag < 0 ? "text-red-700" : ""}>
        {fmtNOK(sum.bidrag)}
        {sum.dg != null ? ` · ${fmtProsent(sum.dg)}` : ""}
      </span>
    </div>
  );
}

export default function WorkshopDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [svc, setSvc] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [msg, setMsg] = useState("");

  // Hvor meldinger til kunden går, fra serveren (kontaktFor i src/lib/kunde.js):
  // kundekortet når telefonen stemmer, ellers det som ble registrert på servicen.
  // Egen state, fordi lagringer bytter ut svc med et svar uten kontakt.
  const [kontakt, setKontakt] = useState(null);
  // Rette telefonnummeret på servicen.
  const [rettTlfApen, setRettTlfApen] = useState(false);
  const [rettTlf, setRettTlf] = useState("");
  const [rettTlfBusy, setRettTlfBusy] = useState(false);
  const [rettTlfMsg, setRettTlfMsg] = useState("");
  // Andre kundekort med samme e-post som servicen/kundekortet (mulig duplikat).
  const [epostTreff, setEpostTreff] = useState([]);

  // SMS
  const [smsText, setSmsText] = useState("");
  const [sendOnStatusChange, setSendOnStatusChange] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);
  const [includePortalLink, setIncludePortalLink] = useState(false);
  const [vippsRequested, setVippsRequested] = useState(false);
  const [vippsBusy, setVippsBusy] = useState(false);

  // SMS-maler
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsTemplateLoading, setSmsTemplateLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [hasAutoSelectedTemplate, setHasAutoSelectedTemplate] = useState(false);

  // 👇 nytt flagg: blokker auto-mal for denne servicen
  const [autoTemplateBlocked, setAutoTemplateBlocked] = useState(false);

  // Modal for status/SMS
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [pendingAvsluttFromModal, setPendingAvsluttFromModal] = useState(false);
  // 👇 Ny state for forventet ferdig-dato i modal
  const [expectedReadyDate, setExpectedReadyDate] = useState("");
  // Ønsket ferdigdato (ski) — overstyres her etter mottak
  const [onsketFerdigDatoInput, setOnsketFerdigDatoInput] = useState("");

  // Produktkommentar
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [productBrandDraft, setProductBrandDraft] = useState("");
  const [productColorDraft, setProductColorDraft] = useState("");
  const [editPriorGrunn, setEditPriorGrunn] = useState("");
  const [editPriorForklaring, setEditPriorForklaring] = useState("");
  const [editPriorHaster, setEditPriorHaster] = useState(false);

  // Meldinger
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendMsgError, setSendMsgError] = useState("");

  // Ansatt som står som avsender i dialogen. Kan byttes rett ved send-knappen, så man slipper
  // å sende som feil ansatt når man har glemt å bytte i menylinjen. Bytte her bytter også
  // ansatt i resten av appen (samme cookie som NavBar, synket med "employeeUpdated").
  const [chatEmployee, setChatEmployee] = useState(Cookies.get("selectedEmployee") || "");
  const [chatEmployees, setChatEmployees] = useState([]);
  const employeeName = chatEmployee.split(" ")[0] || "Mekaniker";

  const [statusModalMode, setStatusModalMode] = useState("default"); // "default" | "ready"
  const [showFerdigDatoWarning, setShowFerdigDatoWarning] = useState(false);
  const [showOdoAdvarsel, setShowOdoAdvarsel] = useState(false);
  const [showPristilbudWarning, setShowPristilbudWarning] = useState(false);
  const [showPristilbudReminderModal, setShowPristilbudReminderModal] = useState(false);
  const pristilbudReminderShownRef = useRef(null);

  const [siblingServices, setSiblingServices] = useState([]);
  const [siblingsLoading, setSiblingsLoading] = useState(false);

  const [partDiscountDraft, setPartDiscountDraft] = useState({});

// Bilder
const [imageUploading, setImageUploading] = useState(false);
const [imageBusyId, setImageBusyId] = useState("");
const [imagesOpen, setImagesOpen] = useState(false);
const [pendingImageFiles, setPendingImageFiles] = useState(null);
const [showImageVisibilityModal, setShowImageVisibilityModal] = useState(false);

// Dokumenter
const [documentUploading, setDocumentUploading] = useState(false);
const [documentBusyId, setDocumentBusyId] = useState("");
const [documentsOpen, setDocumentsOpen] = useState(false);
const [pendingDocumentFiles, setPendingDocumentFiles] = useState(null);
const [showDocumentVisibilityModal, setShowDocumentVisibilityModal] = useState(false);

// Sykkelkort
const [bikeBoxOpen, setBikeBoxOpen] = useState(false);
const [bikeModalOpen, setBikeModalOpen] = useState(false);
const [bikeScannerOpen, setBikeScannerOpen] = useState(false);
const [bikeModalTab, setBikeModalTab] = useState("list"); // "list" | "new"
const [bikes, setBikes] = useState([]);
const [bikesLoading, setBikesLoading] = useState(false);
const [linkedBike, setLinkedBike] = useState(null);
const [bikeBusy, setBikeBusy] = useState(false);
const [bikeForm, setBikeForm] = useState({ merke: "", modell: "", årsmodell: "", type: "", elsykkel: null, rammenummer: "" });
const [editingBikeId, setEditingBikeId] = useState(null);
const [bikeSpecImageUploading, setBikeSpecImageUploading] = useState(false);
const [bikeSpecImageOpen, setBikeSpecImageOpen] = useState(false);
const [qrModalOpen, setQrModalOpen] = useState(false);
const bikeSpecImageRef = useRef(null);
const [bikeSaveError, setBikeSaveError] = useState("");
const [modalBikeImageData, setModalBikeImageData] = useState({ specImageUrl: "", mainImageUrl: "", receiptUrl: "", extraImages: [], diagnoseRapporter: [], softwareRapporter: [] });
const [modalBikeMainUploading, setModalBikeMainUploading] = useState(false);
const [modalBikeSpecUploading, setModalBikeSpecUploading] = useState(false);
const [modalBikeReceiptUploading, setModalBikeReceiptUploading] = useState(false);
const [modalBikeExtraUploading, setModalBikeExtraUploading] = useState(false);
const [modalBikeDiagnoseUploading, setModalBikeDiagnoseUploading] = useState(false);
const [modalBikeSoftwareUploading, setModalBikeSoftwareUploading] = useState(false);
const [modalBikeImgMsg, setModalBikeImgMsg] = useState(null);
const modalBikeMainRef = useRef(null);
const modalBikeSpecRef = useRef(null);
const modalBikeReceiptRef = useRef(null);
const modalBikeExtraRef = useRef(null);
const modalBikeDiagnoseRef = useRef(null);
const modalBikeSoftwareRef = useRef(null);


  // Endre tjenestetype (slug) – integrert i comment-modal
  const [slugDraft, setSlugDraft] = useState("");
  const [slugRawGroups, setSlugRawGroups] = useState([]); // rå Hygraph-grupper
  const [slugOptionsLoading, setSlugOptionsLoading] = useState(false);

  // Tjenester (Hygraph)
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceTab, setServiceTab] = useState("list"); // 'list' | 'manual'
  const [hygraphLoading, setHygraphLoading] = useState(false);
  const [flatServices, setFlatServices] = useState([]);
  const [serviceQuery, setServiceQuery] = useState("");
  const [hyAddBusy, setHyAddBusy] = useState(false);

  // Manuell tjeneste (i modal)
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newMinutes, setNewMinutes] = useState("");
  const [newComment, setNewComment] = useState("");
  const [newReklamasjon, setNewReklamasjon] = useState(false);
  const [newGratisarbeid, setNewGratisarbeid] = useState(false);
  const [newUtfortAvRS, setNewUtfortAvRS] = useState(true);
  const [serviceModalError, setServiceModalError] = useState("");

  // Endre tjenestelinje (Navn, kommentar, tidsbruk, pris, reklamasjon, gratisarbeid)
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [lineEditNavn, setLineEditNavn] = useState("");
  const [lineEditKommentar, setLineEditKommentar] = useState("");
  const [lineEditTidsbruk, setLineEditTidsbruk] = useState("");
  const [lineEditPris, setLineEditPris] = useState("");
  const [lineEditReklamasjon, setLineEditReklamasjon] = useState(false);
  const [lineEditGratisarbeid, setLineEditGratisarbeid] = useState(false);
  const [lineEditBusy, setLineEditBusy] = useState(false);
  const [lineEditError, setLineEditError] = useState("");

  // Deler (katalog)
  const [partsModalOpen, setPartsModalOpen] = useState(false);
  const [partsTab, setPartsTab] = useState("list"); // 'list' | 'manual'
  const [partsLoading, setPartsLoading] = useState(false);
  const [parts, setParts] = useState([]);
  const [partsQuery, setPartsQuery] = useState("");
  const [partsAddBusy, setPartsAddBusy] = useState(false);
  const [editingPartIndex, setEditingPartIndex] = useState(null);

  // Manuell del (i modal)
  const [pName, setPName] = useState("");
  const [pBrand, setPBrand] = useState("");
  const [pEan, setPEan] = useState("");
  const [pPrice, setPPrice] = useState("");
  const [pKostpris, setPKostpris] = useState(""); // eks. mva, tom = ukjent
  const [pQty, setPQty] = useState("1");
  const [pDiscount, setPDiscount] = useState("0");
  const [pDiscountPercent, setPDiscountPercent] = useState("0");
  const [eanScannerOpen, setEanScannerOpen] = useState(false);

  // Frakt (reell fraktkostnad, f.eks. retur av motor til kunde)
  const [fraktFormOpen, setFraktFormOpen] = useState(false);
  const [fraktBeskrivelse, setFraktBeskrivelse] = useState("Frakt");
  const [fraktPris, setFraktPris] = useState("");
  const [fraktKommentar, setFraktKommentar] = useState("");
  const [fraktBusy, setFraktBusy] = useState(false);
  const [fraktError, setFraktError] = useState("");
  const [editingFraktIndex, setEditingFraktIndex] = useState(null);

  // Reklamasjon (del)
  const [pReklamasjonExpanded, setPReklamasjonExpanded] = useState(false);
  const [pReklamasjonKjopsdato, setPReklamasjonKjopsdato] = useState("");
  const [pReklamasjonRammenr, setPReklamasjonRammenr] = useState("");
  const [pReklamasjonTidsforbruk, setPReklamasjonTidsforbruk] = useState("");
  const [pReklamasjonArsak, setPReklamasjonArsak] = useState("");
  const [pReklamasjonAnsvar, setPReklamasjonAnsvar] = useState("");
  const [pReklamasjonButikk, setPReklamasjonButikk] = useState("");
  const [pReklamasjonImportor, setPReklamasjonImportor] = useState("");
  const [pReklamasjonMerke, setPReklamasjonMerke] = useState("");
  const [pReklamasjonModell, setPReklamasjonModell] = useState("");
  const [pReklamasjonSerienr, setPReklamasjonSerienr] = useState("");

  // Servicefaktura-modal
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  // Varer fra butikken koblet til servicen (ordre/skiplukk/pristilbud), til servicefakturaen.
  const [butikkvarer, setButikkvarer] = useState(null);

  // Arbeidsbeskrivelse
  const [workDesc, setWorkDesc] = useState("");
  const [descBusy, setDescBusy] = useState(false);

  // Tekst til pristilbud (kundevisning)
  const [quoteDesc, setQuoteDesc] = useState("");
  const [quoteDescBusy, setQuoteDescBusy] = useState(false);



  const lastActor = svc?.produkt?.lastStatusBy || null;
  const ansvarligNavn = lastActor?.name || null;

  const serviceCustomer = svc?.customerSnapshot || {};
  const kanRetteTelefon =
    !!svc?._id &&
    !["avsluttet", "kansellert"].includes(String(svc?.status || "").toLowerCase()) &&
    !svc?.salgsbilag?.nummer;

  // Etter at servicen er flyttet til et annet kundekort (telefon eller valgt kort).
  async function etterFlytt(data, melding) {
    setSvc(data);
    setKontakt(data.kontakt || null);
    if (data.customerId) {
      const cr = await fetch(`${API_BASE_URL}/customers/${data.customerId}`);
      if (cr.ok) setCustomer(await cr.json());
    }
    if (!data.bikeId) setLinkedBike(null);
    setRettTlfMsg(melding);
  }

  // Flytt bare denne servicen til et kundekort som finnes (PATCH /:id/kunde).
  const [flytter, setFlytter] = useState("");
  async function flyttTilKunde(k) {
    if (!svc?._id) return;
    const navn = [k.firstName, k.lastName].filter(Boolean).join(" ") || "kunden";
    const ok = window.confirm(
      `Flytte service #${svc.serviceRef || ""} til ${navn} · ${k.phoneNumber || "uten nummer"}?\n\n` +
      `Bare denne servicen flyttes. Meldinger om den går til ${k.phoneNumber || "det kortet"}. Ingen kundekort endres eller slettes.`
    );
    if (!ok) return;
    setFlytter(String(k._id));
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}/kunde`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: k._id }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "Kunne ikke flytte servicen");
      await etterFlytt(data, `Servicen er flyttet til ${navn} · ${k.phoneNumber || ""}.`);
    } catch (err) {
      setRettTlfMsg(err.message);
    } finally {
      setFlytter("");
    }
  }

  async function lagreRettTelefon(e) {
    e.preventDefault();
    if (!svc?._id) return;
    setRettTlfBusy(true);
    setRettTlfMsg("");
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}/telefon`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telefon: rettTlf }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || "Kunne ikke rette nummeret");
      await etterFlytt(data, "Nummeret er rettet, og servicen er koblet til kundekortet for det.");
      setRettTlfApen(false);
    } catch (err) {
      setRettTlfMsg(err.message);
    } finally {
      setRettTlfBusy(false);
    }
  }
  // Mulig duplikat: samme e-post på et annet kundekort med et annet nummer.
  const epostNokkel = [serviceCustomer.email, customer?.email, kontakt?.epost]
    .map((e) => String(e || "").trim().toLowerCase())
    .filter(Boolean)
    .filter((e, i, a) => a.indexOf(e) === i)
    .join("|");
  useEffect(() => {
    if (!epostNokkel) { setEpostTreff([]); return undefined; }
    let avbrutt = false;
    const egetNr = normalizePhone(customer?.phoneNumber || serviceCustomer.phoneNumber || "");
    Promise.all(
      epostNokkel.split("|").map((epost) =>
        fetch(`${API_BASE_URL}/customers/epost-treff?epost=${encodeURIComponent(epost)}&utenId=${encodeURIComponent(svc?.customerId || "")}`)
          .then((r) => (r.ok ? r.json() : []))
          .then((liste) => liste.map((k) => ({ ...k, epost })))
          .catch(() => [])
      )
    ).then((lister) => {
      if (avbrutt) return;
      const sett = new Map();
      for (const k of lister.flat()) {
        if (normalizePhone(k.phoneNumber || "") === egetNr) continue;
        sett.set(String(k._id), k);
      }
      setEpostTreff([...sett.values()]);
    });
    return () => { avbrutt = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epostNokkel, svc?.customerId, customer?.phoneNumber]);

  const customerPhone = normalizePhone(kontakt ? kontakt.telefon : serviceCustomer.phoneNumber || "");
  const customerEmail = kontakt ? kontakt.epost : serviceCustomer.email || "";
  const customerName =
    (kontakt && kontakt.navn) ||
    `${serviceCustomer.firstName || ""} ${serviceCustomer.lastName || ""}`.trim() ||
    "Kunde";


  const [customerBoxExpanded, setCustomerBoxExpanded] = useState(false);


  // --- PRISTILBUD ---
  const [quoteValidDays] = useState(14);
  const quote = useQuote({ svc, defaultValidDays: quoteValidDays });

  // Kundelenke (portal)
  const [customerLink, setCustomerLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [resetQuoteBusy, setResetQuoteBusy] = useState(false);
  const [customerViewOpen, setCustomerViewOpen] = useState(false);
  const [openHistoryVisible, setOpenHistoryVisible] = useState(false);

  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);

  // Meldings-tellere
  const [customerMessageCount, setCustomerMessageCount] = useState(0);
  const [unreadCustomerCount, setUnreadCustomerCount] = useState(0);


  // --- Quote-status for kundevisning ---
  // Skal være AV som default, og kun slås på eksplisitt (notifyQuoteLink / enableQuoteAfterSms)
  const quoteEnabled = svc?.quote?.enabled === true;
  const approval = svc?.quote?.approved || null;
  const hasQuoteDecision = typeof approval?.ok === "boolean";
  const isQuoteApproved = approval?.ok === true;

  // e-post ved status
  const [sendEmailOnStatusChange, setSendEmailOnStatusChange] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  // e-post for pristilbud (fra Quote-modal)
  const [sendingQuoteEmail, setSendingQuoteEmail] = useState(false);


  const [notifyBusy, setNotifyBusy] = useState(false);

  const mottakKommentarFraLogg = useMemo(() => {
    const logs = Array.isArray(svc?.produkt?.statusLogs) ? svc.produkt.statusLogs : [];
    // finn siste "mottatt" (hvis flere)
    const last = [...logs].reverse().find((l) => String(l.status || "").toLowerCase() === "mottatt");
    return last?.comment || "";
  }, [svc?.produkt?.statusLogs]);

  // Produktinfo
  const produkt = useMemo(
    () => ({
      merke: svc?.produkt?.produktmerke || "Produkt",
      type: svc?.produkt?.produkttype || "",
      farge: svc?.produkt?.farge || "",
      butikk: svc?.produkt?.butikk || "",
      leveringsDato: svc?.produkt?.leveringsDato || null,
      forventetFerdigDato: svc?.produkt?.forventetFerdigDato || null,
      ferdigDatoValgfri: !!svc?.produkt?.ferdigDatoValgfri,
      onsketFerdigDato: svc?.produkt?.onsketFerdigDato || null,
      kommentar: svc?.produkt?.kommentar || "",
      mottakKommentar:
        svc?.produkt?.mottakKommentar ||
        svc?.produkt?.receivedComment ||
        mottakKommentarFraLogg ||
        svc?.produkt?.lastStatusComment || // optional fallback for gamle data
        "",
      prisoverslag: !!svc?.produkt?.prisoverslag,
    }),
    [svc, mottakKommentarFraLogg]
  );

  const productServiceSlug = useMemo(() => {
    const raw =
      svc?.produkt?.slug ||
      svc?.produkt?.serviceSlug ||
      svc?.produkt?.produkttype ||
      "";

    const normalized = String(raw).trim().toLowerCase();

    if (normalized === "elsykkel") return "el-sykkel";
    if (normalized === "el sykkel") return "el-sykkel";
    if (normalized === "el-sykkel") return "el-sykkel";

    if (normalized === "sykkel uten motor") return "uten-motor";
    if (normalized === "uten motor") return "uten-motor";
    if (normalized === "uten-motor") return "uten-motor";

    return normalized;
  }, [svc?.produkt]);

  // Elsykkelservice: ODO registreres, og «Ferdig» advarer hvis den mangler (Odometer.jsx).
  const erElService = useMemo(() => erElsykkelService(svc, linkedBike), [svc, linkedBike]);

  const serviceCategory = useMemo(
    () => getServiceCategory(svc, produkt),
    [svc, produkt]
  );

  const filteredSlugOptions = useMemo(() => {
    if (!slugRawGroups.length) return [];
    const currentGroup = slugRawGroups.find((g) => g.slug === productServiceSlug);
    const currentKategori = currentGroup?.service?.[0]?.kategori || "";
    const toShow = currentKategori
      ? slugRawGroups.filter((g) => g.service?.[0]?.kategori === currentKategori)
      : slugRawGroups;
    return toShow.map((g) => ({ slug: g.slug || "", navn: renameTjeneste(g.tjenestetype || g.slug || "", g.slug) }));
  }, [slugRawGroups, productServiceSlug]);

  const toggleMessageUnread = async (m) => {
    if (!svc?._id || !m?._id) return;

    // Hvis den allerede er ulest → marker som lest igjen (valgfritt)
    if (!m.readAt) {
      // Lokalt: sett readAt til nå
      setSvc((prev) => ({
        ...prev,
        messages: prev.messages.map((x) =>
          x._id === m._id ? { ...x, readAt: new Date().toISOString() } : x
        ),
      }));
      return;
    }

    // Marker som ULEST (null)
    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/messages/${m._id}/mark-unread`,
        { method: "POST" }
      );

      const body = await r.json();
      if (!r.ok) {
        console.error("mark-unread failed:", body);
        return;
      }

      setSvc((prev) =>
        prev
          ? { ...prev, messages: body.messages }
          : prev
      );
    } catch (e) {
      console.error("toggleMessageUnread error:", e);
    }
  };
  async function handleSendQuoteEmail() {
    if (!svc?._id) return;

    const toEmail = customerEmail || customer?.email || "";
    if (!toEmail) {
      setMsg("Kan ikke sende pristilbud: mangler e-postadresse.");
      return;
    }

    const displayName =
      customerName ||
      `${customer?.firstName || ""} ${customer?.lastName || ""}`.trim() ||
      "kunde";

    setSendingQuoteEmail(true);
    setMsg("");

    try {
      await sendWorkshopQuoteEmail({
        svcId: svc._id,
        to: toEmail,
        customerName: displayName,
        customerLink, // hvis du vil at backend skal bruke den
      });

      setMsg("Pristilbud sendt på e-post.");
    } catch (e) {
      console.error("handleSendQuoteEmail failed:", e);
      setMsg("Feil ved sending av pristilbud på e-post.");
    } finally {
      setSendingQuoteEmail(false);
    }
  }




  useEffect(() => {
    if (!svc?._id) return;
    try {
      const key = `wd:smsBlockAuto:${svc._id}`;
      const v = window.sessionStorage.getItem(key);
      setAutoTemplateBlocked(v === "1");
    } catch (e) {
      console.warn("Kunne ikke lese autoTemplateBlocked fra sessionStorage", e);
    }
  }, [svc?._id]);

  useEffect(() => {
    if (!Array.isArray(svc?.messages)) {
      setCustomerMessageCount(0);
      setUnreadCustomerCount(0);
      return;
    }

    const customerMsgs = svc.messages.filter((m) => m.from === "customer");
    setCustomerMessageCount(customerMsgs.length);

    const unread = customerMsgs.filter((m) => !m.readAt);
    setUnreadCustomerCount(unread.length);
  }, [svc?.messages]);


  const filteredSmsTemplates = useMemo(() => {
    if (!smsTemplates.length) return [];

    const cat = (serviceCategory || "").trim().toLowerCase();
    const typeLower = String(produkt?.type || produkt?.produkttype || "")
      .trim()
      .toLowerCase();

    // Samle alle "signaler" vi har om type
    const signals = new Set([cat, typeLower].filter(Boolean));

    // Grupper
    const WINTER = ["skiservice", "ski", "langrenn", "alpint", "snow"];
    const BIKE = ["sykkelservice", "sykkel", "bike"];

    // Hvis det ligner vintersport → ta med hele vintergruppen
    const isWinter = WINTER.some((w) =>
      [...signals].some((s) => s === w || s.includes(w))
    );

    // Hvis det ligner sykkel → ta med hele sykkelgruppen
    const isBike = BIKE.some((b) =>
      [...signals].some((s) => s === b || s.includes(b))
    );

    const wantedCats = new Set();
    if (cat) wantedCats.add(cat);        // behold original
    if (typeLower) wantedCats.add(typeLower);

    if (isWinter) WINTER.forEach((x) => wantedCats.add(x));
    if (isBike) BIKE.forEach((x) => wantedCats.add(x));

    // Hvis vi fortsatt ikke har noe å filtrere på → vis alle
    if (!wantedCats.size) return smsTemplates;

    const matchesAny = (tplCat) => {
      if (!tplCat) return false;
      for (const w of wantedCats) {
        if (tplCat === w || tplCat.includes(w)) return true;
      }
      return false;
    };

    return smsTemplates.filter((t) => {
      const tplCat = (
        t.kategori ||
        t.category ||
        t.serviceType ||
        t.type ||
        ""
      )
        .toString()
        .trim()
        .toLowerCase();

      return matchesAny(tplCat);
    });
  }, [smsTemplates, serviceCategory, produkt?.type, produkt?.produkttype]);


  useEffect(() => {
    if (!svc || !filteredSmsTemplates.length) return;
    if (hasAutoSelectedTemplate) return; // bare auto-fyll én gang
    if (autoTemplateBlocked) return;     // 👈 ikke auto-fyll hvis blokkert


    const tpl = inferWorkshopSmsTemplate(filteredSmsTemplates, svc, produkt);
    if (!tpl) return;

    const id = String(tpl._id || tpl.id || tpl.key || "");
    if (id) {
      setSelectedTemplateId(id);
    }

    const filled = fillWorkshopSmsTemplate(tpl, {
      customer,
      svc,
      produkt,
      customerLink,
    });

    if (filled) {
      setSmsText(filled);
    }

    setHasAutoSelectedTemplate(true);
  }, [
    svc,
    filteredSmsTemplates,
    produkt,
    customer,
    customerLink,
    hasAutoSelectedTemplate,
    autoTemplateBlocked,   // 👈

  ]);


  // 🔹 LEGG TIL DENNE FUNKSJONEN:
  const closeStatusModal = () => {
    setStatusModalMode("default");
    setIncludePortalLink(false);
    setStatusModalOpen(false);
    setSmsText("");
    setSelectedTemplateId("");
    setMsg("");


    // 👇 Husk at vi har "nullstilt" for denne servicen
    if (svc?._id) {
      try {
        const key = `wd:smsBlockAuto:${svc._id}`;
        window.sessionStorage.setItem(key, "1");
        setAutoTemplateBlocked(true);
      } catch (e) {
        console.warn("Kunne ikke lagre autoTemplateBlocked i sessionStorage", e);
      }
    }
  };





  // slå av/på egen print-kontekst for pristilbud (koblet til useQuote)
  useEffect(() => {
    if (quote.open) {
      document.body.classList.add("printing-quote");
    } else {
      document.body.classList.remove("printing-quote");
    }
    return () => document.body.classList.remove("printing-quote");
  }, [quote.open]);

  useEffect(() => {
    if (!statusModalOpen) return;
    if (!svc?.status) return;

    // IKKE overstyr når vi åpner med Ferdig-knapp
    if (statusModalMode === "ready") return;

    setSelectedStatus(svc.status);
  }, [statusModalOpen, svc?.status, statusModalMode]);



  useEffect(() => {
    const run = async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/workshop-services/${id}`);
        if (!r.ok) throw new Error("Kunne ikke hente service");
        const data = await r.json();
        setSvc(data);
        setKontakt(data?.kontakt || null);

        // Automatisk påminnelse: kunden ønsker pristilbud, men det er ikke aktivert ennå
        if (
          data?.produkt?.prisoverslag &&
          data?.quote?.enabled !== true &&
          data?.status !== "Tilbud godkjent" &&
          pristilbudReminderShownRef.current !== data._id
        ) {
          pristilbudReminderShownRef.current = data._id;
          setShowPristilbudReminderModal(true);
        }

        const siblings = await findSiblingServices(data);
        setSiblingServices(siblings);


        // Sett kundelenke hvis token finnes
        if (data?.portal?.token && data?.portal?.enabled !== false) {
          setCustomerLink(
            `https://status.roykensykkelservice.no/svc/${data.portal.token}`
          );
        } else {
          setCustomerLink(null);
        }

        setWorkDesc(data?.arbeidsbeskrivelse || "");
        setQuoteDesc(data?.quoteDescription || ""); // 👈 ny linje


        if (data?.customerId) {
          const cr = await fetch(
            `${API_BASE_URL}/customers/${data.customerId}`
          );
          if (cr.ok) setCustomer(await cr.json());
        }
      } catch (e) {
        console.error("FEIL i WorkshopDetails:", e);
      }
    };
    run();
  }, [id]);


  useEffect(() => {
    fetchLinkedBike(svc?.bikeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svc?.bikeId]);

  // Elsykkelservice uten sykkelkort: spør om sykkelkort én gang når servicen åpnes
  // (SykkelkortForslag.jsx). Ikke på avsluttede servicer, og ikke når en ansatt har avstått.
  const [sykkelkortForslagOpen, setSykkelkortForslagOpen] = useState(false);
  const sykkelkortForslagVist = useRef(false);
  useEffect(() => {
    if (sykkelkortForslagVist.current || !svc?._id) return;
    if (!erElService || svc.bikeId || svc.sykkelkortAvstatt || svc.status === "avsluttet") return;
    sykkelkortForslagVist.current = true;
    setSykkelkortForslagOpen(true);
  }, [svc?._id, svc?.bikeId, svc?.sykkelkortAvstatt, svc?.status, erElService]);

function openCommentModal() {
  setCommentDraft(svc?.produkt?.kommentar || "");
  setProductBrandDraft(svc?.produkt?.produktmerke || "");
  setProductColorDraft(svc?.produkt?.farge || "");
  setEditPriorGrunn(svc?.produkt?.prioritering?.grunn || "");
  setEditPriorForklaring(svc?.produkt?.prioritering?.forklaring || "");
  setEditPriorHaster(!!svc?.produkt?.prioritering?.haster);
  setSlugDraft(productServiceSlug || "");
  setCommentModalOpen(true);
  loadSlugOptions();
}

function closeCommentModal() {
  setCommentModalOpen(false);
  setCommentDraft("");
  setProductBrandDraft("");
  setProductColorDraft("");
  setEditPriorGrunn("");
  setEditPriorForklaring("");
  setEditPriorHaster(false);
}

async function loadSlugOptions() {
  setSlugOptionsLoading(true);
  try {
    const grupper = await getTjenester();
    setSlugRawGroups(grupper || []);
  } catch (e) {
    console.error("loadSlugOptions feil:", e);
  } finally {
    setSlugOptionsLoading(false);
  }
}

async function saveProductComment() {
  if (!svc?._id) return;

  setCommentBusy(true);
  setMsg("");

  try {
    const isFortlopende =
      String(svc?.produkt?.bookingtype || "").toLowerCase() === "fortlopende";

    const slugChanged = slugDraft && slugDraft !== productServiceSlug;
    const payload = {
      kommentar: (commentDraft || "").trim(),
      produktmerke: (productBrandDraft || "").trim(),
      farge: (productColorDraft || "").trim(),
      ...(isFortlopende && {
        prioritering: {
          grunn: editPriorGrunn,
          forklaring: editPriorForklaring.trim(),
          haster: editPriorHaster,
        },
      }),
      ...(slugChanged && {
        slug: slugDraft,
        produkttype: filteredSlugOptions.find((o) => o.slug === slugDraft)?.navn || slugDraft,
      }),
    };

    const r = await fetch(
      `${API_BASE_URL}/workshop-services/${svc._id}/product-comment`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    const updated = await r.json().catch(() => null);

    if (!r.ok) {
      console.error("saveProductComment failed:", updated);
      throw new Error(updated?.message || "Kunne ikke lagre produktinfo");
    }

    setSvc(updated);
    setMsg("Produktinfo oppdatert.");
    closeCommentModal();
  } catch (e) {
    console.error("saveProductComment error:", e);
    setMsg("Feil ved lagring av produktinfo.");
  } finally {
    setCommentBusy(false);
  }
}

  async function findSiblingServices(currentService) {
    if (!currentService?._id) return [];

    try {
      setSiblingsLoading(true);

      const r = await fetch(`${API_BASE_URL}/workshop-services`);
      if (!r.ok) return [];

      const all = await r.json();
      if (!Array.isArray(all)) return [];

      const myId = currentService._id;
      const myCustomerId = currentService.customerId;
      const myPhone = normalizePhone(
        currentService?.customerSnapshot?.phoneNumber || ""
      );

      const ACTIVE_STATUSES = [
        "mottatt",
        "aktiv",
        "venter på deler",
        "pristilbud",
        "venter på godkjenning",
        "godkjent"
      ];

      const siblings = all.filter(s => {
        if (!s || s._id === myId) return false;
        if (!ACTIVE_STATUSES.includes(s.status)) return false;

        if (myCustomerId && s.customerId === myCustomerId) return true;

        const otherPhone = normalizePhone(
          s?.customerSnapshot?.phoneNumber || ""
        );

        return myPhone && otherPhone && myPhone === otherPhone;
      });

      return siblings;

    } catch (e) {
      console.error("findSiblingServices error:", e);
      return [];
    } finally {
      setSiblingsLoading(false);
    }
  }



  const serviceLines = useMemo(
    () => (Array.isArray(svc?.serviceLines) ? svc.serviceLines : []),
    [svc]
  );
  const hasGarantiServiceLine = serviceLines.some(
    (l) => String(l.slug || "").toLowerCase() === "garantihenvendelse"
  );
  const partLines = useMemo(() => tilDelelinjer(svc?.partsLines), [svc]);

  const fraktLines = useMemo(
    () => (Array.isArray(svc?.fraktLines) ? svc.fraktLines : []),
    [svc]
  );

  const serviceImages = useMemo(
    () => (Array.isArray(svc?.images) ? svc.images : []),
    [svc]
  );

  const visibleCustomerImageCount = useMemo(
  () => serviceImages.filter((img) => img.visibleToCustomer === true).length,
  [serviceImages]
);

  const customerUploadedImages = useMemo(
    () => serviceImages.filter((img) => img.uploadedByCustomer === true),
    [serviceImages]
  );

  const mechanicImages = useMemo(
    () => serviceImages.filter((img) => !img.uploadedByCustomer),
    [serviceImages]
  );

  const buildImageUrl = useCallback((url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${API_BASE_URL}${url}`;
  }, []);

  const serviceDocuments = useMemo(
    () => (Array.isArray(svc?.documents) ? svc.documents : []),
    [svc]
  );

  const visibleCustomerDocumentCount = useMemo(
    () => serviceDocuments.filter((doc) => doc.visibleToCustomer === true).length,
    [serviceDocuments]
  );

  // === SJEKKLISTE (hook) ===
  const checklist = useChecklist({
    svcId: svc?._id,
    serviceLines,
    apiBaseUrl: API_BASE_URL,
    getChecklistsForServiceSlugs,
  });

  const hasChecklistEligible = useMemo(
    () => (serviceLines || []).some(isLineChecklistEligible),
    [serviceLines]
  );

  // Hent SMS-maler når service er lastet
  useEffect(() => {
    if (!svc?._id) return;

    let cancelled = false;

    const fetchTemplates = async () => {
      setSmsTemplateLoading(true);
      try {
        const r = await fetch(`${API_BASE_URL}/smstemplates`);
        if (!r.ok) throw new Error("Kunne ikke hente SMS-maler");
        const data = await r.json();
        const list = Array.isArray(data) ? data : data.items || [];
        if (!cancelled) {
          setSmsTemplates(list);
        }
      } catch (e) {
        console.error("[WorkshopDetails] Feil ved henting av SMS-maler:", e);
        if (!cancelled) setSmsTemplates([]);
      } finally {
        if (!cancelled) setSmsTemplateLoading(false);
      }
    };

    fetchTemplates();

    return () => {
      cancelled = true;
    };
  }, [svc?._id]);


  useEffect(() => {
    if (!svc?.produkt) {
      setExpectedReadyDate("");
      return;
    }
    const raw = svc.produkt.forventetFerdigDato;
    setExpectedReadyDate(raw ? String(raw).slice(0, 10) : "");
  }, [svc?.produkt, statusModalOpen]); // 👈 endret dependency



  function handleTemplateChange(e) {
    const id = e.target.value;

    // Hvis “ingen mal”
    if (!id) {
      setSelectedTemplateId("");
      setSmsText("");
      setHasAutoSelectedTemplate(false);
      setAutoTemplateBlocked(true);

      if (svc?._id) {
        const key = `wd:smsBlockAuto:${svc._id}`;
        window.sessionStorage.setItem(key, "1");
      }
      return; // ferdig
    }

    // Ellers: vanlig logikk for valgt mal
    setSelectedTemplateId(id);

    if (svc?._id) {
      const key = `wd:smsBlockAuto:${svc._id}`;
      window.sessionStorage.removeItem(key);
      setAutoTemplateBlocked(false);
    }

    const tpl = filteredSmsTemplates.find(
      (t) => String(t._id || t.id || t.key || "") === id
    );
    if (!tpl) return;

    const filled = fillWorkshopSmsTemplate(tpl, {
      customer,
      svc,
      produkt,
      customerLink,
    });

    setSmsText(filled || "");
  }


  const markAllCustomerMessagesAsRead = useCallback(async () => {
    if (!svc?._id) return;

    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/messages/mark-read`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Bare den som sendte siste melding, merker svaret lest (src/lib/varselAnsvarlig.js).
          body: JSON.stringify({ ansatt: Cookies.get("selectedEmployee") || "" }),
        }
      );

      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        console.error("markAllCustomerMessagesAsRead failed:", body);
        return;
      }

      const messages = Array.isArray(body.messages)
        ? body.messages
        : svc.messages || [];

      // Oppdater service med nye meldinger (nå med readAt)
      setSvc((prev) =>
        prev
          ? {
            ...prev,
            messages,
          }
          : prev
      );
    } catch (e) {
      console.error("markAllCustomerMessagesAsRead error:", e);
    }
  }, [svc?._id, svc?.messages]);

  async function saveExpectedReadyDateOnService(isoDate) {
    if (!svc?._id) return;

    try {
      setUpdating(true);
      setMsg("");

      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/expected-ready-date`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ expectedReadyDate: isoDate }),
        }
      );

      const updated = await r.json().catch(() => null);
      if (!r.ok) {
        console.error("expected-ready-date failed:", updated);
        throw new Error("Kunne ikke lagre forventet ferdig-dato");
      }

      setSvc(updated);
      setExpectedReadyDate(
        updated?.produkt?.forventetFerdigDato
          ? String(updated.produkt.forventetFerdigDato).slice(0, 10)
          : ""
      );

      setMsg("Forventet ferdig-dato er oppdatert.");
    } catch (e) {
      console.error(e);
      setMsg("Feil ved oppdatering av forventet ferdig-dato.");
    } finally {
      setUpdating(false);
    }
  }

  useEffect(() => {
    const raw = svc?.produkt?.onsketFerdigDato;
    setOnsketFerdigDatoInput(raw ? String(raw).slice(0, 10) : "");
  }, [svc?.produkt?.onsketFerdigDato]);

  async function saveOnsketFerdigDato(isoDate) {
    if (!svc?._id) return;
    try {
      setUpdating(true);
      setMsg("");
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/onsket-ferdig-dato`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ onsketFerdigDato: isoDate || null }),
        }
      );
      const updated = await r.json().catch(() => null);
      if (!r.ok) throw new Error(updated?.message || "Kunne ikke lagre ønsket ferdigdato");
      setSvc(updated);
      setMsg(isoDate ? "Ønsket ferdigdato er oppdatert." : `Ønsket ferdigdato fjernet («${IKKE_SATT}»).`);
    } catch (e) {
      console.error(e);
      setMsg("Feil ved oppdatering av ønsket ferdigdato.");
    } finally {
      setUpdating(false);
    }
  }

  async function removeExpectedReadyDate() {
    if (
      !window.confirm(
        "Fjerne ferdigdato? Servicen vil da vises under «Manglende ferdigdato» i servicelisten."
      )
    )
      return;
    await saveExpectedReadyDateOnService(null);
  }



  useEffect(() => {
    const onEmployeeUpdated = () => setChatEmployee(Cookies.get("selectedEmployee") || "");
    window.addEventListener("employeeUpdated", onEmployeeUpdated);
    return () => window.removeEventListener("employeeUpdated", onEmployeeUpdated);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/employees`);
        const data = await r.json().catch(() => []);
        if (cancelled || !Array.isArray(data)) return;
        const store = Cookies.get("selectedStore");
        setChatEmployees(
          data.filter((emp) => jobberI(emp, store))
        );
      } catch (e) {
        console.error("Kunne ikke hente ansatte til dialogen:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleChatEmployeeChange(e) {
    const selected = e.target.value;
    if (!selected) return;
    const match = chatEmployees.find((emp) => emp.navn === selected);
    Cookies.set("selectedEmployee", selected);
    Cookies.set("selectedEmployeeTilgang", match?.tilgang || "ansatt");
    setChatEmployee(selected);
    window.dispatchEvent(new Event("employeeUpdated"));
  }

  async function sendWorkshopMessage() {
    if (!svc?._id) return;

    const text = messageText.trim();
    if (!text) return;

    setSendingMessage(true);
    setSendMsgError("");

    const payload = {
      from: "workshop",
      fromName: employeeName,
      ansatt: chatEmployee,
      text,
    };

    try {
      const url = `${API_BASE_URL}/workshop-services/${svc._id}/messages`;

      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const body = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(body?.message || "Kunne ikke sende melding");
      }

      // Oppdater meldinger i UI
      const updatedMessages = Array.isArray(body.messages)
        ? body.messages
        : svc.messages || [];

      setSvc((prev) => ({
        ...prev,
        messages: updatedMessages,
      }));

      // 🧹 Tøm inputfeltet
      setMessageText("");

      // 🔔 SEND VARSEL-SMS TIL KUNDEN (NYTT: send riktige felter)
      if (!customerPhone) {
        console.warn(
          "[sendWorkshopMessage] Ingen telefonnummer på servicen – skipper SMS-varsel."
        );
        return;
      }
      if (!customerLink) {
        console.warn(
          "[sendWorkshopMessage] Ingen kundelenke – skipper SMS-varsel."
        );
        return;
      }

      await notifyCustomerNewDialogMessage({
        svc,
        text,
        firstName: customer?.firstName || "",
        customerPhone,
        customerLink,
        produkt: svc.produkt || {},
      });
    } catch (e) {
      console.error("sendWorkshopMessage failed:", e);
      setSendMsgError(e.message || "Ukjent feil");
    } finally {
      setSendingMessage(false);
    }
  }

  async function uploadWorkshopImages(files, visibleToCustomer = false) {
    if (!svc?._id || !files?.length) return;

    setImageUploading(true);
    setMsg("");

    try {
      const formData = new FormData();

      Array.from(files).forEach((file) => {
        formData.append("images", file);
      });

      formData.append("uploadedByName", Cookies.get("selectedEmployee") || "Mekaniker");
      formData.append("uploadedById", Cookies.get("employeeId") || "");
      formData.append("uploadedByStore", Cookies.get("butikk") || "");
      formData.append("visibleToCustomer", visibleToCustomer ? "true" : "false");

      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/images`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await r.json().catch(() => null);

      if (!r.ok) {
        console.error("uploadWorkshopImages failed:", data);
        throw new Error(data?.message || "Kunne ikke laste opp bilder");
      }

      setSvc((prev) =>
        prev
          ? {
              ...prev,
              images: Array.isArray(data?.images) ? data.images : [],
            }
          : prev
      );

      setMsg("Bilder lastet opp.");
    } catch (e) {
      console.error("uploadWorkshopImages error:", e);
      setMsg("Feil ved opplasting av bilder.");
    } finally {
      setImageUploading(false);
    }
  }

  async function toggleImageVisibleToCustomer(imageId, visibleToCustomer) {
  if (!svc?._id || !imageId) return;

  setImageBusyId(imageId);
  setMsg("");

  try {
    const r = await fetch(
      `${API_BASE_URL}/workshop-services/${svc._id}/images/${imageId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibleToCustomer }),
      }
    );

    const data = await r.json().catch(() => null);

    if (!r.ok) {
      console.error("toggleImageVisibleToCustomer failed:", data);
      throw new Error(data?.message || "Kunne ikke oppdatere bildesynlighet");
    }

    setSvc((prev) =>
      prev
        ? {
            ...prev,
            images: Array.isArray(data?.images) ? data.images : [],
          }
        : prev
    );

    setMsg(
      visibleToCustomer
        ? "Bildet er synlig for kunden."
        : "Bildet er skjult for kunden."
    );
  } catch (e) {
    console.error("toggleImageVisibleToCustomer error:", e);
    setMsg("Feil ved oppdatering av bildesynlighet.");
  } finally {
    setImageBusyId("");
  }
}

    async function deleteWorkshopImage(imageId) {
    if (!svc?._id || !imageId) return;

    setImageBusyId(imageId);
    setMsg("");

    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/images/${imageId}`,
        {
          method: "DELETE",
        }
      );

      const data = await r.json().catch(() => null);

      if (!r.ok) {
        console.error("deleteWorkshopImage failed:", data);
        throw new Error(data?.message || "Kunne ikke slette bilde");
      }

      setSvc((prev) =>
        prev
          ? {
              ...prev,
              images: Array.isArray(data?.images) ? data.images : [],
            }
          : prev
      );

      setMsg("Bilde slettet.");
    } catch (e) {
      console.error("deleteWorkshopImage error:", e);
      setMsg("Feil ved sletting av bilde.");
    } finally {
      setImageBusyId("");
    }
  }

  async function uploadWorkshopDocuments(files, visibleToCustomer = false) {
    if (!svc?._id || !files?.length) return;

    setDocumentUploading(true);
    setMsg("");

    try {
      const formData = new FormData();

      Array.from(files).forEach((file) => {
        formData.append("documents", file);
      });

      formData.append("uploadedByName", Cookies.get("selectedEmployee") || "Mekaniker");
      formData.append("uploadedById", Cookies.get("employeeId") || "");
      formData.append("uploadedByStore", Cookies.get("butikk") || "");
      formData.append("visibleToCustomer", visibleToCustomer ? "true" : "false");

      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/documents`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await r.json().catch(() => null);

      if (!r.ok) {
        console.error("uploadWorkshopDocuments failed:", data);
        throw new Error(data?.message || "Kunne ikke laste opp dokumenter");
      }

      setSvc((prev) =>
        prev
          ? {
              ...prev,
              documents: Array.isArray(data?.documents) ? data.documents : [],
            }
          : prev
      );

      setMsg("Dokumenter lastet opp.");
    } catch (e) {
      console.error("uploadWorkshopDocuments error:", e);
      setMsg("Feil ved opplasting av dokumenter.");
    } finally {
      setDocumentUploading(false);
    }
  }

  async function toggleDocumentVisibleToCustomer(docId, visibleToCustomer) {
    if (!svc?._id || !docId) return;

    setDocumentBusyId(docId);
    setMsg("");

    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/documents/${docId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visibleToCustomer }),
        }
      );

      const data = await r.json().catch(() => null);

      if (!r.ok) {
        console.error("toggleDocumentVisibleToCustomer failed:", data);
        throw new Error(data?.message || "Kunne ikke oppdatere dokumentsynlighet");
      }

      setSvc((prev) =>
        prev
          ? {
              ...prev,
              documents: Array.isArray(data?.documents) ? data.documents : [],
            }
          : prev
      );

      setMsg(
        visibleToCustomer
          ? "Dokumentet er synlig for kunden."
          : "Dokumentet er skjult for kunden."
      );
    } catch (e) {
      console.error("toggleDocumentVisibleToCustomer error:", e);
      setMsg("Feil ved oppdatering av dokumentsynlighet.");
    } finally {
      setDocumentBusyId("");
    }
  }

  async function deleteWorkshopDocument(docId) {
    if (!svc?._id || !docId) return;

    setDocumentBusyId(docId);
    setMsg("");

    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/documents/${docId}`,
        {
          method: "DELETE",
        }
      );

      const data = await r.json().catch(() => null);

      if (!r.ok) {
        console.error("deleteWorkshopDocument failed:", data);
        throw new Error(data?.message || "Kunne ikke slette dokument");
      }

      setSvc((prev) =>
        prev
          ? {
              ...prev,
              documents: Array.isArray(data?.documents) ? data.documents : [],
            }
          : prev
      );

      setMsg("Dokument slettet.");
    } catch (e) {
      console.error("deleteWorkshopDocument error:", e);
      setMsg("Feil ved sletting av dokument.");
    } finally {
      setDocumentBusyId("");
    }
  }


  // --- Sykkelkort ---
  const BIKE_TYPES = [
    "landevei", "gravel", "MTB", "FS", "Hybrid",
    "barnesykkel", "E-MTB", "E-FS", "E-trekking",
  ];
  const BIKE_TYPE_LABELS = {
    landevei: "Landevei", gravel: "Gravel", MTB: "MTB", FS: "FS",
    Hybrid: "Hybrid", barnesykkel: "Barnesykkel",
    "E-MTB": "E-MTB", "E-FS": "E-FS", "E-trekking": "E-trekking",
  };

  async function fetchLinkedBike(bikeId) {
    if (!bikeId) { setLinkedBike(null); return; }
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${bikeId}`);
      if (r.ok) setLinkedBike(await r.json());
      else setLinkedBike(null);
    } catch { setLinkedBike(null); }
  }

  async function fetchBikesForModal() {
    const key = svc?.phoneKey || normalizePhone(customerPhone);
    if (!svc?.customerId && !key) { setBikes([]); return; }
    setBikesLoading(true);
    try {
      // Kundens sykkelkort på kunde-ID; uten kunde på servicen, på nummeret.
      const sok = svc?.customerId
        ? `customerId=${encodeURIComponent(svc.customerId)}`
        : `phoneKey=${encodeURIComponent(key)}`;
      const r = await fetch(`${API_BASE_URL}/bikes?${sok}`);
      setBikes(r.ok ? await r.json() : []);
    } catch { setBikes([]); }
    finally { setBikesLoading(false); }
  }

  function openBikeModal() {
    setBikeForm({ merke: "", modell: "", årsmodell: "", type: "", elsykkel: null, rammenummer: "" });
    setEditingBikeId(null);
    setBikeSaveError("");
    setBikeModalTab("list");
    setBikeModalOpen(true);
    fetchBikesForModal();
  }

  async function saveBike() {
    setBikeSaveError("");
    const key = svc?.phoneKey || normalizePhone(customerPhone);
    if (!key) { setBikeSaveError("Ingen telefonnummer tilknyttet denne servicen."); return; }
    if (!bikeForm.merke.trim()) { setBikeSaveError("Merke er påkrevd."); return; }
    if (!bikeForm.modell.trim()) { setBikeSaveError("Modell er påkrevd."); return; }
    if (!bikeForm.type) { setBikeSaveError("Type er påkrevd."); return; }
    if (bikeForm.elsykkel === null) { setBikeSaveError("Du må angi om sykkelen er elsykkel eller ikke."); return; }
    setBikeBusy(true);
    try {
      // Kunde-ID og service, så sykkelkortet kobles til kunden (og vises på Din side).
      const body = { ...bikeForm, phoneKey: key, customerId: svc?.customerId, serviceId: svc?._id };
      const url = editingBikeId
        ? `${API_BASE_URL}/bikes/${editingBikeId}`
        : `${API_BASE_URL}/bikes`;
      const r = await fetch(url, {
        method: editingBikeId ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) { setBikeSaveError(data?.message || "Kunne ikke lagre"); return; }
      if (!editingBikeId) {
        await linkBike(data._id);
      } else {
        await fetchBikesForModal();
        if (svc?.bikeId === editingBikeId || String(svc?.bikeId) === String(editingBikeId)) {
          setLinkedBike(data);
        }
        setEditingBikeId(null);
        setBikeModalTab("list");
      }
    } finally { setBikeBusy(false); }
  }

  async function linkBike(bikeId) {
    if (!svc?._id) return;
    setBikeBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}/bike`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bikeId }),
      });
      if (r.ok) {
        const updated = await r.json();
        setSvc(updated);
        await fetchLinkedBike(bikeId);
        setBikeModalOpen(false);
      }
    } finally { setBikeBusy(false); }
  }

  async function unlinkBike() {
    if (!svc?._id) return;
    setBikeBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}/bike`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bikeId: null }),
      });
      if (r.ok) {
        const updated = await r.json();
        setSvc(updated);
        setLinkedBike(null);
      }
    } finally { setBikeBusy(false); }
  }

  async function uploadBikeSpecImage(file) {
    if (!linkedBike?._id || !file) return;
    setBikeSpecImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const r = await fetch(`${API_BASE_URL}/bikes/${linkedBike._id}/spec-image`, {
        method: "POST",
        body: formData,
      });
      if (r.ok) {
        const { specImageUrl } = await r.json();
        setLinkedBike((b) => ({ ...b, specImageUrl }));
      }
    } finally {
      setBikeSpecImageUploading(false);
    }
  }

  async function deleteBikeSpecImage() {
    if (!linkedBike?._id) return;
    setBikeSpecImageUploading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${linkedBike._id}/spec-image`, { method: "DELETE" });
      if (r.ok) setLinkedBike((b) => ({ ...b, specImageUrl: "" }));
    } finally {
      setBikeSpecImageUploading(false);
    }
  }

  const flashModalBikeImgMsg = (ok, text) => {
    setModalBikeImgMsg({ ok, text });
    setTimeout(() => setModalBikeImgMsg(null), 3000);
  };

  async function uploadModalBikeSpec(file) {
    if (!editingBikeId || !file) return;
    setModalBikeSpecUploading(true);
    try {
      const fd = new FormData(); fd.append("image", file);
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/spec-image`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, specImageUrl: data.specImageUrl }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, specImageUrl: data.specImageUrl }));
      flashModalBikeImgMsg(true, "Lastet opp");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
    finally { setModalBikeSpecUploading(false); }
  }

  async function deleteModalBikeSpec() {
    if (!editingBikeId) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/spec-image`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      setModalBikeImageData(p => ({ ...p, specImageUrl: "" }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, specImageUrl: "" }));
      flashModalBikeImgMsg(true, "Slettet");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
  }

  async function uploadModalBikeMain(file) {
    if (!editingBikeId || !file) return;
    setModalBikeMainUploading(true);
    try {
      const fd = new FormData(); fd.append("image", file);
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/main-image`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, mainImageUrl: data.mainImageUrl }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, mainImageUrl: data.mainImageUrl }));
      flashModalBikeImgMsg(true, "Lastet opp");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
    finally { setModalBikeMainUploading(false); }
  }

  async function deleteModalBikeMain() {
    if (!editingBikeId) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/main-image`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      setModalBikeImageData(p => ({ ...p, mainImageUrl: "" }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, mainImageUrl: "" }));
      flashModalBikeImgMsg(true, "Slettet");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
  }

  async function uploadModalBikeReceipt(file) {
    if (!editingBikeId || !file) return;
    setModalBikeReceiptUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/receipt`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, receiptUrl: data.receiptUrl }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, receiptUrl: data.receiptUrl }));
      flashModalBikeImgMsg(true, "Lastet opp");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
    finally { setModalBikeReceiptUploading(false); }
  }

  async function deleteModalBikeReceipt() {
    if (!editingBikeId) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/receipt`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      setModalBikeImageData(p => ({ ...p, receiptUrl: "" }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, receiptUrl: "" }));
      flashModalBikeImgMsg(true, "Slettet");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
  }

  async function uploadModalBikeExtra(files) {
    if (!editingBikeId) return;
    setModalBikeExtraUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append("images", f));
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/extra-images`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, extraImages: data.extraImages }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, extraImages: data.extraImages }));
      flashModalBikeImgMsg(true, "Lastet opp");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
    finally { setModalBikeExtraUploading(false); }
  }

  async function deleteModalBikeExtra(publicId) {
    if (!editingBikeId) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/extra-images/${encodeURIComponent(publicId)}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, extraImages: data.extraImages }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, extraImages: data.extraImages }));
      flashModalBikeImgMsg(true, "Slettet");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
  }

  async function uploadModalBikeDiagnose(files) {
    if (!editingBikeId) return;
    setModalBikeDiagnoseUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append("files", f));
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/diagnose-reports`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, diagnoseRapporter: data.diagnoseRapporter }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, diagnoseRapporter: data.diagnoseRapporter }));
      flashModalBikeImgMsg(true, "Lastet opp");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
    finally { setModalBikeDiagnoseUploading(false); }
  }

  async function deleteModalBikeDiagnose(publicId) {
    if (!editingBikeId) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/diagnose-reports/${encodeURIComponent(publicId)}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, diagnoseRapporter: data.diagnoseRapporter }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, diagnoseRapporter: data.diagnoseRapporter }));
      flashModalBikeImgMsg(true, "Slettet");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
  }

  async function uploadModalBikeSoftware(files) {
    if (!editingBikeId) return;
    setModalBikeSoftwareUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append("files", f));
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/software-reports`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("Opplasting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, softwareRapporter: data.softwareRapporter }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, softwareRapporter: data.softwareRapporter }));
      flashModalBikeImgMsg(true, "Lastet opp");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
    finally { setModalBikeSoftwareUploading(false); }
  }

  async function deleteModalBikeSoftware(publicId) {
    if (!editingBikeId) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/${editingBikeId}/software-reports/${encodeURIComponent(publicId)}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Sletting feilet");
      const data = await r.json();
      setModalBikeImageData(p => ({ ...p, softwareRapporter: data.softwareRapporter }));
      if (String(svc?.bikeId) === String(editingBikeId)) setLinkedBike(b => ({ ...b, softwareRapporter: data.softwareRapporter }));
      flashModalBikeImgMsg(true, "Slettet");
    } catch(e) { flashModalBikeImgMsg(false, e.message); }
  }

  async function handleBikeScan(raw) {
    setBikeScannerOpen(false);
    let ref = raw.trim();
    const match = ref.match(/\/sykkel\/([^/?#\s]+)/);
    if (match) ref = match[1];
    if (!ref) return;
    setBikeBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/bikes/ref/${encodeURIComponent(ref)}`);
      if (!r.ok) { setMsg("Fant ikke sykkel for denne QR-koden."); return; }
      const { bike } = await r.json();
      await linkBike(bike._id);
    } catch {
      setMsg("Feil ved oppslag av sykkelkort.");
    } finally {
      setBikeBusy(false);
    }
  }

  async function printBikeLabel() {
    if (!linkedBike?.token) return;
    const url = `https://status.roykensykkelservice.no/sykkel/${linkedBike.token}`;
    const qrDataUrl = await QRCode.toDataURL(url, { width: 300, margin: 1, errorCorrectionLevel: "M" });
    const logoUrl = `${window.location.origin}/RoykenSykkelservice_logo.png`;
    const shortId = linkedBike.shortId || "";

    const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<title>Sykkelkort</title>
<style>
  @page { size: 30mm 50mm; margin: 0; }
  html, body { width: 30mm; height: 50mm; margin: 0; padding: 0; background: white; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; }
  .label { width: 30mm; height: 50mm; box-sizing: border-box; padding: 2mm; display: flex; flex-direction: column; align-items: center; overflow: hidden; }
  .logo { width: 100%; height: 7mm; object-fit: contain; object-position: center; }
  .qr { width: 22mm; height: 22mm; margin-top: 2mm; }
  .shortid { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 14pt; font-weight: 800; letter-spacing: 0.2em; margin-top: 2mm; line-height: 1; }
</style>
</head>
<body>
  <div class="label">
    <img class="logo" src="${logoUrl}" alt=""/>
    <img class="qr" src="${qrDataUrl}" alt="QR"/>
    <div class="shortid">${shortId}</div>
  </div>
  <script>
    let n = 0;
    const done = () => { n++; if (n >= 2) { window.print(); setTimeout(() => window.close(), 300); } };
    const logo = document.querySelector('.logo');
    const qr = document.querySelector('img.qr');
    logo.onload = done; logo.onerror = done;
    qr.onload = done; qr.onerror = done;
    setTimeout(() => { window.print(); setTimeout(() => window.close(), 500); }, 2500);
  ${"</script>"}
</body></html>`;

    const w = window.open("", "print_bike_label", "width=400,height=300");
    if (!w) return alert("Popup ble blokkert. Tillat popups for å skrive ut etikett.");
    w.document.open();
    w.document.write(html);
    w.document.close();
  }

  async function sendStatusEmailWrapped(opts) {
    try {
      setEmailBusy(true);
      await sendWorkshopStatusEmail(opts);
      setMsg(
        opts.emailKind === "quote-link"
          ? "Pristilbud sendt på e-post."
          : "E-post sendt."
      );
      return true;
    } catch (e) {
      console.error(e);
      setMsg("En feil oppstod ved sending av e-post.");
      return false;
    } finally {
      setEmailBusy(false);
    }
  }


  const totals = useMemo(
    () => beregnTotaler(serviceLines, partLines, fraktLines),
    [serviceLines, partLines, fraktLines]
  );

  const setPartDraft = (index, patch) => {
    setPartDiscountDraft((prev) => ({
      ...prev,
      [index]: {
        ...(prev[index] || {}),
        ...patch,
        // 👇 husk hvilket felt som var sist brukt
        _mode:
          patch.percent != null ? "percent" :
            patch.discount != null ? "kr" :
              (prev[index]?._mode || null),
      },
    }));
  };

  const clearPartDraft = (index) => {
    setPartDiscountDraft((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const inferDiscountMode = (index, line) => {
    const draftMode = partDiscountDraft?.[index]?._mode;
    if (draftMode) return draftMode;

    const dk = Number(line?.discount) || 0;
    const dp = Number(line?.discountPercent) || 0;

    // Hvis bare én er satt, bruk den
    if (dp > 0 && dk <= 0) return "percent";
    if (dk > 0 && dp <= 0) return "kr";

    // Hvis begge finnes, velg en default (jeg anbefaler % som “sannhet”)
    if (dp > 0) return "percent";
    if (dk > 0) return "kr";

    return null;
  };

  const recomputeDiscountPair = ({ mode, subtotal, discount, discountPercent }) => {
    const dk = Math.max(0, Number(discount) || 0);
    const dp = Math.max(0, Number(discountPercent) || 0);

    if (subtotal <= 0) return { discount: 0, discountPercent: 0 };

    if (mode === "percent") {
      const nextPercent = Math.max(0, Math.min(100, dp));
      const nextDiscount = Math.round((subtotal * nextPercent) / 100);
      return { discount: nextDiscount, discountPercent: nextPercent };
    }

    if (mode === "kr") {
      const nextDiscount = Math.max(0, dk);
      const nextPercent = Math.round((nextDiscount / subtotal) * 1000) / 10; // 1 desimal
      return { discount: nextDiscount, discountPercent: nextPercent };
    }

    // Ingen rabatt
    return { discount: 0, discountPercent: 0 };
  };

  const commitPartDiscount = async (index, mode, rawValue, subtotal) => {
    // mode: "kr" | "percent"
    const cleaned = String(rawValue ?? "").replace(",", "."); // tillat 7,5
    const num = Math.max(0, Number(cleaned) || 0);

    let nextDiscount = 0;
    let nextPercent = 0;

    if (mode === "kr") {
      nextDiscount = num;
      nextPercent =
        subtotal > 0 ? Math.round((nextDiscount / subtotal) * 1000) / 10 : 0;
    } else {
      nextPercent = Math.max(0, Math.min(100, num));
      nextDiscount =
        subtotal > 0 ? Math.round((subtotal * nextPercent) / 100) : 0;
    }

    // 🔥 PATCH én gang
    await updatePartField(index, {
      discount: nextDiscount,
      discountPercent: nextPercent,
    });

    // tøm draft når server har oppdatert
    clearPartDraft(index);
  };


  async function enableQuoteAfterSms({ resetApproval = false } = {}) {
    if (!svc?._id) return;

    const quotePatch = resetApproval
      ? { enabled: true, approved: null } // slå på + nullstill godkjenning
      : { enabled: true }; // bare slå på

    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quote: quotePatch }),
      });

      if (r.ok) {
        const updated = await r.json();
        setSvc(updated);
        return;
      }
    } catch (e) {
      console.error(
        "[enableQuoteAfterSms] PATCH feilet, faller tilbake til lokal state:",
        e
      );
    }

    // Fallback: oppdater lokal state hvis PATCH feiler
    setSvc((prev) => ({
      ...prev,
      quote: {
        ...(prev?.quote || {}),
        ...quotePatch,
      },
    }));
  }


  // ---------- Status ----------
  // A) Send VARSEL ved STATUSENDRING (modalen "Endre status")
  async function notifyStatusChange(nextStatus) {
    const targetForCheck = nextStatus || selectedStatus || svc?.status;
    if (targetForCheck === "avsluttet" && !expectedReadyDate) {
      setPendingAvsluttFromModal(true);
      setShowFerdigDatoWarning(true);
      return;
    }

    if (!customerLink) {
      setMsg("Ingen kundelenke. Opprett lenken først.");
      return;
    }
    if (!sendOnStatusChange && !sendEmailOnStatusChange) {
      setMsg("Velg SMS og/eller e-post for å sende.");
      return;
    }

    const targetStatus =
      nextStatus ||
      selectedStatus || // bruk valgt status i modal
      svc?.status;

    // 1) Oppdater status hvis den faktisk er endret
    if (targetStatus && targetStatus !== svc?.status) {
      await changeStatus(targetStatus);
    }

    const tasks = [];

    const storeLocation = produkt?.butikk || null;
    const storeName = storeLocation || getDisplayName();
    const greeting = "Hei,";

    // --- Felles input: det som står i tekstfeltet i modal ---
    let userText = (smsText || "").trim();

    const statusKey = (selectedStatus || targetStatus || "").trim();
    const templateActive = !!selectedTemplateId;
    const shortRef = toShortRefFront(svc?.serviceRef);

    const statusLine = shortRef
      ? `Din service (ref: ${shortRef}) er oppdatert til: ${statusKey}`
      : `Din service er oppdatert til: ${statusKey}`;

    const linkLine = customerLink
      ? `Følg servicen her:\n${customerLink}`
      : "Vi gir beskjed når vi har mer info.";

    const closingLine = `Mvh ${storeName}`;

    let finalSmsMessage = "";

    const alreadyHasStatusLine =
      userText.toLowerCase().includes("er oppdatert til:");

    if (statusKey === "avsluttet") {
      const parts = [userText || ""];
      if (includePortalLink && customerLink) {
        parts.push("");
        parts.push(`Se betalingsinformasjon: ${customerLink}`);
      }
      finalSmsMessage = parts.join("\n").trim();
    } else if (templateActive) {
      if (!userText) {
        finalSmsMessage = "";
      } else if (alreadyHasStatusLine) {
        const lines = [userText];
        if (customerLink) { lines.push(""); lines.push(linkLine); }
        lines.push(""); lines.push(closingLine);
        finalSmsMessage = lines.join("\n").trim();
      } else {
        const lines = [greeting, statusLine, "", userText];
        if (customerLink) { lines.push(""); lines.push(linkLine); }
        lines.push(""); lines.push(closingLine);
        finalSmsMessage = lines.join("\n").trim();
      }
    } else {
      const lines = [greeting, statusLine];
      if (userText) { lines.push(""); lines.push(userText); }
      lines.push(""); lines.push(linkLine);
      lines.push(""); lines.push(closingLine);
      finalSmsMessage = lines.join("\n").trim();
    }

    // Epost-tekst: kun statusinfo og eventuell brukermelding — INGEN lenke (knappen håndterer det)
    const plainEmailLines = [];
    if (!templateActive || !alreadyHasStatusLine) plainEmailLines.push(statusLine);
    if (userText) plainEmailLines.push(userText);
    const plainEmailText = plainEmailLines.join("\n\n");

    const displayName = `${customer?.firstName || ""} ${customer?.lastName || ""}`.trim() || "kunde";


    // 3) Legg til SMS-sending hvis valgt
    if (sendOnStatusChange && finalSmsMessage) {
      if (!customerPhone) {
        setMsg("Kan ikke sende SMS: mangler telefonnummer på servicen.");
      } else {
        tasks.push(
          sendSms({
            phone: customerPhone,   // 👈 snapshot-telefon
            name: "kunde",      // 👈 snapshot-navn
            statusLabel: null,
            message: finalSmsMessage,
            customerId: svc?.customerId,
            serviceRef: svc?.serviceRef,
          })
        );
      }
    }

    if (sendEmailOnStatusChange && plainEmailText) {
      const toEmail = customerEmail || customer?.email || "";

      if (!toEmail) {
        setMsg("Kan ikke sende e-post: mangler e-postadresse.");
      } else {
        tasks.push(
          sendStatusEmailWrapped({
            to: toEmail,
            customerName: displayName,
            status: targetStatus,
            serviceRef: svc?.serviceRef,
            customerLink,
            plainStatusText: plainEmailText,
            storeLocation,
            emailKind: "status",
          })
        );
      }
    }



    if (!tasks.length) return;

    setNotifyBusy(true);
    setSmsBusy(true);           // 👈 NY LINJE
try {
  await Promise.all(tasks);
  setMsg("Varsel sendt.");
  setStatusModalOpen(false);

  if (statusKey === "avsluttet" && includePortalLink && vippsRequested && svc?._id) {
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vipps: { linkSent: true } }),
      });
      if (r.ok) {
        const updated = await r.json();
        setSvc((prev) => (prev ? { ...prev, vipps: updated.vipps } : prev));
      }
    } catch (e) {
      console.error("Kunne ikke registrere at Vipps-lenken ble sendt:", e);
    }
  }

  const finalStatus = (selectedStatus || targetStatus || "")
    .toString()
    .trim()
    .toLowerCase();

  if (finalStatus === "avsluttet") {
    setTimeout(() => setInvoiceOpen(true), 50);
  }
} catch (e) {
  console.error("notifyStatusChange failed:", e);
  setMsg(e.message || "Feil ved sending av statusvarsel.");
} finally {
  setNotifyBusy(false);
  setSmsBusy(false);
}

  }






  // B) Send PRISTILBUD-LENKE (kundevisning-boksen)
async function notifyQuoteLink() {
  if (!customerLink) {
    setMsg("Ingen kundelenke. Opprett lenken først.");
    return;
  }

  // 🔹 Sett status til "pristilbud" når vi aktiverer pristilbud
  if (svc?.status !== "pristilbud") {
    try {
      await changeStatus("pristilbud");
    } catch (e) {
      console.error("Kunne ikke sette status til 'pristilbud':", e);
    }
  }

  const tasks = [];

  const displayName =
    customerName ||
    `${customer?.firstName || ""} ${customer?.lastName || ""}`.trim() ||
    "kunde";

  const storeLocation = produkt?.butikk || null;
  const storeName = storeLocation || getDisplayName();
  const shortRef = toShortRefFront(svc?.serviceRef);
  const quoteGreeting = "Hei,";

  const smsBody = [
    quoteGreeting,
    `Pristilbud for din service${shortRef ? ` (ref: ${shortRef})` : ""} er nå klart.`,
    "Vi har også sendt pristilbudet som PDF på e-post dersom den er registrert.",
    "",
    `Se detaljer og godkjenn her:\n${customerLink}`,
    "",
    `Mvh ${storeName}`,
  ].join("\n");

  // SMS til kunde hvis avhuket
  if (sendOnStatusChange) {
    if (!customerPhone) {
      setMsg("Kan ikke sende SMS: mangler telefonnummer på servicen.");
    } else {
      tasks.push(
        sendSms({
          phone: customerPhone,
          name: "kunde",
          statusLabel: null,
          message: smsBody,
          customerId: svc?.customerId,
          serviceRef: svc?.serviceRef,
        })
      );
    }
  }

  // ✅ ALLTID send kopi til verksted, uavhengig av kundens e-post og checkbox
  const toEmail = sendEmailOnStatusChange
    ? (customerEmail || customer?.email || "")
    : "";

  tasks.push(
    fetch(`${API_BASE_URL}/workshop-services/${svc._id}/send-quote-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        to: toEmail || null,
        customerName: displayName || "",
        sendCopyToWorkshop: true,
      }),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data?.message || "Kunne ikke sende e-postkopi");
      }
      return data;
    })
  );

  setNotifyBusy(true);
  setSmsBusy(true);

  try {
    await Promise.all(tasks);
    setMsg("Pristilbud aktivert og kopi sendt til verksted.");
    await enableQuoteAfterSms({ resetApproval: true });
  } catch (e) {
    console.error("notifyQuoteLink failed:", e);
    setMsg(e.message || "Feil ved sending av pristilbud.");
  } finally {
    setNotifyBusy(false);
    setSmsBusy(false);
  }
}



  // Alle tilgjengelige statuser i ønsket rekkefølge
  const STATUS_OPTIONS = [
    "mottatt",
    "aktiv",
    "venter på deler",
    "pristilbud",
    "venter på godkjenning",
    "godkjent",
    "avsluttet",
    "kansellert",
  ];

  const changeStatus = async (status) => {
    if (!svc?._id) return;
    setUpdating(true);
    setMsg("");

    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${svc._id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      if (!r.ok) throw new Error("Kunne ikke oppdatere status");
      const updated = await r.json();
      setSvc(updated);

      // Ikke send SMS/e-post her. Kun statusoppdatering.
      setMsg(`Status oppdatert til '${status}'.`);
    } catch (e) {
      console.error(e);
      setMsg("Feil ved oppdatering av status.");
    } finally {
      setUpdating(false);
    }
  };

  async function resetQuoteApprovalOnRotate(currentSvc, setSvcFn) {
    if (!currentSvc?._id) return;

    const newQuote = {
      enabled: false, // Pristilbud skal være AV før du sender lenke igjen
      approved: null, // Nullstill all beslutning
    };

    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${currentSvc._id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ quote: newQuote }),
        }
      );

      if (r.ok) {
        const updated = await r.json();
        setSvcFn(updated);
        return;
      }
    } catch (e) {
      console.error("[resetQuoteApprovalOnRotate] PATCH feilet:", e);
    }

    // Fallback lokal
    setSvcFn((prev) =>
      prev
        ? {
          ...prev,
          quote: newQuote, // nullstill fullstendig
        }
        : prev
    );
  }

  async function createOrRotateCustomerLink({ rotate = false } = {}) {
    if (!svc?._id) return;
    setLinkBusy(true);
    setMsg("");

    const toUrl = (data) => {
      const token =
        data?.token || data?.portal?.token || data?.data?.token || null;

      const directUrl =
        data?.url || data?.portal?.url || data?.data?.url || null;

      if (directUrl) return directUrl;
      if (token) return `https://status.roykensykkelservice.no/svc/${token}`;
      return null;
    };

    const attempts = [
      {
        method: "POST",
        path: `/workshop-services/${svc._id}/portal-link${rotate ? "?rotate=1" : ""
          }`,
      },
      {
        method: "POST",
        path: `/workshop-services/${svc._id}/portal${rotate ? "?rotate=1" : ""
          }`,
      },
      {
        method: "PATCH",
        path: `/workshop-services/${svc._id}`,
        body: { portal: { enabled: true, rotate: !!rotate } },
      },
    ];

    try {
      let lastErr = null;
      for (const a of attempts) {
        try {
          const r = await fetch(`${API_BASE_URL}${a.path}`, {
            method: a.method,
            headers: a.body
              ? { "Content-Type": "application/json" }
              : undefined,
            body: a.body ? JSON.stringify(a.body) : undefined,
          });

          if (!r.ok) {
            if (r.status === 404) continue;
            lastErr = new Error(`HTTP ${r.status}`);
            continue;
          }

          const data = await r.json().catch(() => ({}));
          const url = toUrl(data);
          if (!url) {
            lastErr = new Error("Respons mangler både url og token");
            continue;
          }

          setCustomerLink(url);
          setMsg(rotate ? "Ny kundelenke generert." : "Kundelenke laget.");

          // 🔁 Når vi BYTTER lenke:
          //  1) Nullstill pristilbud (enabled=false, approved=null)
          //  2) Sett status tilbake til "aktiv"
          if (rotate) {
            await resetQuoteApprovalOnRotate(svc, setSvc);

            if (svc?.status !== "aktiv") {
              try {
                await changeStatus("aktiv");
              } catch (e) {
                console.error(
                  "Kunne ikke sette status til 'aktiv' etter rotate:",
                  e
                );
              }
            }
          }

          return; // suksess
        } catch (e) {
          lastErr = e;
        }
      }

      console.error("Klarte ikke å generere kundelenke. Siste feil:", lastErr);
      setMsg("Feil: Fant ikke riktig endepunkt for kundelenke (404).");
    } finally {
      setLinkBusy(false);
    }
  }

  async function copyCustomerLink() {
    if (!customerLink) return;
    try {
      await navigator.clipboard.writeText(customerLink);
      setMsg("Lenke kopiert.");
    } catch {
      setMsg("Kunne ikke kopiere lenke.");
    }
  }

  async function saveVippsRequested(checked) {
    if (!svc?._id) return;
    setVippsRequested(checked);
    setVippsBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vipps: { requested: checked } }),
      });
      if (r.ok) {
        const updated = await r.json();
        setSvc((prev) => (prev ? { ...prev, vipps: updated.vipps } : prev));
      }
    } catch (e) {
      console.error("Kunne ikke lagre Vipps-valg:", e);
    } finally {
      setVippsBusy(false);
    }
  }

  useEffect(() => {
    setFlatServices([]);
  }, [productServiceSlug, svc?._id]);

  // ---------- Hygraph (tjenester) ----------
  const ensureHygraph = async () => {
    if (hygraphLoading || flatServices.length) return;

    setHygraphLoading(true);
    try {
      const grupper = await getTjenester(productServiceSlug);
      const flat = [];

      (grupper || []).forEach((g) => {
        const list = Array.isArray(g.service) ? g.service : [];
        list.forEach((s) => {
          flat.push({
            groupId: g.id,
            serviceId: s.id,
            groupSlug: g.slug,
            groupType: g.tjenestetype,
            slug: s.slug,
            navn: renameTjeneste(s.tjeneste, s.slug),
            pris: s.pris ?? 0,
            tidsbruk: s.tidsbruk ?? 0,
            servicetyper: Array.isArray(s.servicetype) ? s.servicetype : [],
            foranbak: !!s.foranbak,
            kategori: s.kategori || null,
            gratisarbeid: !!s.gratisarbeid,
          });
        });
      });

      setFlatServices(flat);
    } catch (e) {
      console.error("[WD.ensureHygraph] Uventet feil:", e);
      setFlatServices([]);
    } finally {
      setHygraphLoading(false);
    }
  };

  const filteredServices = useMemo(() => {
    const q = serviceQuery.trim().toLowerCase();

    let rows = flatServices;

    if (productServiceSlug) {
      rows = rows.filter(
        (s) => String(s.groupSlug || "").trim().toLowerCase() === productServiceSlug
      );
    }

    if (!q) return rows;

    return rows.filter(
      (s) =>
        (s.navn || "").toLowerCase().includes(q) ||
        (s.slug || "").toLowerCase().includes(q) ||
        (s.groupType || "").toLowerCase().includes(q) ||
        (s.kategori || "").toLowerCase().includes(q)
    );
  }, [flatServices, serviceQuery, productServiceSlug]);

  useEffect(() => {
    console.log("productServiceSlug:", productServiceSlug);
    console.log("svc.produkt:", svc?.produkt);
  }, [productServiceSlug, svc?.produkt]);

  // Last Hygraph-tjenester tidlig slik at badge-visning fungerer for alle linjer
  useEffect(() => { ensureHygraph(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Skjult/åpen for oppsummeringsboksen
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Har denne servicen en faktisk sjekkliste å vise?
  const hasChecklist = useMemo(
    () => hasChecklistEligible && (checklist.items?.length || 0) > 0,
    [hasChecklistEligible, checklist.items]
  );

  const checklistSummary = checklist.summary;

  // Er sjekklisten fullført (alle punkter behandlet)?
  const checklistComplete =
    hasChecklist &&
    checklist.unprocessedCount === 0 &&
    !checklist.loadingItems &&
    !checklist.loadingSaved;

  // ---------- Deler ----------
  // Henter fra den nye produktkatalogen (Produkt/Variant - samme som nettbutikken
  // og varelagerimporten), ikke lenger den gamle CSV-importerte Part-listen
  // (/parts). /produkter/deler speiler bevisst Part sin flate form (product,
  // brand, ean, price) slik at resten av del-plukkeren under er uendret.
  // Katalogen er for stor til å laste i sin helhet - søket styrer henting mot
  // serveren (debounced), i stedet for å filtrere en lokalt lastet liste.
  const partsSearchSeq = useRef(0);

  async function searchParts(query) {
    const q = query.trim();
    if (!q) {
      setParts([]);
      setPartsLoading(false);
      return [];
    }
    const seq = ++partsSearchSeq.current;
    setPartsLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/produkter/deler?q=${encodeURIComponent(q)}`);
      const data = r.ok ? await r.json() : [];
      const result = Array.isArray(data) ? data : [];
      if (seq === partsSearchSeq.current) setParts(result);
      return result;
    } catch (e) {
      console.error("Feil ved henting av deler:", e);
      return [];
    } finally {
      if (seq === partsSearchSeq.current) setPartsLoading(false);
    }
  }

  const PARTS_MIN_QUERY_LENGTH = 3;

  useEffect(() => {
    if (!partsModalOpen || partsTab !== "list") return;
    const q = partsQuery.trim();
    if (q.length < PARTS_MIN_QUERY_LENGTH) {
      setParts([]);
      setPartsLoading(false);
      return;
    }
    const timer = setTimeout(() => searchParts(q), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partsQuery, partsModalOpen, partsTab]);

  const filteredParts = parts;

  async function addPartFromCatalog(part, qtyOverride) {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;

    setPartsAddBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${wsId}/parts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // ref (kjekt for historikk / kobling)
          partId: part._id,

          // snapshot-felter som UI allerede forventer
          product: part.product || part.name || "",
          brand: part.brand || "",
          ean: part.ean || "",
          price: Number(part.price) || 0,
          // Innkjøpspris eks. mva for dekningsbidraget; 0 i katalogen = ikke lagt inn.
          kostpris: Number(part.kostpris) > 0 ? Number(part.kostpris) : null,

          // linjefelter
          qty: Number(qtyOverride) || 1,
          discount: 0,
          discountPercent: 0,
          isWarranty: false,
        }),
      });

      const updated = r.ok ? await r.json() : null;
      if (!r.ok) throw new Error("Kunne ikke legge til del");

      setSvc(updated);
      setMsg(`La til del «${part.product}».`);
      setPartsModalOpen(false);
      setPartsQuery("");
    } catch (e) {
      console.error(e);
      setMsg("Feil ved lagring av del.");
    } finally {
      setPartsAddBusy(false);
    }
  }

  async function addManualPart() {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;

    const price = Number(pPrice) || 0;
    const qty = Math.max(1, Number(pQty) || 1);
    const subtotal = price * qty;

    let discountKr = Math.max(0, Number(pDiscount) || 0);  // 👈 bytt til let
    let discountPercent = Math.max(0, Number(pDiscountPercent) || 0);

    if (!discountPercent && subtotal > 0 && discountKr > 0) {
      discountPercent = Math.round((discountKr / subtotal) * 1000) / 10;
    } else if (discountPercent && subtotal > 0 && !discountKr) {
      discountKr = Math.round((subtotal * discountPercent) / 100);
    }


    const reklamasjon = buildReklamasjonPatch();
    const body = {
      ean: pEan || "",
      brand: pBrand || "",
      product: (pName || "").trim() || "Del",
      price,
      kostpris: pKostpris.trim() === "" ? null : Number(pKostpris.replace(",", ".")),
      qty,
      discount: discountKr,
      discountPercent,
      isWarranty: false,
      ...(reklamasjon ? { reklamasjon } : {}),
    };

    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${wsId}/parts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Kunne ikke legge til del");
      setSvc(await r.json());
      setMsg(`La til del «${body.product}».`);

      // Reset felter
      setPName("");
      setPBrand("");
      setPEan("");
      setPPrice("");
      setPKostpris("");
      setPQty("1");
      setPDiscount("0");
      setPDiscountPercent("0");
      setPReklamasjonExpanded(false);
      setPReklamasjonKjopsdato("");
      setPReklamasjonRammenr("");
      setPReklamasjonTidsforbruk("");
      setPReklamasjonArsak("");
      setPReklamasjonAnsvar("");
      setPReklamasjonButikk("");
      setPReklamasjonImportor("");
      setPReklamasjonMerke("");
      setPReklamasjonModell("");
      setPReklamasjonSerienr("");
      setPartsModalOpen(false);
    } catch (e) {
      console.error(e);
      setMsg("Feil ved lagring av del.");
    }
  }


  async function updatePartField(index, patch) {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;
    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${wsId}/parts/${index}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        }
      );
      const updated = r.ok ? await r.json() : null;
      if (!r.ok) throw new Error("Kunne ikke oppdatere del");
      setSvc(updated);
    } catch (e) {
      console.error(e);
      setMsg("Feil ved oppdatering av del.");
    }
  }

  async function updatePartQty(index, nextQty, line) {
    const qty = Math.max(1, Number(nextQty) || 1);
    const price = Number(line?.price) || 0;
    const subtotal = price * qty;

    const mode = inferDiscountMode(index, line);
    const pair = recomputeDiscountPair({
      mode,
      subtotal,
      discount: line?.discount,
      discountPercent: line?.discountPercent,
    });

    // 👇 viktig: når qty endres, vil vi “slippe” draft så feltene viser ny beregning
    clearPartDraft(index);

    await updatePartField(index, {
      qty,
      ...pair, // discount + discountPercent
    });
  }

  function startEditPart(index, p) {
    setEditingPartIndex(index);
    setPName(p.product || "");
    setPBrand(p.brand || "");
    setPEan(p.ean || "");
    setPPrice(p.price != null ? String(p.price) : "");
    setPKostpris(p.kostpris != null ? String(p.kostpris) : "");
    setPQty(String(Math.max(1, Number(p.qty) || 1)));
    setPDiscount(String(Math.max(0, Number(p.discount) || 0)));
    setPDiscountPercent(String(Math.max(0, Number(p.discountPercent) || 0)));
    const rek = p.reklamasjon || {};
    const hasRek = !!(rek.kjopsdato || rek.rammenr || rek.tidsforbruk || rek.arsak || rek.ansvar || rek.importor || rek.merke || rek.modell || rek.serienr);
    setPReklamasjonExpanded(hasRek);
    setPReklamasjonKjopsdato(rek.kjopsdato || "");
    setPReklamasjonRammenr(rek.rammenr || "");
    setPReklamasjonTidsforbruk(rek.tidsforbruk != null ? String(rek.tidsforbruk) : "");
    setPReklamasjonArsak(rek.arsak || "");
    setPReklamasjonAnsvar(rek.ansvar || "");
    setPReklamasjonButikk(rek.butikk || Cookies.get("selectedStore") || "");
    setPReklamasjonImportor(rek.importor || "");
    setPReklamasjonMerke(rek.merke || svc?.produkt?.produktmerke || "");
    setPReklamasjonModell(rek.modell || svc?.produkt?.farge || "");
    setPReklamasjonSerienr(rek.serienr || "");
    setPartsTab("manual");
    setPartsModalOpen(true);
  }

  function closePartsModal() {
    setPartsModalOpen(false);
    setEditingPartIndex(null);
    setPReklamasjonExpanded(false);
    setPReklamasjonKjopsdato("");
    setPReklamasjonRammenr("");
    setPReklamasjonTidsforbruk("");
    setPReklamasjonArsak("");
    setPReklamasjonAnsvar("");
    setPReklamasjonButikk("");
    setPReklamasjonImportor("");
    setPReklamasjonMerke("");
    setPReklamasjonModell("");
    setPReklamasjonSerienr("");
  }

  function buildReklamasjonPatch() {
    if (!pReklamasjonExpanded) return null;
    const rek = {};
    if (pReklamasjonKjopsdato) rek.kjopsdato = pReklamasjonKjopsdato;
    if (pReklamasjonRammenr.trim()) rek.rammenr = pReklamasjonRammenr.trim();
    if (pReklamasjonTidsforbruk !== "") rek.tidsforbruk = Number(pReklamasjonTidsforbruk) || 0;
    if (pReklamasjonArsak.trim()) rek.arsak = pReklamasjonArsak.trim();
    if (pReklamasjonAnsvar) rek.ansvar = pReklamasjonAnsvar;
    if (pReklamasjonButikk.trim()) rek.butikk = pReklamasjonButikk.trim();
    if (pReklamasjonMerke.trim()) rek.merke = pReklamasjonMerke.trim();
    if (pReklamasjonModell.trim()) rek.modell = pReklamasjonModell.trim();
    if (pReklamasjonSerienr.trim()) rek.serienr = pReklamasjonSerienr.trim();
    if (pReklamasjonImportor.trim()) rek.importor = pReklamasjonImportor.trim();
    return Object.keys(rek).length ? rek : null;
  }

  async function saveEditPart() {
    if (editingPartIndex === null) return;
    const price = Number(pPrice) || 0;
    const qty = Math.max(1, Number(pQty) || 1);
    const subtotal = price * qty;
    let discountKr = Math.max(0, Number(pDiscount) || 0);
    let discountPercent = Math.max(0, Number(pDiscountPercent) || 0);
    if (!discountPercent && subtotal > 0 && discountKr > 0)
      discountPercent = Math.round((discountKr / subtotal) * 1000) / 10;
    else if (discountPercent && subtotal > 0 && !discountKr)
      discountKr = Math.round((subtotal * discountPercent) / 100);
    const reklamasjon = buildReklamasjonPatch();
    const patch = {
      ean: pEan || "",
      brand: pBrand || "",
      product: (pName || "").trim() || "Del",
      price,
      kostpris: pKostpris.trim() === "" ? null : Number(pKostpris.replace(",", ".")),
      qty,
      discount: discountKr,
      discountPercent,
      reklamasjon: reklamasjon || null,
    };
    await updatePartField(editingPartIndex, patch);
    setMsg(`Oppdaterte del «${patch.product}».`);
    setPName(""); setPBrand(""); setPEan(""); setPPrice(""); setPKostpris(""); setPQty("1"); setPDiscount("0"); setPDiscountPercent("0");
    closePartsModal();
  }

  async function removePart(index) {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;
    setUpdating(true);
    setMsg("");
    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${wsId}/parts/${index}`,
        { method: "DELETE" }
      );
      const payload = r.ok ? await r.json() : null;
      if (!r.ok)
        throw new Error(payload?.message || "Kunne ikke slette del.");
      setSvc(payload);
      setMsg("Del slettet.");
    } catch (e) {
      console.error(e);
      setMsg("Feil ved sletting av del.");
    } finally {
      setUpdating(false);
    }
  }

  // ---------- Tjeneste: legg til fra liste / manuell ----------
  const addHygraphService = async (s) => {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;
    if (!Number(s.tidsbruk)) {
      setServiceModalError("Tjenesten mangler tid i arbeid og kan ikke legges til.");
      return;
    }
    setServiceModalError("");
    const body = {
      navn: s.navn,
      slug: s.slug,
      pris: s.pris,
      tidsbruk: s.tidsbruk,
      servicetyper: Array.isArray(s.servicetyper) ? s.servicetyper : [],
      foranBak: [],
      kommentar: "",
      reklamasjon: newReklamasjon,
      utfortAvRS: newUtfortAvRS,
      gratisarbeid: newGratisarbeid || !!s.gratisarbeid,
    };
    setHyAddBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${wsId}/lines`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const updated = r.ok ? await r.json() : null;
      if (!r.ok) throw new Error("Kunne ikke legge til tjeneste");
      setSvc(updated);
      setMsg(`La til «${s.navn}».`);
      setServiceModalOpen(false);
      setServiceQuery("");
    } catch (e) {
      console.error(e);
      setMsg("Feil ved lagring av tjeneste.");
    } finally {
      setHyAddBusy(false);
    }
  };

  async function saveQuoteDesc() {
    if (!svc?._id) return;
    setQuoteDescBusy(true);
    setMsg("");
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quoteDescription: (quoteDesc || "").trim() }),
      });
      if (!r.ok) throw new Error("Kunne ikke lagre tekst til pristilbud");
      const updated = await r.json();
      setSvc(updated);
      setQuoteDesc(updated?.quoteDescription || "");
      setMsg("Tekst til pristilbud lagret.");
    } catch (e) {
      console.error(e);
      setMsg("Feil ved lagring av tekst til pristilbud.");
    } finally {
      setQuoteDescBusy(false);
    }
  }


  async function saveWorkDesc() {
    if (!svc?._id) return;
    setDescBusy(true);
    setMsg("");
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ arbeidsbeskrivelse: (workDesc || "").trim() }),
      });
      if (!r.ok) throw new Error("Kunne ikke lagre arbeidsbeskrivelse");
      const updated = await r.json();
      setSvc(updated);
      setMsg("Arbeidsbeskrivelse lagret.");
    } catch (e) {
      console.error(e);
      setMsg("Feil ved lagring av arbeidsbeskrivelse.");
    } finally {
      setDescBusy(false);
    }
  }

  async function addManualService() {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;
    if (!Number(newMinutes) || Number(newMinutes) <= 0) {
      setServiceModalError("Tid i arbeid er påkrevd.");
      return;
    }
    setServiceModalError("");
    const body = {
      navn: (newName || "").trim() || "Tilleggstjeneste",
      pris: Number(newPrice) || 0,
      tidsbruk: Number(newMinutes) || 0,
      kommentar: newComment || "",
      slug: "",
      servicetyper: [],
      foranBak: [],
      reklamasjon: newReklamasjon,
      gratisarbeid: newGratisarbeid,
      utfortAvRS: newUtfortAvRS,
    };
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${wsId}/lines`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Kunne ikke legge til linje");
      setSvc(await r.json());
      setMsg(`La til «${body.navn}».`);
      setNewName("");
      setNewPrice("");
      setNewMinutes("");
      setNewComment("");
      setNewReklamasjon(false);
      setNewGratisarbeid(false);
      setNewUtfortAvRS(true);
      setServiceModalOpen(false);
    } catch (e) {
      console.error(e);
      setMsg("Feil ved lagring av linje.");
    }
  }



  async function removeLine(_line, index) {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;
    setUpdating(true);
    setMsg("");
    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${wsId}/lines/${index}`,
        { method: "DELETE" }
      );
      const payload = r.ok ? await r.json() : null;
      if (!r.ok)
        throw new Error(payload?.message || "Kunne ikke slette tjenestelinje.");
      setSvc(payload);
      setMsg("Tjenestelinje slettet.");
    } catch (e) {
      console.error("[removeLine] Uventet feil", e);
      setMsg("Feil ved sletting av tjenestelinje.");
    } finally {
      setUpdating(false);
    }
  }

  function startEditLine(index, line) {
    setEditingLineIndex(index);
    setLineEditNavn(line?.navn || "");
    setLineEditKommentar(line?.kommentar || "");
    setLineEditTidsbruk(line?.tidsbruk != null ? String(line.tidsbruk) : "");
    setLineEditPris(line?.pris != null ? String(line.pris) : "");
    setLineEditReklamasjon(!!line?.reklamasjon);
    setLineEditGratisarbeid(!!line?.gratisarbeid);
    setLineEditError("");
  }

  function closeLineEditModal() {
    setEditingLineIndex(null);
    setLineEditNavn("");
    setLineEditKommentar("");
    setLineEditTidsbruk("");
    setLineEditPris("");
    setLineEditReklamasjon(false);
    setLineEditGratisarbeid(false);
    setLineEditError("");
  }

  async function saveEditLine() {
    const wsId = svc?._id || svc?.id;
    if (!wsId || editingLineIndex === null) return;
    if (!lineEditNavn.trim()) {
      setLineEditError("Navn er påkrevd.");
      return;
    }
    setLineEditBusy(true);
    setLineEditError("");
    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${wsId}/lines/${editingLineIndex}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            navn: lineEditNavn.trim(),
            kommentar: lineEditKommentar,
            tidsbruk: Number(lineEditTidsbruk) || 0,
            pris: Number(lineEditPris) || 0,
            reklamasjon: lineEditReklamasjon,
            gratisarbeid: lineEditGratisarbeid,
          }),
        }
      );
      const payload = r.ok ? await r.json() : null;
      if (!r.ok)
        throw new Error(payload?.message || "Kunne ikke oppdatere tjenestelinje.");
      setSvc(payload);
      setMsg("Tjenestelinje oppdatert.");
      closeLineEditModal();
    } catch (e) {
      console.error("[saveEditLine] Uventet feil", e);
      setLineEditError(e.message || "Feil ved oppdatering av tjenestelinje.");
    } finally {
      setLineEditBusy(false);
    }
  }

  // ---------- Frakt ----------
  function openFraktForm() {
    setEditingFraktIndex(null);
    setFraktBeskrivelse("Frakt");
    setFraktPris("");
    setFraktKommentar("");
    setFraktError("");
    setFraktFormOpen(true);
  }

  function startEditFrakt(index, line) {
    setEditingFraktIndex(index);
    setFraktBeskrivelse(line?.beskrivelse || "Frakt");
    setFraktPris(line?.pris != null ? String(line.pris) : "");
    setFraktKommentar(line?.kommentar || "");
    setFraktError("");
    setFraktFormOpen(true);
  }

  function closeFraktForm() {
    setFraktFormOpen(false);
    setEditingFraktIndex(null);
    setFraktBeskrivelse("Frakt");
    setFraktPris("");
    setFraktKommentar("");
    setFraktError("");
  }

  async function saveFraktLine() {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;
    if (!(Number(fraktPris) > 0)) {
      setFraktError("Pris må være et positivt beløp.");
      return;
    }
    setFraktBusy(true);
    setFraktError("");
    const body = {
      beskrivelse: fraktBeskrivelse.trim() || "Frakt",
      pris: Number(fraktPris) || 0,
      kommentar: fraktKommentar || "",
    };
    try {
      const isEdit = editingFraktIndex !== null;
      const url = isEdit
        ? `${API_BASE_URL}/workshop-services/${wsId}/frakt/${editingFraktIndex}`
        : `${API_BASE_URL}/workshop-services/${wsId}/frakt`;
      const r = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = r.ok ? await r.json() : null;
      if (!r.ok) throw new Error(payload?.message || "Kunne ikke lagre frakt.");
      setSvc(payload);
      setMsg(isEdit ? "Frakt oppdatert." : `La til frakt «${body.beskrivelse}».`);
      closeFraktForm();
    } catch (e) {
      console.error("[saveFraktLine] Uventet feil", e);
      setFraktError(e.message || "Feil ved lagring av frakt.");
    } finally {
      setFraktBusy(false);
    }
  }

  async function removeFraktLine(index) {
    const wsId = svc?._id || svc?.id;
    if (!wsId) return;
    setUpdating(true);
    setMsg("");
    try {
      const r = await fetch(
        `${API_BASE_URL}/workshop-services/${wsId}/frakt/${index}`,
        { method: "DELETE" }
      );
      const payload = r.ok ? await r.json() : null;
      if (!r.ok) throw new Error(payload?.message || "Kunne ikke slette frakt.");
      setSvc(payload);
      setMsg("Frakt slettet.");
    } catch (e) {
      console.error("[removeFraktLine] Uventet feil", e);
      setMsg("Feil ved sletting av frakt.");
    } finally {
      setUpdating(false);
    }
  }

  // ---------- Navigasjon ----------
  const openCustomer = () => {
    if (svc?.customerId) navigate(`/customer-details/${svc.customerId}`);
  };

  if (!svc) return <div className="p-6">Laster…</div>;



  function finallyOpenReadyModal() {
    setStatusModalMode("ready");
    setIncludePortalLink(false);
    setVippsRequested(!!svc?.vipps?.requested);

    setSendOnStatusChange(!!(customerPhone || customer?.phoneNumber));
    setSendEmailOnStatusChange(!!(customerEmail || customer?.email));

    setSelectedTemplateId("");
    setHasAutoSelectedTemplate(false);
    setAutoTemplateBlocked(true);

    if (svc?._id) {
      const key = `wd:smsBlockAuto:${svc._id}`;
      window.sessionStorage.setItem(key, "1");
    }

    setSelectedStatus("avsluttet");

    const serviceId = svc?.serviceRef ? `#${svc.serviceRef}` : "";
    const butikk = produkt?.butikk || getDisplayName();

    const typeRaw =
      (produkt?.type || produkt?.produkttype || serviceCategory || "").toString();
    const typeLower = typeRaw.trim().toLowerCase();

    const klarFor = fraktLines.length > 0 ? "sending" : "henting";

    let text = "";
    if (typeLower.includes("sykkel")) {
      text = `Hei, nå er sykkelen din klar for ${klarFor}. Ditt servicenummer er ${serviceId}. Mvh ${butikk}`;
    } else {
      text = `Hei, nå er servicen din ferdig for ${klarFor}. Ditt servicenummer er ${serviceId}. Mvh ${butikk}`;
    }

    setSmsText(text);
    setStatusModalOpen(true);
  }

  function proceedOpenReadyModal() {
    if (produkt?.prisoverslag && !quoteEnabled) {
      setShowPristilbudWarning(true);
      return;
    }
    finallyOpenReadyModal();
  }

  function openReadyModal() {
    // Elsykkel uten kilometerstand: advarsel først (kan fylles inn der, eller fortsette uten).
    if (erElService && !harOdo(svc)) {
      setShowOdoAdvarsel(true);
      return;
    }
    openReadyModalEtterOdo();
  }

  function openReadyModalEtterOdo() {
    if (!expectedReadyDate) {
      setShowFerdigDatoWarning(true);
      return;
    }
    proceedOpenReadyModal();
  }

  // Ny ODO lagret: oppdater servicen og loggen på det koblede sykkelkortet.
  function odoLagret({ odo, odoLogg }) {
    setSvc((prev) => (prev ? { ...prev, odo } : prev));
    if (odoLogg) setLinkedBike((b) => (b ? { ...b, odoLogg } : b));
  }

  async function handleSetTodayAndContinue() {
    const today = new Date().toISOString().slice(0, 10);
    setShowFerdigDatoWarning(false);
    await saveExpectedReadyDateOnService(today);
    if (pendingAvsluttFromModal) {
      setPendingAvsluttFromModal(false);
      // status modal stays open – user can now confirm
      return;
    }
    proceedOpenReadyModal();
  }

  function handleProceedWithoutQuote() {
    setShowPristilbudWarning(false);
    finallyOpenReadyModal();
  }




  return (
    <div className="app-screen mx-auto w-full max-w-7xl pb-10">
      {/* Tilbake og sidetopp i det nye designet (vinnsel, 30.10). Tilbake går som før til forrige side. */}
      <div className="mb-4 space-y-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50"
        >
          <Ikon navn="tilbake" className="w-5 h-5" /> Tilbake
        </button>
        <Sidetopp
          ikon="skiftenokkel"
          farge="verksted"
          tittel={<>Service{svc.serviceRef ? <> <span className="font-mono">#{svc.serviceRef}</span></> : null}</>}
          undertittel={`Opprettet: ${fmtDT(svc.createdAt)}`}
        />
      </div>

      {showOdoAdvarsel && (
        <OdoAdvarsel
          svc={svc}
          sykkel={linkedBike}
          onLagret={odoLagret}
          onAvbryt={() => setShowOdoAdvarsel(false)}
          onFortsett={() => { setShowOdoAdvarsel(false); openReadyModalEtterOdo(); }}
        />
      )}

      {showFerdigDatoWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl leading-none mt-0.5">📅</span>
              <div>
                <div className="font-bold text-slate-900 text-base">Ferdigdato mangler</div>
                <div className="text-sm text-slate-600 mt-1">
                  Du må sette en ferdigdato før du kan avslutte servicen. Vil du bruke dagens dato?
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => { setShowFerdigDatoWarning(false); setPendingAvsluttFromModal(false); }}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm"
              >
                Avbryt
              </button>
              <button
                onClick={handleSetTodayAndContinue}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              >
                Velg dagens dato
              </button>
            </div>
          </div>
        </div>
      )}

      {showPristilbudReminderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl leading-none mt-0.5">💰</span>
              <div>
                <div className="font-bold text-slate-900 text-base">Pristilbud ønsket</div>
                <div className="text-sm text-slate-600 mt-1">
                  Kunden ønsker pristilbud før jobben utføres. Ikke start arbeidet før pristilbud er opprettet, sendt og godkjent.
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => setShowPristilbudReminderModal(false)}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm"
              >
                Lukk
              </button>
              <button
                onClick={() => { setShowPristilbudReminderModal(false); quote.show(); }}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              >
                Opprett pristilbud
              </button>
            </div>
          </div>
        </div>
      )}

      {showPristilbudWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl leading-none mt-0.5">💰</span>
              <div>
                <div className="font-bold text-slate-900 text-base">Pristilbud ikke sendt</div>
                <div className="text-sm text-slate-600 mt-1">
                  Kunden har bedt om pristilbud, men det er ikke aktivert. Vil du fortsette uten å sende pristilbud?
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => setShowPristilbudWarning(false)}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm"
              >
                Avbryt
              </button>
              <button
                onClick={handleProceedWithoutQuote}
                className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm"
              >
                Fortsett uansett
              </button>
            </div>
          </div>
        </div>
      )}

      {showImageVisibilityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl leading-none mt-0.5">🖼️</span>
              <div>
                <div className="font-bold text-slate-900 text-base">
                  {pendingImageFiles?.length || 0} bilde{(pendingImageFiles?.length || 0) !== 1 ? "r" : ""} valgt
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Skal bildet{(pendingImageFiles?.length || 0) !== 1 ? "ne" : ""} være synlig for kunden?
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => {
                  setShowImageVisibilityModal(false);
                  setPendingImageFiles(null);
                }}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  const files = pendingImageFiles;
                  setShowImageVisibilityModal(false);
                  setPendingImageFiles(null);
                  uploadWorkshopImages(files, false);
                }}
                className="px-4 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-sm"
              >
                Kun internt
              </button>
              <button
                onClick={() => {
                  const files = pendingImageFiles;
                  setShowImageVisibilityModal(false);
                  setPendingImageFiles(null);
                  uploadWorkshopImages(files, true);
                }}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              >
                Synlig for kunde
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocumentVisibilityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl leading-none mt-0.5">📄</span>
              <div>
                <div className="font-bold text-slate-900 text-base">
                  {pendingDocumentFiles?.length || 0} dokument{(pendingDocumentFiles?.length || 0) !== 1 ? "er" : ""} valgt
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Skal dokumentet{(pendingDocumentFiles?.length || 0) !== 1 ? "ne" : ""} være synlig for kunden?
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={() => {
                  setShowDocumentVisibilityModal(false);
                  setPendingDocumentFiles(null);
                }}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  const files = pendingDocumentFiles;
                  setShowDocumentVisibilityModal(false);
                  setPendingDocumentFiles(null);
                  uploadWorkshopDocuments(files, false);
                }}
                className="px-4 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-sm"
              >
                Kun internt
              </button>
              <button
                onClick={() => {
                  const files = pendingDocumentFiles;
                  setShowDocumentVisibilityModal(false);
                  setPendingDocumentFiles(null);
                  uploadWorkshopDocuments(files, true);
                }}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              >
                Synlig for kunde
              </button>
            </div>
          </div>
        </div>
      )}

      {produkt?.prisoverslag && svc?.status !== "Tilbud godkjent" && (
        <div className="mb-4 p-4 rounded-xl border-2 border-yellow-400 bg-yellow-50 flex items-start gap-3">
          <span className="text-2xl leading-none mt-0.5">💰</span>
          <div>
            <div className="font-bold text-yellow-900 text-base">Kunden ønsker pristilbud</div>
            <div className="text-sm text-yellow-800 mt-0.5">
              Ikke start arbeid uten å ha sendt pristilbud og fått godkjenning.
            </div>
          </div>
        </div>
      )}

      {!siblingsLoading && siblingServices.length > 0 && (
        <div className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="text-amber-800 font-medium">
              ⚠ Denne servicen har {siblingServices.length} annen
              {siblingServices.length > 1 ? "e aktive servicer" : " aktiv service"}
              {" "}på samme kunde.
            </div>

            <div className="flex flex-wrap gap-2">
              {siblingServices.map(s => (
                <button
                  key={s._id}
                  onClick={() => navigate(`/workshop/${s._id}`)}
                  className="text-sm px-3 py-1 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
                >
                  #{s.serviceRef || s._id.slice(-5)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}


      <SmsTilKunden telefon={customerPhone || customer?.phoneNumber} />

      {/* Kunde: felles banner med lenke til kundekortet (KundeBanner). «Vis detaljer» folder ut
          kundedetaljene som før. */}
      <KundeBanner
        className="mb-4"
        kundeId={svc?.customerId}
        navn={(kontakt?.navn ||
          `${serviceCustomer?.firstName || customer?.firstName || "—"} ${serviceCustomer?.lastName || customer?.lastName || ""}`).trim()}
        telefon={customerPhone || customer?.phoneNumber || "Ingen telefon registrert"}
        epost={customerEmail || customer?.email || "Ingen e-post registrert"}
      >
        {kontakt && !kontakt.fraKundekort && svc?.customerId && (
          <div className="text-xs text-amber-800 mt-1">
            Telefonen på servicen er ikke kundens nummer i registeret. Meldinger går til
            kontaktinfoen på servicen.
          </div>
        )}
        {!customerBoxExpanded && epostTreff.length > 0 && (
          <div className="text-xs text-amber-800 mt-1">
            Samme e-post står på {epostTreff.length === 1 ? "et annet kundekort" : `${epostTreff.length} andre kundekort`}. Se «Vis detaljer».
          </div>
        )}
        {kontakt?.egenEpostPaSaken && (
          <div className="text-xs text-slate-700 mt-1">
            Kunden har valgt en annen e-post for denne servicen enn den på kundekortet.
          </div>
        )}
        {(kontakt?.vedRegistrering?.telefon || kontakt?.vedRegistrering?.epost) && (
          <div className="text-xs text-slate-600 mt-1">
            Ved registrering:{" "}
            {[kontakt.vedRegistrering.telefon, kontakt.vedRegistrering.epost].filter(Boolean).join(", ")}
          </div>
        )}
        <button
          type="button"
          onClick={() => setCustomerBoxExpanded((v) => !v)}
          aria-expanded={customerBoxExpanded}
          className="rounded font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
        >
          {customerBoxExpanded ? "Skjul detaljer" : "Vis detaljer"}
        </button>
      </KundeBanner>

      {/* Mulig duplikat: samme e-post på et annet kundekort med et annet nummer.
          Hver linje har to valg: behold det andre kortets nummer, eller dette. */}
      {customerBoxExpanded && epostTreff.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 mb-4 text-sm text-amber-900" role="status">
          <p className="font-medium">Samme e-post står på andre kundekort med et annet nummer</p>
          {customer && (
            <p className="mt-1">
              Denne servicen er koblet til kundekortet{" "}
              <strong>{[customer.firstName, customer.lastName].filter(Boolean).join(" ")} · {customer.phoneNumber}</strong>
              {svc?.customerId && (
                <>
                  {" "}
                  (<Link to={`/customer-details/${svc.customerId}`} className="text-indigo-700 underline">åpne kortet</Link>)
                </>
              )}
              .
            </p>
          )}
          <ul className="mt-2 space-y-2">
            {epostTreff.map((k) => {
              const navn = [k.firstName, k.lastName].filter(Boolean).join(" ") || "Uten navn";
              return (
                <li key={k._id} className="rounded-lg border border-amber-200 bg-white/60 p-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="min-w-0 break-words font-medium">{navn} · {k.phoneNumber || "uten nummer"}</span>
                    <Link to={`/customer-details/${k._id}`} className="text-indigo-700 underline">
                      Åpne kortet<span className="sr-only"> til {navn}</span>
                    </Link>
                  </div>
                  {kanRetteTelefon && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-xs">Hører servicen til denne kunden?</span>
                      <button
                        type="button"
                        onClick={() => flyttTilKunde(k)}
                        disabled={!!flytter}
                        className="rounded-lg border border-indigo-300 bg-white px-2 py-0.5 text-xs font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                      >
                        {flytter === String(k._id) ? "Flytter…" : `Flytt servicen til ${k.phoneNumber || "dette kortet"}`}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs">
            «Flytt servicen» flytter bare denne servicen. Kundekortene endres ikke. Er det ulike personer
            (f.eks. familie som deler e-post), kan du se bort fra dette.
          </p>
          {rettTlfMsg && <p className="mt-1 text-xs" role="status">{rettTlfMsg}</p>}
        </div>
      )}

      {/* Full kundevisning — vises ved klikk */}
      {customerBoxExpanded && (
        <div
          className={`rounded-2xl border border-slate-200 bg-white p-3 mb-4 ${svc?.customerId ? "cursor-pointer hover:bg-slate-50" : ""
            }`}
          onClick={svc?.customerId ? openCustomer : undefined}
          role={svc?.customerId ? "button" : undefined}
          tabIndex={svc?.customerId ? 0 : -1}
          title={svc?.customerId ? "Åpne kundedetaljer" : undefined}
        >
          <div className="flex items-start justify-between">
            <h3 className="mb-2 text-base font-bold text-slate-900">Kundedetaljer</h3>
            {svc?.customerId && (
              <span className="text-indigo-700 text-sm underline">
                Åpne kundekort →
              </span>
            )}
          </div>

          <div className="space-y-4">
            {/* Kunde på servicen */}
            {serviceCustomer && (
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-600 mb-1">
                  Registrert på denne servicen
                </div>
                <p>
                  {serviceCustomer.firstName} {serviceCustomer.lastName}
                </p>
                <p>{serviceCustomer.phoneNumber}</p>
                <p>{serviceCustomer.email}</p>
                {serviceCustomer.prisoverslag && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-yellow-100 border border-yellow-400 text-yellow-900 font-semibold text-xs">
                    💰 Pristilbud ønsket
                  </span>
                )}
              </div>
            )}

            {/* Registrert kunde */}
            {customer && (
              <div className="pt-3 border-t">
                <div className="text-xs uppercase tracking-wide text-slate-600 mb-1">
                  Kundekortet
                </div>
                <p>
                  {customer.firstName} {customer.lastName}
                </p>
                <p>{customer.phoneNumber}</p>
                <p>{customer.email}</p>
              </div>
            )}

            {/* Hvor meldinger går (kontaktFor på serveren) */}
            {kontakt && (
              <p className="text-xs text-slate-700 pt-3 border-t">
                Meldinger går til {kontakt.fraKundekort ? "kundekortet" : "kontaktinfoen på servicen"}:{" "}
                {[kontakt.telefon, kontakt.epost].filter(Boolean).join(", ") || "ingen kontaktinfo"}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Rett feil telefonnummer på servicen (PATCH /workshop-services/:id/telefon).
          Utenfor den klikkbare kundeboksen. Bare åpne servicer uten kvittering. */}
      {customerBoxExpanded && kanRetteTelefon && (
        <div className="border rounded-lg p-3 mb-4 text-sm">
          {!rettTlfApen ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-slate-700">Feil telefonnummer på servicen?</span>
              <button
                type="button"
                onClick={() => { setRettTlf(serviceCustomer?.phoneNumber || ""); setRettTlfMsg(""); setRettTlfApen(true); }}
                className="px-3 py-1.5 rounded-lg border text-sm hover:bg-slate-50"
              >
                Rett telefonnummer
              </button>
            </div>
          ) : (
            <form onSubmit={lagreRettTelefon} className="flex flex-wrap items-end gap-2">
              <div>
                <label htmlFor="rett-telefon-service" className="block text-xs text-slate-700 mb-1">Riktig telefonnummer</label>
                <input
                  id="rett-telefon-service"
                  type="tel"
                  autoFocus
                  value={rettTlf}
                  onChange={(e) => setRettTlf(e.target.value)}
                  className="w-40 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                />
              </div>
              <button type="submit" disabled={rettTlfBusy} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
                {rettTlfBusy ? "Lagrer…" : "Lagre"}
              </button>
              <button type="button" onClick={() => setRettTlfApen(false)} className="rounded-lg border px-3 py-1.5 text-sm">Avbryt</button>
              <p className="basis-full text-xs text-slate-600">
                Servicen kobles til kundekortet for dette nummeret (det lages hvis det ikke finnes). Ingen kundekort endres.
              </p>
            </form>
          )}
          {rettTlfMsg && <p className="mt-1 text-xs text-slate-700" role="status">{rettTlfMsg}</p>}
        </div>
      )}



      {/* Produkt + Status/Sjekkliste */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {/* Venstre: Tittel + produktinfo */}
          <div className="md:col-span-2">
            <h3 className="mb-3 flex items-center gap-1.5 text-base font-bold text-slate-900">
              Produkt
              <button
                type="button"
                onClick={openCommentModal}
                className="shrink-0 text-slate-600 hover:text-indigo-700"
                title="Rediger produktinfo"
                aria-label="Rediger produktinfo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M17.414 2.586a2 2 0 010 2.828l-8.9 8.9a1 1 0 01-.39.242l-3.5 1.167a1 1 0 01-1.265-1.265l1.167-3.5a1 1 0 01.242-.39l8.9-8.9a2 2 0 012.828 0zm-9.61 9.196l7.486-7.486-1.414-1.414-7.486 7.486-.73 2.144 2.144-.73z" />
                </svg>
              </button>

              {svc?._id && (
                <button
                  type="button"
                  onClick={() => setQrModalOpen(true)}
                  className="shrink-0 text-slate-600 hover:text-indigo-700"
                  title="Vis QR-kode for å åpne servicen på mobil"
                  aria-label="Vis QR-kode for å åpne servicen på mobil"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zM3 11h6v6H3v-6zm2 2v2h2v-2H5zM11 3h6v6h-6V3zm2 2v2h2V5h-2zM11 11h2v2h-2v-2zM15 11h2v2h-2v-2zM11 15h2v2h-2v-2zM15 15h2v2h-2v-2z" />
                  </svg>
                </button>
              )}
            </h3>

            <p className="font-medium">
              {produkt.merke}
              {produkt.type ? ` – ${renameTjeneste(produkt.type)}` : ""}
              {produkt.farge ? ` (${produkt.farge})` : ""}
            </p>
            <div className="text-sm text-slate-600 mt-1 space-y-1">
              {produkt.butikk && <div>Butikk: {produkt.butikk}</div>}
              {produkt.leveringsDato && (
                <div>Leveringsdato: {fmtDate(produkt.leveringsDato)}</div>
              )}
              {produkt.forventetFerdigDato && (
                <div>Forventet ferdig: {fmtDate(produkt.forventetFerdigDato)}</div>
              )}
              {produkt.ferdigDatoValgfri && (
                <div className="pt-1">
                  <div>
                    Ønsket ferdig (lovet kunden):{" "}
                    <strong>{onsketFerdigTekst(produkt, fmtDate)}</strong>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <label htmlFor="onsket-ferdig-dato" className="sr-only">
                      Ønsket ferdigdato
                    </label>
                    <input
                      id="onsket-ferdig-dato"
                      type="date"
                      className="rounded-lg border px-2 py-1 text-sm"
                      value={onsketFerdigDatoInput}
                      onChange={(e) => setOnsketFerdigDatoInput(e.target.value)}
                    />
                    <button
                      type="button"
                      className="rounded-lg border px-2 py-1 text-sm hover:bg-slate-50 disabled:opacity-50"
                      disabled={
                        updating ||
                        onsketFerdigDatoInput ===
                          (produkt.onsketFerdigDato ? String(produkt.onsketFerdigDato).slice(0, 10) : "")
                      }
                      onClick={() => saveOnsketFerdigDato(onsketFerdigDatoInput)}
                    >
                      Lagre
                    </button>
                    {produkt.onsketFerdigDato && (
                      <button
                        type="button"
                        className="rounded-lg border px-2 py-1 text-sm hover:bg-slate-50 disabled:opacity-50"
                        disabled={updating}
                        onClick={() => saveOnsketFerdigDato(null)}
                      >
                        Fjern
                      </button>
                    )}
                  </div>
                  {erForTidligOnsket(onsketFerdigDatoInput, produkt.forventetFerdigDato) && (
                    <p role="alert" className="mt-1 text-xs font-medium text-amber-700">
                      Advarsel: ønsket dato er før beregnet ferdigdato. Sjekk kapasiteten
                      før du lover kunden dette.
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-start gap-2">
                <div>
                  <span className="font-medium">Kommentar:</span>{" "}
                  {produkt.kommentar || (
                    <span className="text-slate-500 italic">Ingen kommentar</span>
                  )}
                </div>

              </div>

              {produkt.mottakKommentar && (
                <div>
                  <span className="font-medium">Kommentar ved mottak:</span>{" "}
                  {produkt.mottakKommentar}
                </div>
              )}

              {ansvarligNavn && <div>Mottatt av: {ansvarligNavn}</div>}
              {workDesc?.trim() && (
                <div className="italic">«{workDesc.trim()}»</div>
              )}
              <div>
                {produkt.prisoverslag ? (
                  svc?.status === "Tilbud godkjent" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 border border-green-400 text-green-900 font-semibold text-xs">
                      ✅ Pristilbud godkjent
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 border border-yellow-400 text-yellow-900 font-semibold text-xs">
                      💰 Pristilbud ønsket
                    </span>
                  )
                ) : (
                  <span className="text-slate-500">Prisoverslag: Ikke avhuket</span>
                )}
              </div>

              {(productServiceSlug === "uten-motor" || productServiceSlug === "el-sykkel") && !linkedBike && (
                <button
                  type="button"
                  onClick={() => document.getElementById("sykkelkort-boks")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 hover:bg-amber-100 w-full text-left"
                >
                  <span>🚲</span>
                  <span>Ingen sykkel registrert — opprett sykkelkort</span>
                  <span className="ml-auto text-amber-500">↓</span>
                </button>
              )}

              {(productServiceSlug === "uten-motor" || productServiceSlug === "el-sykkel") && linkedBike && (
                <button
                  type="button"
                  onClick={() => document.getElementById("sykkelkort-boks")?.scrollIntoView({ behavior: "smooth" })}
                  className="mt-2 flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1.5 hover:bg-green-100 w-full text-left"
                >
                  <span>🚲</span>
                  <span className="font-medium">
                    {[linkedBike.merke, linkedBike.modell].filter(Boolean).join(" ") || "Sykkel"}
                  </span>
                  {linkedBike.shortId && (
                    <span className="font-mono text-green-600 ml-1">· {linkedBike.shortId}</span>
                  )}
                  <span className="ml-auto text-green-500">↓</span>
                </button>
              )}

              {svc?.produkt?.prioritering?.grunn && (
                <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-red-700">Fortløpende kø</span>
                    {svc.produkt.prioritering.haster && (
                      <span className="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-white text-[10px] font-bold tracking-wide">
                        HASTER
                      </span>
                    )}
                  </div>
                  <div className="text-slate-700">
                    <span className="font-medium">Grunn:</span>{" "}
                    {({
                      etterjustering: "Etterjustering / førstegangsservice",
                      akuttservice: "Akuttservice",
                      reklamasjon: "Reklamasjon",
                      kundeprioritering: "Kundeprioritering",
                      avtalemekaniker: "Avtale med mekaniker",
                    })[svc.produkt.prioritering.grunn] ?? svc.produkt.prioritering.grunn}
                  </div>
                  {svc.produkt.prioritering.forklaring && (
                    <div className="text-slate-600 italic">
                      "{svc.produkt.prioritering.forklaring}"
                    </div>
                  )}
                </div>
              )}

              {(svc?.produkt?.garantihenvendelse || hasGarantiServiceLine) && (
                <div className="mt-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs space-y-1">
                  <div className="font-semibold text-orange-700">Reklamasjonsvurdering</div>
                  {svc?.produkt?.garantihenvendelse?.reklamasjonsgrunn && (
                    <div className="text-slate-700">
                      <span className="font-medium">Reklamasjonsgrunn:</span>{" "}
                      {svc.produkt.garantihenvendelse.reklamasjonsgrunn}
                    </div>
                  )}
                  {svc?.produkt?.garantihenvendelse?.problemdato && (
                    <div className="text-slate-700">
                      <span className="font-medium">Problem oppsto:</span>{" "}
                      {svc.produkt.garantihenvendelse.problemdato}
                    </div>
                  )}
                  {svc?.produkt?.garantihenvendelse?.kjopsdato && (
                    <div className="text-slate-700">
                      <span className="font-medium">Kjøpsdato:</span>{" "}
                      {svc.produkt.garantihenvendelse.kjopsdato}
                    </div>
                  )}
                  {svc?.produkt?.garantihenvendelse?.forklaring && (
                    <div className="text-slate-600 italic">
                      "{svc.produkt.garantihenvendelse.forklaring}"
                    </div>
                  )}
                  {svc?.produkt?.garantihenvendelse?.kvitteringBilde && (
                    <div className="mt-1">
                      <img
                        src={svc.produkt.garantihenvendelse.kvitteringBilde}
                        alt="Kvittering"
                        className="max-h-24 rounded-lg border border-orange-200 cursor-pointer"
                        onClick={() => window.open(svc.produkt.garantihenvendelse.kvitteringBilde, "_blank")}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Høyre: Kontroller */}
          <div className="md:col-span-1 md:max-w-xs md:ml-auto w-full">
            <div className="space-y-4">
              {/* Linje 1: Status */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="text-slate-600 leading-none mb-1">
                    Status: <strong>{svc.status}</strong>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    className="text-sm px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                    onClick={() => {
                      // Nullstill mal + tekstfelt hver gang modal åpnes
                      setSelectedTemplateId("");
                      setSmsText("");
                      setHasAutoSelectedTemplate(false);
                      setAutoTemplateBlocked(true);

                      if (svc?._id) {
                        const key = `wd:smsBlockAuto:${svc._id}`;
                        window.sessionStorage.setItem(key, "1");
                      }

                      setStatusModalOpen(true);
                    }}
                  >
                    Endre status
                  </button>

                  <button
                    className="text-sm px-3 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                    onClick={openReadyModal}
                    title="Sett status til avsluttet og fyll inn ferdig-melding"
                  >
                    Ferdig
                  </button>
                </div>

              </div>

              {/* Linje 2: Sjekkliste */}
              {hasChecklistEligible && (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="text-slate-600 leading-none mb-1">
                      Sjekkliste
                    </div>
                    <div className="text-xs">
                      {checklist.loadingItems || checklist.loadingSaved ? (
                        <span className="text-slate-600">
                          Laster sjekkliste …
                        </span>
                      ) : checklist.unprocessedCount > 0 ? (
                        <span className="text-amber-600">
                          ⚠ {checklist.unprocessedCount} punkt
                          {checklist.unprocessedCount === 1 ? "" : "er"} ikke
                          behandlet
                        </span>
                      ) : (checklist.items?.length ?? 0) > 0 ? (
                        <span className="text-emerald-700">
                          ✔ Alle sjekkpunkt er behandlet
                        </span>
                      ) : (
                        <span className="text-slate-600">
                          Ingen sjekkliste tilknyttet
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    className="text-sm px-3 py-1 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shrink-0"
                    onClick={checklist.openModal}
                    title="Åpne sjekkliste for servicepakker"
                  >
                    Sjekkliste
                  </button>
                </div>
              )}

              {/* Linje 4: Pristilbud */}
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="text-slate-600 leading-none mb-1">
                    Pristilbud
                  </div>
                  <div className="text-xs text-slate-600">
                    Kundevennlig tilbud for godkjenning
                  </div>
                </div>
                <button
                  className="text-sm px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shrink-0"
                  onClick={quote.show}
                  title="Åpne pristilbud"
                >
                  Åpne
                </button>
              </div>

              {/* Linje 3: Servicefaktura */}
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                  <div className="text-slate-600 leading-none mb-1">
                    Servicefaktura
                  </div>
                  <div className="text-xs text-slate-600">
                    For utskrift i A4
                  </div>
                </div>
                <button
                  className="text-sm px-3 py-1 rounded-lg bg-slate-800 text-white hover:bg-black shrink-0"
                  onClick={() => setInvoiceOpen(true)}
                  title="Åpne servicefaktura"
                >
                  Åpne
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {svc?._id && <ServiceButikkvarer serviceId={svc._id} onKobling={setButikkvarer} />}

      {/* Meldinger mellom kunde og verksted */}
      <div className="border border-teal-200 rounded-2xl mb-4 bg-teal-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-teal-100 border-b border-teal-200">
          <div className="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-teal-700 shrink-0"><path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z" clipRule="evenodd" /></svg>
            <h3 className="font-semibold text-teal-900">Dialog med kunde</h3>

            {customerMessageCount > 0 && (
              <span className="text-xs text-teal-700">
                {customerMessageCount} melding
                {customerMessageCount === 1 ? "" : "er"} fra kunde
              </span>
            )}
          </div>

          <button
            className="relative text-sm px-3 py-1 rounded-xl border border-teal-300 bg-white hover:bg-teal-50 text-teal-800 flex items-center gap-2"
            onClick={() => {
              setCustomerDialogOpen((prev) => {
                const next = !prev;
                // Når vi åpner dialogen → marker alle kundemeldinger som lest
                if (!prev && next) {
                  markAllCustomerMessagesAsRead();
                }
                return next;
              });
            }}
          >
            {/* Badge for uleste når boksen er lukket */}
            {!customerDialogOpen && unreadCustomerCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-600 text-white">
                {unreadCustomerCount}
              </span>
            )}
            {customerDialogOpen ? "Skjul dialog" : "Vis dialog"}
          </button>
        </div>


        {customerDialogOpen && (
          <div className="px-4 py-4">
            {/* Meldingsliste */}
            <div className="max-h-64 overflow-y-auto mb-4 space-y-2 text-sm">
              {Array.isArray(svc?.messages) && svc.messages.length > 0 ? (
                svc.messages
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt || b.at || 0) -
                      new Date(a.createdAt || a.at || 0)
                  )
                  .map((m) => {
                    const fromCustomer = m.from === "customer";
                    const label = fromCustomer
                      ? customer
                        ? `${customer.firstName || "Kunde"}`
                        : "Kunde"
                      : m.fromName || "Mekaniker";

                    const at = fmtDT(m.createdAt || m.at);

                    return (
                      <div
                        key={
                          m._id ||
                          `${m.from}-${m.createdAt}-${m.text.slice(0, 10)}`
                        }
                        className={`flex ${fromCustomer ? "justify-start" : "justify-end"
                          }`}
                      >
                        <div className="max-w-[80%]">
                          <div
                            className={`text-[11px] mb-0.5 ${fromCustomer
                              ? "text-slate-600"
                              : "text-right text-slate-600"
                              }`}
                          >
                            {label} {at ? `· ${at}` : ""}
                          </div>
                          <div
                            onClick={() => toggleMessageUnread(m)}
                            title="Trykk for å markere som ulest"
                            className={`cursor-pointer rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words transition ${fromCustomer
                              ? m.readAt
                                ? "bg-slate-100 hover:bg-slate-200"
                                : "bg-yellow-100 hover:bg-yellow-200"
                              : "bg-emerald-600 text-white hover:opacity-90"
                              }`}
                          >
                            {m.text}
                          </div>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-slate-600 text-sm">
                  Ingen meldinger ennå. Start dialogen under.
                </p>
              )}

            </div>

            {/* Skriv melding */}
            <textarea
              className="w-full border border-teal-300 rounded-xl px-3 py-2 text-sm min-h-[70px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
              placeholder="Skriv en melding til kunden …"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
            />

            {sendMsgError && (
              <div className="text-xs text-red-600 mt-1">
                {sendMsgError}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
              <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1.5 text-sm text-slate-700">
                Sendes som
                <select
                  value={chatEmployee}
                  onChange={handleChatEmployeeChange}
                  className="border border-teal-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  {!chatEmployee && <option value="">Velg ansatt</option>}
                  {chatEmployee && !chatEmployees.some((emp) => emp.navn === chatEmployee) && (
                    <option value={chatEmployee}>{chatEmployee.split(" ")[0]}</option>
                  )}
                  {chatEmployees.map((emp) => (
                    <option key={emp._id} value={emp.navn}>
                      {emp.navn.split(" ")[0]}
                    </option>
                  ))}
                </select>
              </label>
              <button
                onClick={sendWorkshopMessage}
                disabled={sendingMessage || !messageText.trim()}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white ${sendingMessage || !messageText.trim()
                  ? "bg-slate-400 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
              >
                {sendingMessage ? "Sender…" : `Send som ${employeeName}`}
              </button>
              </div>
              <p className="text-[11px] text-slate-600">
                Vises også for kunden.
              </p>
            </div>
          </div>
        )}
      </div>


      {/* Kundevisning (portal) */}
      <div className="border border-indigo-200 rounded-2xl p-0 mb-4 bg-indigo-50 overflow-hidden">
        <div className="flex items-start justify-between gap-2 px-4 py-3 bg-indigo-100 border-b border-indigo-200">
          <div>
            <h3 className="font-semibold text-indigo-900">Kundevisning og pristilbud</h3>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm text-slate-600">Pristilbud:</span>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-lg border ${quoteEnabled
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-100 text-slate-600 border-slate-300"
                  }`}
                title={
                  quoteEnabled
                    ? "Aktiv: Kunden ser arbeid/deler og kan godkjenne"
                    : "Inaktiv: Kunden ser kun Produkt-blokken"
                }
              >
                {quoteEnabled ? "Aktiv" : "Inaktiv"}
              </span>


              {quoteEnabled && hasQuoteDecision && (
                <div className="mt-2 text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 rounded-lg border text-xs font-medium ${isQuoteApproved
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-red-50 text-red-700 border-red-200"
                        }`}
                    >
                      {isQuoteApproved
                        ? "Godkjent av kunde"
                        : "Avslått av kunde"}
                    </span>
                  </div>

                  {approval?.note && (
                    <div className="text-slate-700">
                      <span className="font-medium">
                        Kundens kommentar:
                      </span>{" "}
                      {approval.note}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
            onClick={() => setCustomerViewOpen((v) => !v)}
            aria-expanded={customerViewOpen}
            aria-controls="customer-view-body"
          >
            {customerViewOpen ? "Skjul" : "Vis"}
          </button>
        </div>

        {customerViewOpen && (
          <div id="customer-view-body" className="px-4 py-4 space-y-4">
            {/* Lenke og kontrollknapper */}
            {customerLink ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col md:flex-row md:items-center gap-2">
                  <a
                    href={`${customerLink}${customerLink.includes("?") ? "&" : "?"}staffPreview=1`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-indigo-700 underline break-all"
                    title="Åpne kundeside i ny fane (registreres som butikkens forhåndsvisning, ikke som kunde-åpning)"
                  >
                    {customerLink}
                  </a>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={copyCustomerLink}
                      className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
                    >
                      Kopier
                    </button>

                    <button
                      onClick={async () => {
                        if (!svc?._id) return;

                        setResetQuoteBusy(true);
                        setMsg("");
                        console.log("[Nullstill pristilbud] start", { quote: svc.quote });

                        const newQuote = { enabled: false, approved: null };

                        try {
                          const r = await fetch(`${API_BASE_URL}/workshop-services/${svc._id}`, {
                            method: "PATCH",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ quote: newQuote }),
                          });

                          if (!r.ok) {
                            const body = await r.json().catch(() => ({}));
                            console.error(
                              "[Nullstill pristilbud] PATCH feilet:",
                              r.status,
                              body
                            );
                            throw new Error("Kunne ikke nullstille pristilbud");
                          }

                          const updated = await r.json();
                          console.log(
                            "[Nullstill pristilbud] oppdatert fra server:",
                            updated.quote
                          );

                          // Oppdater state
                          setSvc(updated);

                          // Sett status til aktiv hvis noe annet
                          if (updated.status !== "aktiv") {
                            await changeStatus("aktiv");
                          }

                          setMsg("Pristilbud er nullstilt og status satt til Aktiv.");
                        } catch (e) {
                          console.error("Nullstill pristilbud feilet:", e);
                          setMsg("Kunne ikke nullstille pristilbud.");
                        } finally {
                          setResetQuoteBusy(false);
                        }
                      }}
                      disabled={resetQuoteBusy}
                      className={`text-sm px-3 py-1 rounded-lg text-white ${resetQuoteBusy
                        ? "bg-slate-400 cursor-not-allowed"
                        : "bg-amber-600 hover:bg-amber-700"
                        }`}
                    >
                      {resetQuoteBusy ? "Nullstiller…" : "Nullstill pristilbud"}
                    </button>



                  </div>
                </div>

                <div className="text-xs text-slate-600">
                  {svc?.portal?.openCount ? (
                    <span className="text-emerald-700 font-medium">
                      ✓ Kunden har åpnet lenken {svc.portal.openCount}{" "}
                      {svc.portal.openCount === 1 ? "gang" : "ganger"}
                      {svc.portal.lastOpenedAt
                        ? `, sist ${fmtDT(svc.portal.lastOpenedAt)}`
                        : ""}
                    </span>
                  ) : (
                    <span className="text-slate-600">
                      Kunden har ikke åpnet lenken ennå
                    </span>
                  )}
                  {svc?.portal?.previewCount ? (
                    <span className="text-slate-500">
                      {" "}
                      · Forhåndsvist av butikk {svc.portal.previewCount}{" "}
                      {svc.portal.previewCount === 1 ? "gang" : "ganger"}
                      {svc.portal.lastPreviewedAt
                        ? `, sist ${fmtDT(svc.portal.lastPreviewedAt)}`
                        : ""}
                    </span>
                  ) : null}
                </div>

                {Array.isArray(svc?.portal?.openEvents) &&
                  svc.portal.openEvents.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setOpenHistoryVisible((v) => !v)}
                        className="text-xs text-indigo-700 underline hover:no-underline"
                      >
                        {openHistoryVisible
                          ? "Skjul logg over klikk"
                          : `Vis logg over klikk (${svc.portal.openEvents.length})`}
                      </button>

                      {openHistoryVisible && (
                        <ul className="mt-1 max-h-48 overflow-y-auto border rounded-lg bg-white divide-y text-xs">
                          {[...svc.portal.openEvents]
                            .reverse()
                            .map((ev, i) => (
                              <li
                                key={ev._id || i}
                                className="px-2 py-1 flex items-center justify-between gap-2"
                              >
                                <span
                                  className={
                                    ev.source === "staff"
                                      ? "text-slate-600"
                                      : "text-emerald-700 font-medium"
                                  }
                                >
                                  {ev.source === "staff" ? "Butikk" : "Kunde"}
                                </span>
                                <span className="text-slate-600">
                                  {fmtDT(ev.at)}
                                </span>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  )}
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-slate-600">
                  Ingen kundelenke enda. Opprett for å dele prisoverslaget med
                  kunden.
                </div>
                <button
                  onClick={() =>
                    createOrRotateCustomerLink({ rotate: false })
                  }
                  disabled={linkBusy}
                  className={`text-sm px-3 py-1 rounded-lg text-white ${linkBusy
                    ? "bg-slate-400"
                    : "bg-indigo-600 hover:bg-indigo-700"
                    }`}
                >
                  {linkBusy ? "Lager…" : "Opprett kundelenke"}
                </button>
              </div>
            )}

            {/* Tekst til pristilbud (vises for kunde) */}
            <div className="pt-3 border-t">
              <h4 className="font-medium mb-2">
                Tekst til pristilbud
              </h4>
              <p className="text-xs text-slate-600 mb-2">
                Denne teksten vises i pristilbudet til kunden som forklaring på det
                foreslåtte arbeidet.
              </p>

              <textarea
                rows={3}
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="F.eks: Vi anbefaler full service med bytte av kjede og kassett, samt justering av gir og bremser…"
                value={quoteDesc}
                onChange={(e) => setQuoteDesc(e.target.value)}
                onBlur={saveQuoteDesc}          // 👈 lagres når du går ut av feltet
              />

              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={saveQuoteDesc}
                  disabled={quoteDescBusy}
                  className={`px-3 py-1 rounded-lg text-white ${quoteDescBusy
                    ? "bg-slate-400 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                >
                  {quoteDescBusy ? "Lagrer…" : "Lagre"}
                </button>
                <span className="text-xs text-slate-600">
                  Lagrer automatisk når du forlater feltet.
                </span>
              </div>
            </div>



            {/* Send lenke på melding (SMS/e-post) */}
            {customerLink && (
              <div className="pt-3 border-t">
                <h4 className="font-medium mb-2">
                  Send lenke til kunden
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={sendOnStatusChange}
                      onChange={(e) =>
                        setSendOnStatusChange(e.target.checked)
                      }
                    />
                    <span>Send SMS</span>
                    <span className="text-xs text-slate-600">
                      {customerPhone || customer?.phoneNumber || "Mangler telefonnummer"}
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={sendEmailOnStatusChange}
                      onChange={(e) =>
                        setSendEmailOnStatusChange(e.target.checked)
                      }
                    />
                    <span>Send e-post</span>
                    <span className="text-xs text-slate-600">
                      {customerEmail || customer?.email || "Mangler e-postadresse"}
                    </span>
                  </label>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    onClick={notifyQuoteLink}
                    disabled={
                      !customerLink ||
                      smsBusy ||
                      notifyBusy ||
                      emailBusy ||
                      (!sendOnStatusChange && !sendEmailOnStatusChange) ||
                      (sendOnStatusChange && !(customerPhone || customer?.phoneNumber)) ||
                      (sendEmailOnStatusChange && !(customerEmail || customer?.email))
                    }
                    className={`px-3 py-1 rounded-lg text-white ${!customerLink ||
                      smsBusy ||
                      notifyBusy ||
                      emailBusy ||
                      (!sendOnStatusChange && !sendEmailOnStatusChange) ||
                      (sendOnStatusChange && !(customerPhone || customer?.phoneNumber)) ||
                      (sendEmailOnStatusChange && !(customerEmail || customer?.email))
                      ? "bg-slate-400 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                      }`}
                    title={
                      !customerLink
                        ? "Opprett kundelenke først"
                        : !sendOnStatusChange && !sendEmailOnStatusChange
                          ? "Huk av SMS og/eller e-post"
                          : sendOnStatusChange &&
                            !(customerPhone || customer?.phoneNumber)
                            ? "Kunde mangler telefonnummer"
                            : sendEmailOnStatusChange &&
                              !(customerEmail || customer?.email)
                              ? "Kunde mangler e-postadresse"
                              : "Send valgte varsler"
                    }
                  >
                    {smsBusy || notifyBusy || emailBusy ? "Sender …" : "Aktiver pristilbud"}
                  </button>


                  <span className="text-xs text-slate-600">
                    {sendOnStatusChange ? "SMS " : ""}
                    {sendOnStatusChange && sendEmailOnStatusChange ? "+ " : ""}
                    {sendEmailOnStatusChange ? "e-post" : ""}
                  </span>
                </div>

                {msg && (
                  <div className="text-sm text-slate-800 mt-2 transition-opacity duration-300">
                    {msg}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tjenester */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
        <div className="flex items-center justify-between">
          <h3 className="mb-2 text-base font-bold text-slate-900">Tjenester</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setServiceModalOpen(true);
                setServiceTab("list");
                ensureHygraph();
              }}
              className="text-sm px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Legg til tjeneste
            </button>
          </div>
        </div>

        {serviceLines.length ? (
          <ul className="list-none space-y-2 mt-4">
            {serviceLines.map((l, i) => (
              <li
                key={l._id || `${i}-${l.slug || l.navn || "row"}`}
                className="group"
              >
                <div className="flex items-start gap-3">
                  <button
                    disabled={updating}
                    onClick={() => startEditLine(i, l)}
                    className="shrink-0 text-xs px-2 py-0.5 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
                    title="Endre linje"
                  >
                    Endre
                  </button>
                  <button
                    disabled={updating}
                    onClick={() => removeLine(l, i)}
                    className="shrink-0 text-xs px-2 py-0.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
                    title="Slett linje"
                  >
                    Slett
                  </button>

                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-medium">
                        {renameTjeneste(l.navn, l.slug) || "Tjeneste"}
                      </span>
                      <span
                        className={
                          l.reklamasjon || l.gratisarbeid
                            ? "text-slate-600 line-through"
                            : "text-slate-700"
                        }
                      >
                        {fmtNOK(l.pris)}
                      </span>
                      {l.tidsbruk ? (
                        <span className="text-slate-600 text-sm">
                          ({Math.round(l.tidsbruk)} min)
                        </span>
                      ) : null}
                      {(l.reklamasjon || String(l.slug || "").toLowerCase() === "garantihenvendelse") && (
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-lg bg-red-100 text-red-700">
                          Reklamasjon
                        </span>
                      )}
                      {(l.gratisarbeid || flatServices.find(s => s.slug === l.slug)?.gratisarbeid) && (
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-lg bg-emerald-100 text-emerald-700">
                          Gratisarbeid
                        </span>
                      )}
                    </div>

                    {(l.kommentar ||
                      (Array.isArray(l.foranBak) && l.foranBak.length) ||
                      (Array.isArray(l.servicetyper) &&
                        l.servicetyper.length)) && (
                        <div className="text-sm text-slate-600 mt-1">
                          {l.kommentar ? <div>{l.kommentar}</div> : null}
                          {Array.isArray(l.foranBak) && l.foranBak.length ? (
                            <div>Posisjon: {l.foranBak.join(", ")}</div>
                          ) : null}
                          {Array.isArray(l.servicetyper) &&
                            l.servicetyper.length ? (
                            isLineChecklistEligible(l) && checklistComplete ? (
                              <div>Se sjekkliste på neste side/bakside for detaljer</div>
                            ) : (
                              <div>Type: {l.servicetyper.join(", ")}</div>
                            )
                          ) : null}
                        </div>
                      )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-slate-600 mt-3">
            Ingen tjenestelinjer.
          </div>
        )}

        <div className="mt-4 border-t pt-3 text-base flex items-center justify-between">
          <span className="font-semibold">Sum tjenester</span>
          <div className="text-right">
            <div>{fmtNOK(totals.arbeid)}</div>
            {totals.min ? (
              <div className="text-sm text-slate-600">
                {fmtMin(totals.min)}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {editingLineIndex !== null && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={closeLineEditModal}
        >
          <div
            className="bg-white w-full max-w-md rounded-lg shadow p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold">Endre tjenestelinje</h4>
              <button
                onClick={closeLineEditModal}
                className="text-slate-600 hover:text-black"
              >
                Lukk
              </button>
            </div>

            {lineEditError && (
              <div className="mb-3 rounded-lg bg-red-50 border border-red-300 text-red-700 text-sm px-3 py-2">
                {lineEditError}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Navn</label>
                <input
                  className="w-full border rounded-lg p-2"
                  value={lineEditNavn}
                  onChange={(e) => setLineEditNavn(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Kommentar</label>
                <textarea
                  className="w-full border rounded-lg p-2"
                  rows={3}
                  value={lineEditKommentar}
                  onChange={(e) => setLineEditKommentar(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Tidsbruk (min)</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border rounded-lg p-2"
                    value={lineEditTidsbruk}
                    onChange={(e) => setLineEditTidsbruk(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Pris (kr)</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full border rounded-lg p-2"
                    value={lineEditPris}
                    onChange={(e) => setLineEditPris(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={lineEditReklamasjon}
                    onChange={(e) => setLineEditReklamasjon(e.target.checked)}
                    className="w-4 h-4 accent-red-600"
                  />
                  <span className="text-sm font-medium text-red-700">Reklamasjonsarbeid</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={lineEditGratisarbeid}
                    onChange={(e) => setLineEditGratisarbeid(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                  />
                  <span className="text-sm font-medium text-emerald-700">Gratisarbeid</span>
                </label>
              </div>
              {(lineEditReklamasjon || lineEditGratisarbeid) && (
                <div className="text-xs text-slate-600 -mt-2">
                  Prisen telles ikke med i summen, men vises med strek over på kvitteringen.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={closeLineEditModal}
                className="px-4 py-2 text-sm rounded-lg border hover:bg-slate-50"
              >
                Avbryt
              </button>
              <button
                onClick={saveEditLine}
                disabled={lineEditBusy}
                className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {lineEditBusy ? "Lagrer…" : "Lagre endringer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deler */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
        <div className="flex items-center justify-between">
          <h3 className="mb-2 text-base font-bold text-slate-900">Deler</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setPartsModalOpen(true);
                setPartsTab("list");
                setParts([]);
                setPartsQuery("");
              }}
              className="text-sm px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Legg til del
            </button>
          </div>
        </div>

        {partLines.length ? (
          <ul className="list-none space-y-4 mt-4">
            {partLines.map((p, i) => {
              const price = Number(p.price) || 0;
              const qty = Math.max(1, Number(p.qty) || 1);
              const subtotal = price * qty;

              let discountKr = Math.max(0, Number(p.discount) || 0);

              // Hvis vi ikke har discountKr, men har discountPercent → regn om
              let discountPercent =
                p.discountPercent != null
                  ? Number(p.discountPercent) || 0
                  : 0;

              if (!discountKr && discountPercent && subtotal > 0) {
                discountKr = Math.round((subtotal * discountPercent) / 100);
              } else if (!discountPercent && subtotal > 0 && discountKr > 0) {
                // Hvis vi bare har kr, men ingen prosent, utled prosent
                discountPercent = Math.round((discountKr / subtotal) * 1000) / 10; // én desimal
              }

              const lineTotal = Math.max(0, subtotal - discountKr);

              // 👇 LEGG DETTE HER
              const draft = partDiscountDraft?.[i] || {};

              const displayDiscountKr =
                draft.discount != null
                  ? String(draft.discount)
                  : discountKr > 0
                    ? String(discountKr)
                    : "";

              const displayPercent =
                draft.percent != null
                  ? String(draft.percent)
                  : discountPercent > 0
                    ? String(discountPercent)
                    : "";

              return (
                <li
                  key={`${i}-${p.ean || p.product || "part"}`}
                  className="group"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      disabled={updating}
                      onClick={() => startEditPart(i, p)}
                      className="shrink-0 text-xs px-3 py-1 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
                      title="Endre del"
                    >
                      Endre
                    </button>
                    <button
                      disabled={updating}
                      onClick={() => removePart(i)}
                      className="shrink-0 text-xs px-3 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
                      title="Slett del"
                    >
                      Slett
                    </button>

                    <div className="flex-1 min-w-[240px]">
                      <span className="font-medium">
                        {p.product || "Del"}
                      </span>
                      {p.brand ? (
                        <span className="text-slate-600 text-sm">
                          {" "}
                          ({p.brand})
                        </span>
                      ) : null}
                    </div>

                    <span className="text-slate-700">
                      {fmtNOK(p.price)}
                    </span>

                    <div className="flex items-center gap-1 ml-2">
                      <button
                        type="button"
                        disabled={updating}
                        className="px-2 border rounded-lg"
                        onClick={() => updatePartQty(i, Math.max(1, (Number(p.qty) || 1) - 1), p)}
                        aria-label="Minsk antall"
                      >
                        –
                      </button>

                      {/* ✅ Antall-visning */}
                      <span className="min-w-[2rem] text-center text-sm font-medium select-none">
                        {Math.max(1, Number(p.qty) || 1)}
                      </span>

                      <button
                        type="button"
                        disabled={updating}
                        className="px-2 border rounded-lg"
                        onClick={() => updatePartQty(i, (Number(p.qty) || 1) + 1, p)}
                        aria-label="Øk antall"
                      >
                        +
                      </button>
                    </div>

                    <div className="flex items-center gap-2 ml-3">
                      {/* Rabatt i kroner */}
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-20 border rounded-lg p-1 text-sm"
                        value={displayDiscountKr}
                        onChange={(e) => setPartDraft(i, { discount: e.target.value })}
                        onBlur={() => commitPartDiscount(i, "kr", displayDiscountKr, subtotal)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.currentTarget.blur(); // trigger commit
                          }
                        }}
                        placeholder="0"
                        title="Rabatt i kroner for denne linjen"
                      />

                      {/* Rabatt i prosent */}
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-20 border rounded-lg p-1 text-sm"
                        value={displayPercent}
                        onChange={(e) => setPartDraft(i, { percent: e.target.value })}
                        onBlur={() => commitPartDiscount(i, "percent", displayPercent, subtotal)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.currentTarget.blur();
                          }
                        }}
                        placeholder="0"
                        title="Rabatt i prosent for denne linjen"
                      />
                      <span className="text-sm text-slate-600">% rabatt</span>
                    </div>

                    <span className="ml-auto text-slate-900 font-semibold">
                      {fmtNOK(lineTotal)}
                    </span>
                  </div>
                  <DelBidrag linje={p} />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="text-slate-600 mt-3">Ingen deler.</div>
        )}

        <div className="mt-4 border-t pt-3 text-base flex items-center justify-between">
          <span className="font-semibold">Sum deler</span>
          <div className="text-right">
            <div>{fmtNOK(totals.deler)}</div>
          </div>
        </div>
        <DelerBidragSum partLines={partLines} />
      </div>

      {/* Frakt — reell fraktkostnad, f.eks. retur av motor til kunde etter
          motorservice. Holdes bevisst utenom arbeid og deler. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
        <div className="flex items-center justify-between">
          <h3 className="mb-2 text-base font-bold text-slate-900">Frakt</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={openFraktForm}
              className="text-sm px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Legg til frakt
            </button>
          </div>
        </div>

        {fraktFormOpen && (
          <div className="mt-3 p-3 bg-slate-50 rounded-lg border space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-600 mb-1">Beskrivelse</label>
                <input
                  type="text"
                  value={fraktBeskrivelse}
                  onChange={(e) => setFraktBeskrivelse(e.target.value)}
                  className="w-full border rounded-lg px-2 py-1 text-sm"
                  placeholder="F.eks. Retur til kunde"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Pris (kr)</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={fraktPris}
                  onChange={(e) => setFraktPris(e.target.value)}
                  className="w-full border rounded-lg px-2 py-1 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Kommentar (valgfritt)</label>
              <input
                type="text"
                value={fraktKommentar}
                onChange={(e) => setFraktKommentar(e.target.value)}
                className="w-full border rounded-lg px-2 py-1 text-sm"
              />
            </div>
            {fraktError && <p className="text-sm text-red-600">{fraktError}</p>}
            <div className="flex gap-2">
              <button
                onClick={saveFraktLine}
                disabled={fraktBusy}
                className="text-sm px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {fraktBusy ? "Lagrer…" : editingFraktIndex !== null ? "Lagre endring" : "Legg til"}
              </button>
              <button
                onClick={closeFraktForm}
                className="text-sm px-3 py-1.5 rounded-lg border hover:bg-slate-100"
              >
                Avbryt
              </button>
            </div>
          </div>
        )}

        {fraktLines.length ? (
          <ul className="list-none space-y-2 mt-4">
            {fraktLines.map((f, i) => (
              <li key={i} className="group">
                <div className="flex items-start gap-3">
                  <button
                    disabled={updating}
                    onClick={() => startEditFrakt(i, f)}
                    className="shrink-0 text-xs px-2 py-0.5 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
                    title="Endre frakt"
                  >
                    Endre
                  </button>
                  <button
                    disabled={updating}
                    onClick={() => removeFraktLine(i)}
                    className="shrink-0 text-xs px-2 py-0.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-60"
                    title="Slett frakt"
                  >
                    Slett
                  </button>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-medium">{f.beskrivelse || "Frakt"}</span>
                      <span className="text-slate-700">{fmtNOK(f.pris)}</span>
                    </div>
                    {f.kommentar && (
                      <div className="text-sm text-slate-600">{f.kommentar}</div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-slate-600 mt-3">Ingen frakt.</div>
        )}

        <div className="mt-4 border-t pt-3 text-base flex items-center justify-between">
          <span className="font-semibold">Sum frakt</span>
          <div className="text-right">
            <div>{fmtNOK(totals.frakt)}</div>
          </div>
        </div>
      </div>

      {/* Sjekkliste – oppsummering (skjult som standard) */}
      {hasChecklist &&
        !checklist.loadingItems &&
        !checklist.loadingSaved &&
        !invoiceOpen && (
          <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4 no-print">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">Sjekkliste (oppsummering)</h3>
              <button
                className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
                onClick={() => setSummaryOpen((v) => !v)}
              >
                {summaryOpen ? "Skjul sjekklisten" : "Vis sjekklisten"}
              </button>
            </div>

            {summaryOpen && (
              <>
                {checklistSummary.length ? (
                  <ul className="mt-3 space-y-2">
                    {checklistSummary.map((row) => (
                      <li
                        key={row.slug}
                        className="border rounded-lg p-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                      >
                        <span className="font-medium">
                          {row.title}
                        </span>
                        <div className="flex items-center gap-2 text-sm">
                          {/* OK-badge */}
                          {row.ok && (
                            <span className="px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                              OK
                            </span>
                          )}

                          {/* Utbedres-badge */}
                          {row.done && (
                            <span className="px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200">
                              Utføres
                            </span>
                          )}


                          {/* Avvik-badge */}
                          {row.deviation && (
                            <span className="px-2 py-0.5 rounded-lg bg-red-50 text-red-700 border-red-200">
                              {row.done
                                ? "Avvik utføres"
                                : "Avvik utføres ikke"}
                            </span>
                          )}


                          {/* Kommentar-badge */}
                          {row.comment && (
                            <span className="px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                              Kommentar: {row.comment}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-3 text-slate-600">
                    Ingen punkt er huket av eller kommentert ennå.
                  </div>
                )}
              </>
            )}
          </div>
        )}


      {/* Kilometerstand (ODO) på elsykkel, rett over arbeidsbeskrivelsen (Anders, 04.11) */}
      {erElService && <OdoFelt svc={svc} sykkel={linkedBike} onLagret={odoLagret} />}

      {/* Arbeid & Ansvarlig */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
        <h3 className="mb-3 text-base font-bold text-slate-900">Arbeidsbeskrivelse</h3>

        {/* Utført arbeid */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Utført arbeid (arbeidsbeskrivelse)
          </label>
          <textarea
            rows={4}
            className="w-full border rounded-lg p-2"
            placeholder="Beskriv kort hva som er gjort i servicen …"
            value={workDesc}
            onChange={(e) => setWorkDesc(e.target.value)}
            onBlur={saveWorkDesc}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={saveWorkDesc}
              disabled={descBusy}
              className={`px-3 py-1 rounded-lg text-white ${descBusy
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
                }`}
            >
              {descBusy ? "Lagrer…" : "Lagre"}
            </button>
            <span className="text-xs text-slate-600">
              Lagrer automatisk når du forlater feltet.
            </span>
          </div>
        </div>

        {/* Ansvarlig (leses fra produkt.lastStatusBy) */}
        <div className="mt-2">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Service mottatt av
          </label>

          <div className="p-2 border rounded-lg bg-slate-50">
            <div className="text-sm">
              <span className="font-medium">
                {svc?.produkt?.lastStatusBy?.name || "—"}
              </span>
              {svc?.produkt?.lastStatusBy?.store ? (
                <span className="text-slate-600">
                  {" "}
                  · {svc.produkt.lastStatusBy.store}
                </span>
              ) : null}
            </div>
            {svc?.produkt?.lastStatusBy?.id ? (
              <div className="text-xs text-slate-600">
                ID: {svc.produkt.lastStatusBy.id}
              </div>
            ) : (
              <div className="text-xs text-slate-600">
                Settes automatisk når status oppdateres.
              </div>
            )}
          </div>
        </div>
      </div>

{/* Bilder */}
<div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
  <div className="flex items-start justify-between gap-3">
    <div>
      <h3 className="text-base font-bold text-slate-900">Bilder</h3>
      <p className="text-sm text-slate-600 mt-1">
        {serviceImages.length === 0 ? (
          "Ingen bilder lastet opp ennå."
        ) : (
          <>
            {serviceImages.length} bilde{serviceImages.length !== 1 ? "r" : ""} lagt til
            {visibleCustomerImageCount > 0 && (
              <> · {visibleCustomerImageCount} synlig for kunde</>
            )}
            {customerUploadedImages.length > 0 && (
              <> · <span className="text-teal-700">{customerUploadedImages.length} fra kunde</span></>
            )}
          </>
        )}
      </p>
    </div>

    <button
      type="button"
      onClick={() => setImagesOpen((v) => !v)}
      className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50 shrink-0"
    >
      {imagesOpen ? "Skjul" : "Vis"}
    </button>
  </div>

  {imagesOpen && (
    <div className="mt-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="text-sm text-slate-600">
          Last opp bilder av produktet, skader eller utført arbeid.
        </div>

        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer text-sm">
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (files?.length) {
                setPendingImageFiles(Array.from(files));
                setShowImageVisibilityModal(true);
              }
              e.target.value = "";
            }}
            disabled={imageUploading}
          />
          {imageUploading ? "Laster opp…" : "Last opp bilder"}
        </label>
      </div>

      {/* Bilder fra mekaniker */}
      {mechanicImages.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {mechanicImages.map((img) => (
            <div
              key={img._id || img.filename}
              className="border rounded-lg overflow-hidden bg-slate-50"
            >
              <a
                href={buildImageUrl(img.url)}
                target="_blank"
                rel="noreferrer"
                className="block"
              >
                <img
                  src={buildImageUrl(img.url)}
                  alt={img.caption || img.originalName || "Servicebilde"}
                  className="w-full h-40 object-cover"
                />
              </a>

              <div className="p-2">
                <div className="text-xs text-slate-600 break-words">
                  {img.originalName || img.filename}
                </div>

                {img.uploadedAt && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Lastet opp: {fmtDT(img.uploadedAt)}
                  </div>
                )}

                {img.uploadedBy?.name && (
                  <div className="text-[11px] text-slate-500">
                    Av: {img.uploadedBy.name}
                  </div>
                )}

                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={img.visibleToCustomer === true}
                      onChange={(e) =>
                        toggleImageVisibleToCustomer(img._id, e.target.checked)
                      }
                      disabled={imageBusyId === img._id}
                    />
                    Synlig for kunden
                  </label>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => deleteWorkshopImage(img._id)}
                      disabled={imageBusyId === img._id}
                      className={`text-xs px-2 py-1 rounded-lg border ${
                        imageBusyId === img._id
                          ? "bg-slate-200 text-slate-600 cursor-not-allowed"
                          : "border-red-300 text-red-700 hover:bg-red-50"
                      }`}
                    >
                      {imageBusyId === img._id ? "Jobber…" : "Slett"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-slate-600">Ingen bilder lastet opp ennå.</div>
      )}

      {/* Bilder fra kunde */}
      {customerUploadedImages.length > 0 && (
        <div className="mt-6 border-t pt-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">
            Bilder fra kunde
            <span className="ml-2 text-xs font-normal text-slate-600">
              {customerUploadedImages.length} bilde{customerUploadedImages.length !== 1 ? "r" : ""}
            </span>
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {customerUploadedImages.map((img) => (
              <div
                key={img._id || img.filename}
                className="border rounded-lg overflow-hidden bg-teal-50 border-teal-200"
              >
                <a
                  href={buildImageUrl(img.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block relative"
                >
                  <img
                    src={buildImageUrl(img.url)}
                    alt={img.caption || img.originalName || "Kundebilde"}
                    className="w-full h-40 object-cover"
                  />
                  <span className="absolute top-1.5 left-1.5 text-[10px] bg-teal-600 text-white px-1.5 py-0.5 rounded-lg">
                    Kunde
                  </span>
                </a>

                <div className="p-2">
                  <div className="text-xs text-slate-600 break-words">
                    {img.originalName || img.filename}
                  </div>

                  {img.uploadedAt && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      {fmtDT(img.uploadedAt)}
                    </div>
                  )}

                  <div className="mt-3 space-y-2">
                    <label className="flex items-center gap-2 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={img.visibleToCustomer === true}
                        onChange={(e) =>
                          toggleImageVisibleToCustomer(img._id, e.target.checked)
                        }
                        disabled={imageBusyId === img._id}
                      />
                      Synlig for kunden
                    </label>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => deleteWorkshopImage(img._id)}
                        disabled={imageBusyId === img._id}
                        className={`text-xs px-2 py-1 rounded-lg border ${
                          imageBusyId === img._id
                            ? "bg-slate-200 text-slate-600 cursor-not-allowed"
                            : "border-red-300 text-red-700 hover:bg-red-50"
                        }`}
                      >
                        {imageBusyId === img._id ? "Jobber…" : "Slett"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )}
</div>

{/* Dokumenter */}
<div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
  <div className="flex items-start justify-between gap-3">
    <div>
      <h3 className="text-base font-bold text-slate-900">Dokumenter</h3>
      <p className="text-sm text-slate-600 mt-1">
        {serviceDocuments.length === 0 ? (
          "Ingen dokumenter lastet opp ennå."
        ) : (
          <>
            {serviceDocuments.length} dokument{serviceDocuments.length !== 1 ? "er" : ""} lagt til
            {visibleCustomerDocumentCount > 0 && (
              <> · {visibleCustomerDocumentCount} synlig for kunde</>
            )}
          </>
        )}
      </p>
    </div>

    <button
      type="button"
      onClick={() => setDocumentsOpen((v) => !v)}
      className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50 shrink-0"
    >
      {documentsOpen ? "Skjul" : "Vis"}
    </button>
  </div>

  {documentsOpen && (
    <div className="mt-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="text-sm text-slate-600">
          Last opp PDF-dokumenter, f.eks. diagnoserapport eller softwareoppdatering.
        </div>

        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer text-sm">
          <input
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (files?.length) {
                setPendingDocumentFiles(Array.from(files));
                setShowDocumentVisibilityModal(true);
              }
              e.target.value = "";
            }}
            disabled={documentUploading}
          />
          {documentUploading ? "Laster opp…" : "Last opp dokumenter"}
        </label>
      </div>

      {serviceDocuments.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {serviceDocuments.map((doc) => (
            <div
              key={doc._id || doc.filename}
              className="border rounded-lg bg-slate-50 p-3 flex items-start gap-3"
            >
              <span className="text-2xl leading-none mt-0.5">📄</span>
              <div className="min-w-0 flex-1">
                <a
                  href={buildImageUrl(doc.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-indigo-700 hover:underline break-words"
                >
                  {doc.caption || doc.originalName || doc.filename}
                </a>

                {doc.uploadedAt && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    Lastet opp: {fmtDT(doc.uploadedAt)}
                  </div>
                )}

                {doc.uploadedBy?.name && (
                  <div className="text-[11px] text-slate-500">
                    Av: {doc.uploadedBy.name}
                  </div>
                )}

                <div className="mt-3 space-y-2">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={doc.visibleToCustomer === true}
                      onChange={(e) =>
                        toggleDocumentVisibleToCustomer(doc._id, e.target.checked)
                      }
                      disabled={documentBusyId === doc._id}
                    />
                    Synlig for kunden
                  </label>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => deleteWorkshopDocument(doc._id)}
                      disabled={documentBusyId === doc._id}
                      className={`text-xs px-2 py-1 rounded-lg border ${
                        documentBusyId === doc._id
                          ? "bg-slate-200 text-slate-600 cursor-not-allowed"
                          : "border-red-300 text-red-700 hover:bg-red-50"
                      }`}
                    >
                      {documentBusyId === doc._id ? "Jobber…" : "Slett"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-slate-600">Ingen dokumenter lastet opp ennå.</div>
      )}
    </div>
  )}
</div>

      {/* Sykkelkort */}
      {(productServiceSlug === "uten-motor" || productServiceSlug === "el-sykkel") && (
        <div id="sykkelkort-boks" className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-bold text-slate-900">Sykkelkort</h3>
              <p className="text-sm text-slate-600 mt-1">
                {linkedBike ? (
                  <span className="flex items-center gap-2 flex-wrap">
                    <span>{[linkedBike.merke, linkedBike.modell, linkedBike.type].filter(Boolean).join(" · ")}</span>
                    {linkedBike.shortId && (
                      <span className="font-mono font-bold text-slate-800 tracking-widest">{linkedBike.shortId}</span>
                    )}
                  </span>
                ) : "Ingen sykkel koblet"}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setBikeScannerOpen(true)}
                disabled={bikeBusy}
                className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
                title="Skann QR-kode fra sykkelkort"
              >
                📷 Skann
              </button>
              <button
                type="button"
                onClick={() => setBikeBoxOpen((v) => !v)}
                className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
              >
                {bikeBoxOpen ? "Skjul" : "Vis"}
              </button>
            </div>
          </div>

          {bikeBoxOpen && (
            <div className="mt-4">
              {linkedBike ? (
                <div className="border rounded-lg p-3 bg-slate-50 mb-3">
                  <div className="flex gap-4 items-start">
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        {linkedBike.shortId && (
                          <><span className="text-slate-600">Kort-ID</span><span className="font-mono font-bold tracking-widest text-slate-900">{linkedBike.shortId}</span></>
                        )}
                        {linkedBike.merke && (
                          <><span className="text-slate-600">Merke</span><span className="font-medium">{linkedBike.merke}</span></>
                        )}
                        {linkedBike.modell && (
                          <><span className="text-slate-600">Modell</span><span className="font-medium">{linkedBike.modell}</span></>
                        )}
                        {linkedBike.årsmodell && (
                          <><span className="text-slate-600">Årsmodell</span><span className="font-medium">{linkedBike.årsmodell}</span></>
                        )}
                        {linkedBike.type && (
                          <><span className="text-slate-600">Type</span><span className="font-medium">{linkedBike.type}</span></>
                        )}
                        {linkedBike.elsykkel !== null && linkedBike.elsykkel !== undefined && (
                          <><span className="text-slate-600">Elsykkel</span><span className={`font-medium ${linkedBike.elsykkel ? "text-green-700" : "text-slate-700"}`}>{linkedBike.elsykkel ? "Ja" : "Nei"}</span></>
                        )}
                        {linkedBike.rammenummer && (
                          <><span className="text-slate-600">Rammenr.</span><span className="font-medium">{linkedBike.rammenummer}</span></>
                        )}
                      </div>
                      {linkedBike.specImageUrl && (
                        <div className="mt-3 border-t pt-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Spesifikasjonsbilde</span>
                            <button
                              type="button"
                              onClick={() => setBikeSpecImageOpen(true)}
                              className="text-xs text-indigo-700 hover:underline"
                            >
                              Vis
                            </button>
                          </div>
                          <img
                            src={linkedBike.specImageUrl}
                            alt="Spesifikasjoner"
                            onClick={() => setBikeSpecImageOpen(true)}
                            className="w-full max-h-32 object-contain rounded-lg border cursor-pointer hover:opacity-90"
                          />
                        </div>
                      )}
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <button
                          type="button"
                          onClick={() => navigate(`/sykkelkort/${linkedBike._id}`)}
                          className="text-sm px-3 py-1 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        >
                          Åpne sykkelkort
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setBikeForm({ merke: linkedBike.merke || "", modell: linkedBike.modell || "", årsmodell: linkedBike.årsmodell ? String(linkedBike.årsmodell) : "", type: linkedBike.type || "", elsykkel: linkedBike.elsykkel ?? null, rammenummer: linkedBike.rammenummer || "" });
                            setModalBikeImageData({
                              specImageUrl: linkedBike.specImageUrl || "",
                              mainImageUrl: linkedBike.mainImageUrl || "",
                              receiptUrl: linkedBike.receiptUrl || "",
                              extraImages: linkedBike.extraImages || [],
                              diagnoseRapporter: linkedBike.diagnoseRapporter || [],
                              softwareRapporter: linkedBike.softwareRapporter || [],
                            });
                            setEditingBikeId(linkedBike._id);
                            setBikeSaveError("");
                            setBikeModalTab("new");
                            setBikeModalOpen(true);
                            fetchBikesForModal();
                          }}
                          className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
                        >
                          Endre
                        </button>
                        <button
                          type="button"
                          onClick={openBikeModal}
                          className="text-sm px-3 py-1 rounded-lg border hover:bg-slate-50"
                        >
                          Bytt sykkel
                        </button>
                        <button
                          type="button"
                          onClick={unlinkBike}
                          disabled={bikeBusy}
                          className="text-sm px-3 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
                        >
                          Koble fra
                        </button>
                        <button
                          type="button"
                          onClick={() => bikeSpecImageRef.current?.click()}
                          disabled={bikeSpecImageUploading}
                          className="text-sm px-3 py-1 rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        >
                          {bikeSpecImageUploading ? "Laster opp…" : linkedBike.specImageUrl ? "Bytt spesifikasjonsbilde" : "Last opp spesifikasjonsbilde"}
                        </button>
                        {linkedBike.specImageUrl && (
                          <button
                            type="button"
                            onClick={deleteBikeSpecImage}
                            disabled={bikeSpecImageUploading}
                            className="text-sm px-3 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
                          >
                            Slett bilde
                          </button>
                        )}
                        <input
                          ref={bikeSpecImageRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => { if (e.target.files[0]) { uploadBikeSpecImage(e.target.files[0]); e.target.value = ""; } }}
                        />
                      </div>
                    </div>
                    {/* Etikett */}
                    {linkedBike.token && linkedBike.shortId && (
                      <div
                        onClick={printBikeLabel}
                        title="Klikk for å skrive ut etikett (30×50mm)"
                        className="cursor-pointer shrink-0 rounded-lg border-2 border-dashed border-slate-300 bg-white hover:border-slate-500 hover:shadow-sm transition-colors"
                        style={{ width: 96, height: 160 }}
                      >
                        <div className="flex flex-col items-center h-full p-1.5 overflow-hidden" style={{ fontFamily: "system-ui, sans-serif" }}>
                          <img src="/RoykenSykkelservice_logo.png" alt="" style={{ width: "100%", height: 20, objectFit: "contain", objectPosition: "center" }} />
                          <QRCodeSVG value={`https://status.roykensykkelservice.no/sykkel/${linkedBike.token}`} size={66} level="M" style={{ marginTop: 6 }} />
                          <div className="font-mono font-black tracking-widest mt-1.5" style={{ fontSize: 15 }}>{linkedBike.shortId}</div>
                          <div className="text-slate-500 mt-1" style={{ fontSize: 7 }}>klikk for å skrive ut</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="flex items-center gap-2 flex-wrap">
                <form
                  onSubmit={(e) => { e.preventDefault(); handleBikeScan(e.target.shortIdInput.value); e.target.reset(); }}
                  className="flex items-center gap-1"
                >
                  <input
                    name="shortIdInput"
                    type="text"
                    maxLength={8}
                    placeholder="Kort-ID"
                    className="border rounded-lg px-2 py-1.5 text-sm font-mono uppercase w-24 tracking-widest"
                    style={{ letterSpacing: "0.15em" }}
                  />
                  <button
                    type="submit"
                    disabled={bikeBusy}
                    className="text-sm px-3 py-1.5 rounded-lg border hover:bg-slate-50"
                  >
                    Hent
                  </button>
                </form>
                {!linkedBike && (
                  <button
                    type="button"
                    onClick={openBikeModal}
                    className="text-sm px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    Koble til sykkel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {sykkelkortForslagOpen && (
        <SykkelkortForslag
          svc={svc}
          phoneKey={svc?.phoneKey || normalizePhone(customerPhone)}
          ansatt={Cookies.get("selectedEmployee") || ""}
          onKoblet={(oppdatert, bikeId) => {
            setSvc(oppdatert);
            fetchLinkedBike(bikeId);
            setSykkelkortForslagOpen(false);
            setMsg("Sykkelkortet er koblet til servicen.");
          }}
          onAvstatt={(oppdatert) => {
            setSvc(oppdatert);
            setSykkelkortForslagOpen(false);
          }}
          onLukk={() => setSykkelkortForslagOpen(false)}
        />
      )}

      {/* Sykkelkort-modal */}
      {bikeModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setBikeModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-lg shadow flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
              <h2 className="font-semibold">
                {editingBikeId ? "Rediger sykkel" : "Sykkelkort"}
              </h2>
              <button
                type="button"
                onClick={() => setBikeModalOpen(false)}
                className="text-slate-600 hover:text-slate-700 text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b shrink-0">
              <button
                type="button"
                onClick={() => { setBikeModalTab("list"); setEditingBikeId(null); }}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${bikeModalTab === "list" ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-600 hover:text-slate-700"}`}
              >
                Sykler registrert
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!editingBikeId) {
                    setBikeForm({ merke: "", modell: "", årsmodell: "", type: "", elsykkel: null, rammenummer: "" });
                    setModalBikeImageData({ specImageUrl: "", mainImageUrl: "", receiptUrl: "", extraImages: [], diagnoseRapporter: [], softwareRapporter: [] });
                  }
                  setBikeModalTab("new");
                  setBikeSaveError("");
                }}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${bikeModalTab === "new" ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-600 hover:text-slate-700"}`}
              >
                {editingBikeId ? "Rediger sykkel" : "Registrer ny"}
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4">
              {bikeModalTab === "list" && (
                <div>
                  {bikesLoading ? (
                    <p className="text-sm text-slate-600">Laster sykler…</p>
                  ) : bikes.length === 0 ? (
                    <p className="text-sm text-slate-600">Ingen sykler registrert på dette nummeret.</p>
                  ) : (
                    <div className="space-y-3">
                      {bikes.map((b) => {
                        const isLinked = String(svc?.bikeId) === String(b._id);
                        return (
                          <div
                            key={b._id}
                            className={`border rounded-lg p-3 ${isLinked ? "border-indigo-400 bg-indigo-50" : "bg-slate-50"}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="text-sm flex-1 min-w-0">
                                <div className="font-medium">
                                  {[b.merke, b.modell].filter(Boolean).join(" ") || "Ukjent sykkel"}
                                  {isLinked && (
                                    <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-lg">Koblet</span>
                                  )}
                                  {b.shortId && (
                                    <span className="ml-2 font-mono font-bold text-slate-600 tracking-widest">{b.shortId}</span>
                                  )}
                                </div>
                                <div className="text-slate-600 mt-0.5">
                                  {[b.type, b.rammenummer ? `Ramme: ${b.rammenummer}` : ""].filter(Boolean).join(" · ")}
                                </div>
                                {b.token && (
                                  <div className="mt-2 flex items-center gap-3">
                                    <QRCodeSVG
                                      value={`https://status.roykensykkelservice.no/sykkel/${b.token}`}
                                      size={56}
                                      level="M"
                                    />
                                    <a
                                      href={`https://status.roykensykkelservice.no/sykkel/${b.token}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs text-indigo-700 underline break-all"
                                    >
                                      Åpne sykkelkort
                                    </a>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setBikeForm({
                                      merke: b.merke || "",
                                      modell: b.modell || "",
                                      årsmodell: b.årsmodell ? String(b.årsmodell) : "",
                                      type: b.type || "",
                                      elsykkel: b.elsykkel ?? null,
                                      rammenummer: b.rammenummer || "",
                                    });
                                    setModalBikeImageData({
                                      specImageUrl: b.specImageUrl || "",
                                      mainImageUrl: b.mainImageUrl || "",
                                      receiptUrl: b.receiptUrl || "",
                                      extraImages: b.extraImages || [],
                                      diagnoseRapporter: b.diagnoseRapporter || [],
                                      softwareRapporter: b.softwareRapporter || [],
                                    });
                                    setEditingBikeId(b._id);
                                    setBikeSaveError("");
                                    setBikeModalTab("new");
                                  }}
                                  className="text-xs px-2 py-1 rounded-lg border hover:bg-white"
                                >
                                  Rediger
                                </button>
                                {!isLinked && (
                                  <button
                                    type="button"
                                    onClick={() => linkBike(b._id)}
                                    disabled={bikeBusy}
                                    className="text-xs px-2 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                                  >
                                    Koble til
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {bikeModalTab === "new" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Rad 1: Merke | Modell */}
                  <div className="bg-amber-50 rounded-xl p-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Merke <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={bikeForm.merke}
                      onChange={(e) => setBikeForm((f) => ({ ...f, merke: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      placeholder="f.eks. Trek, Specialized…"
                    />
                  </div>
                  <div className="bg-amber-50 rounded-xl p-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Modell <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={bikeForm.modell}
                      onChange={(e) => setBikeForm((f) => ({ ...f, modell: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      placeholder="f.eks. Marlin 7…"
                    />
                  </div>
                  {/* Rad 2: Type | Elsykkel */}
                  <div className="bg-amber-50 rounded-xl p-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Type <span className="text-red-500">*</span></label>
                    <select
                      value={bikeForm.type}
                      onChange={(e) => setBikeForm((f) => ({ ...f, type: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="">Velg type…</option>
                      {BIKE_TYPES.map((t) => (
                        <option key={t} value={t}>{BIKE_TYPE_LABELS[t] || t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Elsykkel <span className="text-red-500">*</span></label>
                    <div className="flex gap-2 mt-1">
                      <button
                        type="button"
                        onClick={() => setBikeForm((f) => ({ ...f, elsykkel: true }))}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          bikeForm.elsykkel === true
                            ? "bg-green-600 border-green-600 text-white"
                            : "border-slate-300 text-slate-700 hover:bg-white"
                        }`}
                      >
                        Ja
                      </button>
                      <button
                        type="button"
                        onClick={() => setBikeForm((f) => ({ ...f, elsykkel: false }))}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          bikeForm.elsykkel === false
                            ? "bg-slate-800 border-slate-800 text-white"
                            : "border-slate-300 text-slate-700 hover:bg-white"
                        }`}
                      >
                        Nei
                      </button>
                    </div>
                  </div>
                  {/* Rad 3: Årsmodell | Rammenummer */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Årsmodell</label>
                    <input
                      type="number"
                      min="1990"
                      max="2030"
                      value={bikeForm.årsmodell}
                      onChange={(e) => setBikeForm((f) => ({ ...f, årsmodell: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="f.eks. 2023"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Rammenummer</label>
                    <input
                      type="text"
                      value={bikeForm.rammenummer}
                      onChange={(e) => setBikeForm((f) => ({ ...f, rammenummer: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Rammenummer / serienummer"
                    />
                  </div>
                  {/* Feilmelding — full bredde */}
                  {bikeSaveError && (
                    <p className="sm:col-span-2 text-sm text-red-600">{bikeSaveError}</p>
                  )}

                  {/* Bilder og dokumenter — kun ved redigering av eksisterende sykkel */}
                  {editingBikeId && (
                    <div className="sm:col-span-2 border-t pt-3 mt-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Bilder og dokumenter</span>
                        {modalBikeImgMsg && (
                          <span className={`text-xs ${modalBikeImgMsg.ok ? "text-green-600" : "text-red-600"}`}>{modalBikeImgMsg.text}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">

                        {/* Spesifikasjonsbilde */}
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Spesifikasjonsbilde</p>
                          {modalBikeImageData.specImageUrl ? (
                            <div>
                              <img src={modalBikeImageData.specImageUrl} alt="Spesifikasjoner" className="w-full h-24 object-contain rounded-lg border mb-1.5 cursor-pointer hover:opacity-90" onClick={() => window.open(modalBikeImageData.specImageUrl, "_blank")} />
                              <div className="flex gap-1">
                                <button type="button" onClick={() => modalBikeSpecRef.current?.click()} disabled={modalBikeSpecUploading} className="flex-1 text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">Bytt</button>
                                <button type="button" onClick={deleteModalBikeSpec} disabled={modalBikeSpecUploading} className="text-xs px-2 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50">Slett</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="w-full h-24 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-500 text-xs mb-1.5">Ikke lastet opp</div>
                              <button type="button" onClick={() => modalBikeSpecRef.current?.click()} disabled={modalBikeSpecUploading} className="w-full text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">{modalBikeSpecUploading ? "Laster opp…" : "Last opp"}</button>
                            </div>
                          )}
                          <input ref={modalBikeSpecRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) { uploadModalBikeSpec(e.target.files[0]); e.target.value = ""; } }} />
                        </div>

                        {/* Hovedbilde */}
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Hovedbilde</p>
                          {modalBikeImageData.mainImageUrl ? (
                            <div>
                              <img src={modalBikeImageData.mainImageUrl} alt="Hovedbilde" className="w-full h-24 object-contain rounded-lg border mb-1.5 cursor-pointer hover:opacity-90" onClick={() => window.open(modalBikeImageData.mainImageUrl, "_blank")} />
                              <div className="flex gap-1">
                                <button type="button" onClick={() => modalBikeMainRef.current?.click()} disabled={modalBikeMainUploading} className="flex-1 text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">Bytt</button>
                                <button type="button" onClick={deleteModalBikeMain} disabled={modalBikeMainUploading} className="text-xs px-2 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50">Slett</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="w-full h-24 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-500 text-xs mb-1.5">Ikke lastet opp</div>
                              <button type="button" onClick={() => modalBikeMainRef.current?.click()} disabled={modalBikeMainUploading} className="w-full text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">{modalBikeMainUploading ? "Laster opp…" : "Last opp"}</button>
                            </div>
                          )}
                          <input ref={modalBikeMainRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) { uploadModalBikeMain(e.target.files[0]); e.target.value = ""; } }} />
                        </div>

                        {/* Kjøpskvittering */}
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Kjøpskvittering</p>
                          {modalBikeImageData.receiptUrl ? (
                            <div>
                              {modalBikeImageData.receiptUrl.includes("/raw/") ? (
                                <div className="w-full h-24 rounded-lg border flex items-center justify-center gap-1 text-slate-600 mb-1.5">
                                  <span className="text-2xl">📄</span><span className="text-xs">PDF</span>
                                </div>
                              ) : (
                                <img src={modalBikeImageData.receiptUrl} alt="Kvittering" className="w-full h-24 object-contain rounded-lg border mb-1.5 cursor-pointer hover:opacity-90" onClick={() => window.open(modalBikeImageData.receiptUrl, "_blank")} />
                              )}
                              <div className="flex gap-1">
                                <a href={modalBikeImageData.receiptUrl} target="_blank" rel="noreferrer" className="flex-1 text-center text-xs px-2 py-1 rounded-lg border hover:bg-slate-50">Åpne</a>
                                <button type="button" onClick={() => modalBikeReceiptRef.current?.click()} disabled={modalBikeReceiptUploading} className="text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">Bytt</button>
                                <button type="button" onClick={deleteModalBikeReceipt} disabled={modalBikeReceiptUploading} className="text-xs px-2 py-1 rounded-lg border border-red-300 text-red-700 hover:bg-red-50">Slett</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="w-full h-24 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-500 text-xs mb-1.5">Ikke lastet opp</div>
                              <button type="button" onClick={() => modalBikeReceiptRef.current?.click()} disabled={modalBikeReceiptUploading} className="w-full text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">{modalBikeReceiptUploading ? "Laster opp…" : "Last opp"}</button>
                              <p className="text-xs text-slate-500 mt-0.5">Bilde eller PDF</p>
                            </div>
                          )}
                          <input ref={modalBikeReceiptRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => { if (e.target.files[0]) { uploadModalBikeReceipt(e.target.files[0]); e.target.value = ""; } }} />
                        </div>

                        {/* Andre bilder */}
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Andre bilder</p>
                          {modalBikeImageData.extraImages?.length > 0 && (
                            <div className="grid grid-cols-2 gap-1 mb-1.5">
                              {modalBikeImageData.extraImages.map(img => (
                                <div key={img.publicId} className="relative group">
                                  <img src={img.url} alt={img.caption || "Bilde"} className="w-full h-16 object-cover rounded-lg border cursor-pointer hover:opacity-90" onClick={() => window.open(img.url, "_blank")} />
                                  <button onClick={() => deleteModalBikeExtra(img.publicId)} className="absolute top-0.5 right-0.5 bg-white/90 hover:bg-red-50 text-red-600 rounded-full w-4 h-4 text-xs flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                                </div>
                              ))}
                            </div>
                          )}
                          <button type="button" onClick={() => modalBikeExtraRef.current?.click()} disabled={modalBikeExtraUploading} className="w-full text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">{modalBikeExtraUploading ? "Laster opp…" : "Last opp bilder"}</button>
                          <input ref={modalBikeExtraRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { if (e.target.files?.length) { uploadModalBikeExtra(e.target.files); e.target.value = ""; } }} />
                        </div>

                        {/* Diagnoserapporter */}
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Diagnoserapporter (PDF)</p>
                          {modalBikeImageData.diagnoseRapporter?.length > 0 && (
                            <div className="space-y-1 mb-1.5">
                              {modalBikeImageData.diagnoseRapporter.map(rep => (
                                <div key={rep.publicId} className="flex items-center gap-1.5 border rounded-lg px-1.5 py-1 text-xs">
                                  <span>📄</span>
                                  <a href={rep.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0 truncate text-indigo-700 hover:underline" title={rep.originalName}>{rep.originalName || "Rapport"}</a>
                                  <button onClick={() => deleteModalBikeDiagnose(rep.publicId)} className="text-red-600 hover:text-red-800 shrink-0">×</button>
                                </div>
                              ))}
                            </div>
                          )}
                          <button type="button" onClick={() => modalBikeDiagnoseRef.current?.click()} disabled={modalBikeDiagnoseUploading} className="w-full text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">{modalBikeDiagnoseUploading ? "Laster opp…" : "Last opp PDF"}</button>
                          <input ref={modalBikeDiagnoseRef} type="file" accept="application/pdf" multiple className="hidden" onChange={e => { if (e.target.files?.length) { uploadModalBikeDiagnose(e.target.files); e.target.value = ""; } }} />
                        </div>

                        {/* Softwareoppdateringsrapporter */}
                        <div>
                          <p className="text-xs text-slate-600 mb-1">Softwareoppdateringsrapporter (PDF)</p>
                          {modalBikeImageData.softwareRapporter?.length > 0 && (
                            <div className="space-y-1 mb-1.5">
                              {modalBikeImageData.softwareRapporter.map(rep => (
                                <div key={rep.publicId} className="flex items-center gap-1.5 border rounded-lg px-1.5 py-1 text-xs">
                                  <span>📄</span>
                                  <a href={rep.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0 truncate text-indigo-700 hover:underline" title={rep.originalName}>{rep.originalName || "Rapport"}</a>
                                  <button onClick={() => deleteModalBikeSoftware(rep.publicId)} className="text-red-600 hover:text-red-800 shrink-0">×</button>
                                </div>
                              ))}
                            </div>
                          )}
                          <button type="button" onClick={() => modalBikeSoftwareRef.current?.click()} disabled={modalBikeSoftwareUploading} className="w-full text-xs px-2 py-1 rounded-lg border hover:bg-slate-50 disabled:opacity-50">{modalBikeSoftwareUploading ? "Laster opp…" : "Last opp PDF"}</button>
                          <input ref={modalBikeSoftwareRef} type="file" accept="application/pdf" multiple className="hidden" onChange={e => { if (e.target.files?.length) { uploadModalBikeSoftware(e.target.files); e.target.value = ""; } }} />
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {bikeModalTab === "new" && (
              <div className="flex justify-end gap-2 px-4 py-3 border-t shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setBikeModalOpen(false);
                    setEditingBikeId(null);
                  }}
                  className="px-4 py-2 text-sm rounded-lg border hover:bg-slate-50"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={saveBike}
                  disabled={bikeBusy}
                  className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {bikeBusy ? "Lagrer…" : editingBikeId ? "Lagre endringer" : "Registrer og koble til"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Total */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 mb-4">
        <div className="flex items-center justify-between text-lg">
          <span className="font-semibold">Total pris</span>
          <span className="font-bold">{fmtNOK(totals.total)}</span>
        </div>
      </div>

      {/* Lenker */}
      <div className="flex items-center gap-4">
        {svc.bookingId ? (
          <Link
            className="text-indigo-700 underline"
            to={`/booking/${svc.bookingId}`}
          >
            Gå til booking
          </Link>
        ) : null}
      </div>

      {/* Tjeneste-modal: liste + manuell */}
      {serviceModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setServiceModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-lg shadow p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold">Legg til tjeneste</h4>
              <button
                onClick={() => setServiceModalOpen(false)}
                className="text-slate-600 hover:text-black"
              >
                Lukk
              </button>
            </div>

            <div className="flex gap-2 mb-3">
              <button
                className={`px-3 py-1 rounded-lg border ${serviceTab === "list"
                  ? "bg-indigo-600 text-white"
                  : "bg-white"
                  }`}
                onClick={() => { setServiceTab("list"); setServiceModalError(""); }}
              >
                Fra liste
              </button>
              <button
                className={`px-3 py-1 rounded-lg border ${serviceTab === "manual"
                  ? "bg-indigo-600 text-white"
                  : "bg-white"
                  }`}
                onClick={() => { setServiceTab("manual"); setServiceModalError(""); }}
              >
                Manuell
              </button>
            </div>
            <div className="flex flex-wrap gap-6 mb-3 px-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newReklamasjon}
                  onChange={(e) => setNewReklamasjon(e.target.checked)}
                  className="w-4 h-4 accent-red-600"
                />
                <span className="text-sm font-medium text-red-700">Reklamasjonsarbeid</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newGratisarbeid}
                  onChange={(e) => setNewGratisarbeid(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-sm font-medium text-emerald-700">Gratisarbeid</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newUtfortAvRS}
                  onChange={(e) => setNewUtfortAvRS(e.target.checked)}
                  className="w-4 h-4 accent-indigo-600"
                />
                <span className="text-sm font-medium text-slate-700">Utført av RS</span>
              </label>
            </div>

            {serviceModalError && (
              <div className="mb-3 rounded-lg bg-red-50 border border-red-300 text-red-700 text-sm px-3 py-2">
                {serviceModalError}
              </div>
            )}

            {serviceTab === "list" ? (
              <>
                <input
                  value={serviceQuery}
                  onChange={(e) => setServiceQuery(e.target.value)}
                  placeholder="Søk (navn, slug, type, kategori)…"
                  className="w-full border rounded-lg p-2 mb-3"
                />
                {hygraphLoading ? (
                  <div className="py-6 text-center">
                    Henter tjenester…
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto divide-y">
                    {filteredServices.map((s) => (
                      <div
                        key={`${s.groupId || s.groupSlug}:${s.serviceId || s.slug
                          }`}
                        className="py-2 flex items-center justify-between gap-4"
                      >
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{s.navn}</span>
                            {s.gratisarbeid && (
                              <span className="text-xs font-semibold px-1.5 py-0.5 rounded-lg bg-emerald-100 text-emerald-700">
                                Gratisarbeid
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-600">
                            {s.groupType || "—"} ·{" "}
                            {s.kategori || "ukategorisert"} ·{" "}
                            {s.tidsbruk || 0} min
                          </div>
                          <div className="text-sm">
                            {s.gratisarbeid
                              ? <span className="text-emerald-700 font-medium">0 kr (gratis)</span>
                              : typeof s.pris === "number" ? `${s.pris} kr` : "—"
                            }
                          </div>
                        </div>
                        <button
                          onClick={() => addHygraphService(s)}
                          disabled={hyAddBusy}
                          className={`px-3 py-1 rounded-lg text-white ${hyAddBusy
                            ? "bg-slate-400 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700"
                            }`}
                        >
                          {hyAddBusy ? "Legger til…" : "Legg til"}
                        </button>
                      </div>
                    ))}
                    {!filteredServices.length && (
                      <div className="py-6 text-center text-slate-600">
                        Ingen treff
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                <div>
                  <label className="block text-xs text-slate-600">
                    Navn
                  </label>
                  <input
                    className="w-full border rounded-lg p-2"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Navn på tjeneste"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600">
                    Pris (kr)
                  </label>
                  <input
                    type="number"
                    className="w-full border rounded-lg p-2"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600">
                    Tidsbruk (min) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    className={`w-full border rounded-lg p-2 ${!Number(newMinutes) ? "border-red-400 bg-red-50" : ""}`}
                    value={newMinutes}
                    onChange={(e) => { setNewMinutes(e.target.value); setServiceModalError(""); }}
                    placeholder="Påkrevd"
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-xs text-slate-600">
                    Kommentar
                  </label>
                  <textarea
                    rows={2}
                    className="w-full border rounded-lg p-2"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Valgfritt"
                  />
                </div>
                <div className="md:col-span-4 flex justify-end">
                  <button
                    className={`px-3 py-1 rounded-lg text-white ${!Number(newMinutes) ? "bg-slate-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
                    onClick={addManualService}
                    disabled={!Number(newMinutes)}
                  >
                    Legg til
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {eanScannerOpen && (
        <BarcodeScanner
          onDetected={(code) => { setPEan(code); setEanScannerOpen(false); }}
          onClose={() => setEanScannerOpen(false)}
        />
      )}

      {bikeScannerOpen && (
        <BarcodeScanner
          onDetected={handleBikeScan}
          onClose={() => setBikeScannerOpen(false)}
        />
      )}

      {/* Del-modal: liste + manuell */}
      {partsModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={closePartsModal}
        >
          <div
            className="bg-white w-full max-w-3xl rounded-lg shadow p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold">
                {editingPartIndex !== null ? "Endre del" : "Legg til del"}
              </h4>
              <button
                onClick={closePartsModal}
                className="text-slate-600 hover:text-black"
              >
                Lukk
              </button>
            </div>

            {editingPartIndex === null && (
              <div className="flex gap-2 mb-3">
                <button
                  className={`px-3 py-1 rounded-lg border ${partsTab === "list"
                    ? "bg-indigo-600 text-white"
                    : "bg-white"
                    }`}
                  onClick={() => setPartsTab("list")}
                >
                  Fra deleliste
                </button>
                <button
                  className={`px-3 py-1 rounded-lg border ${partsTab === "manual"
                    ? "bg-indigo-600 text-white"
                    : "bg-white"
                    }`}
                  onClick={() => setPartsTab("manual")}
                >
                  Manuell
                </button>
              </div>
            )}

            {partsTab === "list" ? (
              <>
                <input
                  value={partsQuery}
                  onChange={(e) => setPartsQuery(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    const code = partsQuery.trim().toLowerCase();
                    if (!code || partsAddBusy) return;
                    // Skanning fyrer Enter raskere enn debounce'en rekker å
                    // hente treff, så hent på nytt her og se etter eksakt EAN.
                    const results = await searchParts(code);
                    const exact = results.find((p) => (p.ean || "").toLowerCase() === code);
                    if (exact) addPartFromCatalog(exact);
                  }}
                  placeholder="Søk (produkt, varemerke, EAN)… — klar for skanning"
                  className="w-full border rounded-lg p-2 mb-3"
                  autoFocus
                />
                {partsLoading ? (
                  <div className="py-6 text-center">
                    Søker…
                  </div>
                ) : partsQuery.trim().length < PARTS_MIN_QUERY_LENGTH ? (
                  <div className="py-6 text-center text-slate-600">
                    Skriv minst {PARTS_MIN_QUERY_LENGTH} tegn for å søke (produkt, varemerke eller EAN)
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto divide-y">
                    {filteredParts.map((p) => (
                      <div
                        key={p._id}
                        className="py-2 grid grid-cols-12 items-center gap-3"
                      >
                        <div className="col-span-7">
                          <div className="font-medium">{p.product}</div>
                          <div className="text-xs text-slate-600">
                            {p.brand || "—"} ·{" "}
                            {p.ean || "ingen EAN"}
                          </div>
                        </div>
                        <div className="col-span-2 text-sm">
                          {fmtNOK(p.price)}
                        </div>
                        <div className="col-span-3 flex justify-end">
                          <button
                            onClick={() => addPartFromCatalog(p)}
                            disabled={partsAddBusy}
                            className={`px-3 py-1 rounded-lg text-white ${partsAddBusy
                              ? "bg-slate-400 cursor-not-allowed"
                              : "bg-green-600 hover:bg-green-700"
                              }`}
                          >
                            {partsAddBusy ? "Legger til…" : "Legg til"}
                          </button>
                        </div>
                      </div>
                    ))}
                    {!filteredParts.length && (
                      <div className="py-6 text-center text-slate-600">
                        Ingen treff
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-600">
                    Produkt
                  </label>
                  <input
                    className="w-full border rounded-lg p-2"
                    placeholder="Navn på del"
                    value={pName}
                    onChange={(e) => setPName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600">
                    Varemerke
                  </label>
                  <input
                    className="w-full border rounded-lg p-2"
                    value={pBrand}
                    onChange={(e) => setPBrand(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600">
                    EAN
                  </label>
                  <div className="relative">
                    <input
                      className="w-full border rounded-lg p-2 pr-8"
                      value={pEan}
                      onChange={(e) => setPEan(e.target.value)}
                      placeholder="EAN-kode"
                    />
                    <button
                      type="button"
                      title="Skann strekkode med kamera"
                      onClick={() => setEanScannerOpen(true)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-800 leading-none"
                    >
                      📷
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-600">
                    Pris (kr)
                  </label>
                  <input
                    type="number"
                    className="w-full border rounded-lg p-2"
                    placeholder="0"
                    value={pPrice}
                    onChange={(e) => setPPrice(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="del-kostpris" className="block text-xs text-slate-600">
                    Kostpris eks. mva
                  </label>
                  <input
                    id="del-kostpris"
                    type="text"
                    inputMode="decimal"
                    className="w-full border rounded-lg p-2"
                    placeholder="ukjent"
                    value={pKostpris}
                    onChange={(e) => setPKostpris(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600">
                    Antall
                  </label>
                  <input
                    type="number"
                    className="w-full border rounded-lg p-2"
                    min="1"
                    value={pQty}
                    onChange={(e) => setPQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-600">
                    Rabatt (kr)
                  </label>
                  <input
                    type="number"
                    className="w-full border rounded-lg p-2"
                    min="0"
                    value={pDiscount}
                    onChange={(e) => setPDiscount(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-600">
                    Rabatt (%)
                  </label>
                  <input
                    type="number"
                    className="w-full border rounded-lg p-2"
                    min="0"
                    max="100"
                    step="0.1"
                    value={pDiscountPercent}
                    onChange={(e) => setPDiscountPercent(e.target.value)}
                  />
                </div>


                {/* Reklamasjon – collapsible */}
                <div className="md:col-span-6 border-t pt-3 mt-1">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const next = !pReklamasjonExpanded;
                        setPReklamasjonExpanded(next);
                        if (next && !pReklamasjonButikk) {
                          setPReklamasjonButikk(Cookies.get("selectedStore") || "");
                        }
                        if (next && !pReklamasjonMerke) {
                          setPReklamasjonMerke(svc?.produkt?.produktmerke || "");
                        }
                        if (next && !pReklamasjonModell) {
                          setPReklamasjonModell(svc?.produkt?.farge || "");
                        }
                      }}
                      className="flex items-center gap-2 text-sm font-medium text-orange-700 hover:text-orange-900"
                    >
                      <span>{pReklamasjonExpanded ? "▼" : "▶"}</span>
                      Reklamasjon
                    </button>
                    {pReklamasjonExpanded && (
                      <button
                        type="button"
                        onClick={() => {
                          setPReklamasjonExpanded(false);
                          setPReklamasjonKjopsdato("");
                          setPReklamasjonRammenr("");
                          setPReklamasjonTidsforbruk("");
                          setPReklamasjonArsak("");
                          setPReklamasjonAnsvar("");
                          setPReklamasjonButikk("");
                          setPReklamasjonImportor("");
                          setPReklamasjonMerke("");
                          setPReklamasjonModell("");
                          setPReklamasjonSerienr("");
                        }}
                        className="text-xs px-2 py-0.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
                      >
                        Nullstill reklamasjon
                      </button>
                    )}
                  </div>

                  {pReklamasjonExpanded && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3">
                      <div className="md:col-span-2">
                        <label className="block text-xs text-slate-600">Butikk</label>
                        <select
                          className="w-full border rounded-lg p-2"
                          value={pReklamasjonButikk}
                          onChange={(e) => setPReklamasjonButikk(e.target.value)}
                        >
                          <option value="">Velg butikk…</option>
                          <option value="Sport 1 Røyken">Sport 1 Røyken</option>
                          <option value="Sport 1 Slemmestad">Sport 1 Slemmestad</option>
                          <option value="Røyken Sykkelverksted">Røyken Sykkelverksted</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-slate-600">Importør</label>
                        <ImportorSelect
                          value={pReklamasjonImportor}
                          onChange={setPReklamasjonImportor}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-slate-600">Kundens kjøpsdato</label>
                        <input
                          type="date"
                          className="w-full border rounded-lg p-2"
                          value={pReklamasjonKjopsdato}
                          onChange={(e) => setPReklamasjonKjopsdato(e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-slate-600">Rammenr. sykkel</label>
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2"
                          placeholder="Rammenummer"
                          value={pReklamasjonRammenr}
                          onChange={(e) => setPReklamasjonRammenr(e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-slate-600">Serienr. del</label>
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2"
                          placeholder="Serienummer på delen"
                          value={pReklamasjonSerienr}
                          onChange={(e) => setPReklamasjonSerienr(e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-slate-600">Merke</label>
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2"
                          placeholder="Merke"
                          value={pReklamasjonMerke}
                          onChange={(e) => setPReklamasjonMerke(e.target.value)}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs text-slate-600">Modell</label>
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2"
                          placeholder="Modell"
                          value={pReklamasjonModell}
                          onChange={(e) => setPReklamasjonModell(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600">Tidsforbruk (min)</label>
                        <input
                          type="number"
                          className="w-full border rounded-lg p-2"
                          min="0"
                          value={pReklamasjonTidsforbruk}
                          onChange={(e) => setPReklamasjonTidsforbruk(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-600">Ansvar</label>
                        <select
                          className="w-full border rounded-lg p-2"
                          value={pReklamasjonAnsvar}
                          onChange={(e) => setPReklamasjonAnsvar(e.target.value)}
                        >
                          <option value="">Velg…</option>
                          <option value="Intern">Intern</option>
                          <option value="Ekstern">Ekstern</option>
                        </select>
                      </div>
                      <div className="md:col-span-6">
                        <label className="block text-xs text-slate-600">Reklamasjonsårsak</label>
                        <textarea
                          className="w-full border rounded-lg p-2"
                          rows={2}
                          placeholder="Beskriv årsaken til reklamasjon…"
                          value={pReklamasjonArsak}
                          onChange={(e) => setPReklamasjonArsak(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="md:col-span-6 flex justify-end gap-2">
                  <button
                    className="px-3 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
                    onClick={closePartsModal}
                  >
                    Avbryt
                  </button>
                  <button
                    className="px-3 py-1 rounded-lg bg-green-600 text-white hover:bg-green-700"
                    onClick={editingPartIndex !== null ? saveEditPart : addManualPart}
                  >
                    {editingPartIndex !== null ? "Lagre endringer" : "Legg til del"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

{commentModalOpen && (
  <div
    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
    onClick={closeCommentModal}
  >
    <div
      className="bg-white w-full max-w-lg rounded-2xl shadow-lg flex flex-col max-h-[90vh]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
        <h4 className="text-base font-semibold">Rediger produktinfo</h4>
        <button
          type="button"
          onClick={closeCommentModal}
          className="text-slate-600 hover:text-black"
        >
          Lukk
        </button>
      </div>

      <div className="overflow-y-auto px-4 py-3 space-y-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Produktmerke
          </label>
          <input
            type="text"
            className="w-full border rounded-lg p-2 text-sm"
            placeholder="Skriv produktmerke …"
            value={productBrandDraft}
            onChange={(e) => setProductBrandDraft(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Modell
          </label>
          <input
            type="text"
            className="w-full border rounded-lg p-2 text-sm"
            placeholder="Skriv modell …"
            value={productColorDraft}
            onChange={(e) => setProductColorDraft(e.target.value)}
          />
        </div>

        {filteredSlugOptions.length > 1 && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Tjenestetype
            </label>
            {slugOptionsLoading ? (
              <p className="text-xs text-slate-500">Henter tjenester…</p>
            ) : (
              <select
                className="w-full border rounded-lg p-2 text-sm"
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value)}
              >
                {filteredSlugOptions.map((o) => (
                  <option key={o.slug} value={o.slug}>{o.navn}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Kommentar
          </label>
          <textarea
            rows={3}
            className="w-full border rounded-lg p-2 text-sm"
            placeholder="Skriv kommentar til produktet …"
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
          />
        </div>

        {String(svc?.produkt?.bookingtype || "").toLowerCase() === "fortlopende" && (
          <>
            <hr className="border-slate-200" />
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">
              Fortløpende kø
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Grunn
              </label>
              <select
                className="w-full border rounded-lg p-2 text-sm"
                value={editPriorGrunn}
                onChange={(e) => setEditPriorGrunn(e.target.value)}
              >
                <option value="">Velg grunn…</option>
                <option value="etterjustering">Etterjustering / førstegangsservice</option>
                <option value="akuttservice">Akuttservice</option>
                <option value="reklamasjon">Reklamasjon</option>
                <option value="kundeprioritering">Kundeprioritering</option>
                <option value="avtalemekaniker">Avtale med mekaniker</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Forklaring
              </label>
              <textarea
                rows={2}
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="Beskriv hvorfor denne servicen prioriteres…"
                value={editPriorForklaring}
                onChange={(e) => setEditPriorForklaring(e.target.value)}
              />
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded-lg border-slate-300 text-red-600"
                checked={editPriorHaster}
                onChange={(e) => setEditPriorHaster(e.target.checked)}
              />
              <span className="text-sm font-medium text-slate-700">Haster</span>
            </label>
          </>
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-end gap-2 shrink-0">
        <button
          type="button"
          onClick={closeCommentModal}
          className="px-4 py-2 rounded-lg border hover:bg-slate-50"
        >
          Avbryt
        </button>
        <button
          type="button"
          onClick={saveProductComment}
          disabled={commentBusy}
          className={`px-4 py-2 rounded-lg text-white ${
            commentBusy
              ? "bg-slate-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700"
          }`}
        >
          {commentBusy ? "Lagrer…" : "Lagre"}
        </button>
      </div>
    </div>
  </div>
)}

      <StatusModal
        open={statusModalOpen}
        onClose={closeStatusModal}
        expectedReadyDate={expectedReadyDate}
        onChangeExpectedReadyDate={setExpectedReadyDate}
        onSaveExpectedReadyDate={saveExpectedReadyDateOnService}   // 👈 NY
        onRemoveExpectedReadyDate={removeExpectedReadyDate}

        currentStatus={svc.status}
        statusOptions={STATUS_OPTIONS}
        selectedStatus={selectedStatus}
        onSelectStatus={setSelectedStatus}
        smsText={smsText}
        onChangeSmsText={setSmsText}
        smsTemplateLoading={smsTemplateLoading}
        smsTemplates={filteredSmsTemplates}
        selectedTemplateId={selectedTemplateId}
        onChangeTemplate={handleTemplateChange}
        sendOnStatusChange={sendOnStatusChange}
        onToggleSendSms={setSendOnStatusChange}
        sendEmailOnStatusChange={sendEmailOnStatusChange}
        onToggleSendEmail={setSendEmailOnStatusChange}
        customerPhoneLabel={
          customerPhone || customer?.phoneNumber || "Mangler telefonnummer"
        }
        customerEmailLabel={
          customerEmail || customer?.email || "Mangler e-postadresse"
        }
        onChangeStatusOnly={async () => {
          const target = selectedStatus || svc.status;
          if (target === "avsluttet" && !expectedReadyDate) {
            setPendingAvsluttFromModal(true);
            setShowFerdigDatoWarning(true);
            return;
          }
          const hasText = (smsText || "").trim().length > 0;
          if (hasText) {
            setMsg(
              "Du har skrevet en melding. Huk av for SMS og/eller e-post hvis du vil sende den, " +
              "eller tøm meldingsfeltet før du endrer status."
            );
            return;
          }
          await changeStatus(target);
          setStatusModalOpen(false);
        }}
        onSendStatusNotification={() => notifyStatusChange(selectedStatus)}
        updating={updating}
        notifyBusy={notifyBusy}
        emailBusy={emailBusy}
        smsBusy={smsBusy}
        msg={msg}
        showPortalLinkOption={statusModalMode === "ready"}
        linkBusy={linkBusy}
        includePortalLink={includePortalLink}
        onTogglePortalLink={async (checked) => {
          if (!checked) {
            setIncludePortalLink(false);
            // Vipps-knappen vises kun bak kvitteringslenken, så den kan
            // ikke stå huket av alene når lenken fjernes.
            if (vippsRequested) await saveVippsRequested(false);
            return;
          }
          if (!customerLink) await createOrRotateCustomerLink();
          setIncludePortalLink(true);
        }}
        showVippsOption={statusModalMode === "ready"}
        vippsBusy={vippsBusy}
        vippsRequested={vippsRequested}
        onToggleVipps={async (checked) => {
          if (checked) {
            // Vipps-knappen vises kun bak kvitteringslenken i portalen,
            // så den må alltid være med når Vipps er huket av.
            if (!customerLink) await createOrRotateCustomerLink();
            setIncludePortalLink(true);
          }
          await saveVippsRequested(checked);
        }}
      />




      {/* Lightbox for spesifikasjonsbilde */}
      {bikeSpecImageOpen && linkedBike?.specImageUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setBikeSpecImageOpen(false)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setBikeSpecImageOpen(false)}
              className="absolute -top-10 right-0 text-white text-3xl leading-none hover:text-slate-300"
            >
              ×
            </button>
            <img
              src={linkedBike.specImageUrl}
              alt="Spesifikasjoner"
              className="w-full max-h-[85vh] object-contain rounded-lg"
            />
            {linkedBike && (
              <p className="text-center text-white/70 text-sm mt-2">
                {[linkedBike.merke, linkedBike.modell, linkedBike.årsmodell].filter(Boolean).join(" ")}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Lightbox for QR-kode */}
      {qrModalOpen && svc?._id && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setQrModalOpen(false)}
        >
          <div
            className="relative bg-white rounded-xl p-6 flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setQrModalOpen(false)}
              className="absolute -top-10 right-0 text-white text-3xl leading-none hover:text-slate-300"
            >
              ×
            </button>
            <QRCodeSVG
              value={`${window.location.origin}/workshop/${svc._id}`}
              size={220}
              level="M"
            />
            <p className="text-sm text-slate-600">Skann for å åpne servicen på mobil</p>
          </div>
        </div>
      )}

      {/* Sjekkliste-modal */}
      <ChecklistModal
        open={checklist.open}
        onClose={checklist.closeModal}
        items={checklist.items}
        values={checklist.values}
        visibility={checklist.visibility}
        showHidden={checklist.showHidden}
        setShowHidden={checklist.setShowHidden}
        loadingItems={checklist.loadingItems}
        loadingSaved={checklist.loadingSaved}
        visBusySlug={checklist.visBusySlug}
        setValue={checklist.setValue}
        setItemVisibility={checklist.setItemVisibility}
        onSave={checklist.save}
      />

      <ServiceInvoiceModal
        open={invoiceOpen}
        butikkvarer={butikkvarer}
        onClose={() => setInvoiceOpen(false)}
        svc={svc}
        customer={customer}
        produkt={produkt}
        serviceLines={serviceLines}
        partLines={partLines}
        totals={totals}
        workDesc={workDesc}
        checklistSummary={checklistSummary}
        checklistComplete={checklistComplete}
        fmtNOK={fmtNOK}
        fmtDT={fmtDT}
        fmtDate={fmtDate}
        fmtMin={fmtMin}
        onSvcUpdate={setSvc}
      />

      <QuoteModal
        open={quote.open}
        onClose={quote.hide}                 // 👈 fra useQuote
        svc={svc}
        customer={{
          ...(serviceCustomer || customer || {}),
          phoneNumber: customerPhone || serviceCustomer?.phoneNumber || "",
          email: customerEmail || serviceCustomer?.email || "",
        }}
        produkt={produkt}
        serviceLines={serviceLines}
        partLines={partLines}
        totals={totals}
        workDesc={quoteDesc || workDesc}
        quoteValidUntil={quote.validUntil || null}
        checklistSummary={checklistSummary}
        fmtNOK={fmtNOK}
        fmtDT={fmtDT}
        fmtDate={fmtDate}
        fmtMin={fmtMin}
        onSendEmail={handleSendQuoteEmail}      // 👈 viktig
        sendingEmail={sendingQuoteEmail}        // 👈 viktig
      />




    </div>
  );
}
